/**
 * n8n-style visual workflow canvas editor.
 * Full drag-and-drop node graph for building automation workflows.
 */
import { useState, useCallback, useRef, useEffect, useMemo, DragEvent as RDragEvent } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { TbLayoutSidebarLeftCollapse, TbLayoutSidebarLeftExpand } from 'react-icons/tb';
import ReactFlow, {
  Background,
  BackgroundVariant,
  Panel,
  MiniMap,
  addEdge,
  updateEdge,
  useNodesState,
  useEdgesState,
  Connection,
  Edge,
  Node,
  NodeTypes,
  MarkerType,
  Handle,
  Position,
  NodeProps,
  ReactFlowProvider,
  useReactFlow,
  useViewport,
  useUpdateNodeInternals,
} from 'reactflow';
import 'reactflow/dist/style.css';
import {
  Box,
  Typography,
  TextField,
  MenuItem,
  Button,
  IconButton,
  Stack,
  CircularProgress,
  Tooltip,
  Switch,
  FormControlLabel,
  Divider,
  Alert,
  ToggleButtonGroup,
  ToggleButton,
  Autocomplete,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Link,
  Menu,
} from '@mui/material';
import {
  ArrowBack,
  Bolt,
  CallSplit,
  AccessTime,
  Campaign,
  Person,
  SupportAgent,
  Http,
  AccountTree,
  Add,
  Delete,
  Close,
  CheckCircle,
  Fullscreen,
  FullscreenExit,
  ChatBubbleOutline,
  Forum,
  Reply,
  AttachFile,
  AutoAwesome,
  Flag as FlagIcon,
  PlayArrow,
  TouchApp,
  LibraryBooks,
  SmartToy,
  FormatListBulleted,
  SmartButton,
  QuestionAnswer,
  VerifiedUser,
  Refresh,
  OpenInNew,
  LightMode,
  DarkMode,
  ZoomIn,
  ZoomOut,
  FitScreen,
  Lock as LockIcon,
  LockOpen,
  Check,
  ArrowDownward,
  Search as SearchIcon,
  InfoOutlined as InfoOutlinedIcon,
  ErrorOutline as ErrorOutlineIcon,
  WarningAmber as WarningAmberIcon,
  BookmarkAddOutlined,
  StopCircleOutlined,
} from '@mui/icons-material';
import adminApi, {
  type RoutingSettingsResponse,
  type RoutingTarget,
  type WhatsAppTemplate,
  type MediaAsset,
  type InteractiveMessage,
  type FieldDefinition,
} from '@/services/admin.api';
import userManagementService from '@/services/user-management.service';
import apiConnectionApi, { ApiRequestDef } from '@/services/apiConnection.api';
import { joinEndpoint, insertAtCaret } from './apiConnectionUtils';
import FlowVariablePicker from './FlowVariablePicker';
import { boundsOfNodes } from './canvasFit.util';
import { usePlanFeature } from '@/hooks/usePlanFeature';
import WhatsAppPreviewBubble from '@/pages/admin/campaigns/WhatsAppPreviewBubble';
import { showPlanLockIfTagged } from '@/utils/plan-lock.util';
import { isTemplateMediaUnfit } from '@/utils/whatsapp-template-media.util';
import { ChipListInput } from '@/components/common/ChipListInput';
import './WorkflowCanvas.css';

// ─── Node metadata ───────────────────────────────────────────────────────────

/** One accent for every node type. Type is told by icon + label, not hue; the CSS draws
 *  node chrome from the theme's --wf-accent, so this only feeds the few JS consumers. */
const NODE_ACCENT = '#1d6b4a';

const NODE_META = {
  trigger: {
    color: NODE_ACCENT,
    label: 'Trigger',
    desc: 'The one, required starting point for this flow',
    isTrigger: true,
    isCondition: false,
  },
  condition: {
    color: NODE_ACCENT,
    label: 'Condition',
    desc: 'Branch on a rule',
    isTrigger: false,
    isCondition: true,
  },
  delay: {
    color: NODE_ACCENT,
    label: 'Delay',
    desc: 'Pause execution',
    isTrigger: false,
    isCondition: false,
  },
  send_campaign: {
    color: NODE_ACCENT,
    label: 'Send Campaign',
    desc: 'Send a message',
    isTrigger: false,
    isCondition: false,
  },
  update_lead: {
    color: NODE_ACCENT,
    label: 'Update Lead',
    desc: 'Edit lead fields',
    isTrigger: false,
    isCondition: false,
  },
  assign_agent: {
    color: NODE_ACCENT,
    label: 'Assign Agent',
    desc: 'Route to an agent',
    isTrigger: false,
    isCondition: false,
  },
  webhook: {
    color: NODE_ACCENT,
    label: 'Webhook',
    desc: 'Call an HTTP URL',
    isTrigger: false,
    isCondition: false,
  },
  trigger_workflow: {
    color: NODE_ACCENT,
    label: 'Trigger Workflow',
    desc: 'Start sub-workflow',
    isTrigger: false,
    isCondition: false,
  },
  // ── HTTP automation (works on either runtime) ────────────────────────────
  http_request: {
    color: NODE_ACCENT,
    label: 'Call API',
    desc: 'Call an external API and map its response into variables',
    isTrigger: false,
    isCondition: false,
  },
  // ── Conversation-flow-only: collect input + step-up ──────────────────────
  collect_input: {
    color: NODE_ACCENT,
    label: 'Get User Input',
    desc: 'Ask the customer a question and save their answer',
    isTrigger: false,
    isCondition: false,
  },
  grant_verification: {
    color: NODE_ACCENT,
    label: 'Mark Verified',
    desc: 'Mark this chat verified (place after your own check)',
    isTrigger: false,
    isCondition: false,
  },
  // ── Conversation-flow-only node types ─────────────────────────────────────
  // Valid only on a workflow whose Trigger Event is "When a Customer
  // Messages" (trigger.type === 'message.received') — executed by the
  // separate synchronous conversation-flow evaluator, not the Bull-queued
  // CRM engine. See PALETTE_ITEMS filtering below and isConversationFlow in
  // ConfigPanel for where that split shows up in this file.
  // Inbound pair shares a hue family (bright → deep cyan) so the two
  // conversation nodes read as related, and neither collides with the
  // trigger's amber or the CRM nodes' blues/violets.
  keyword_trigger: {
    color: NODE_ACCENT,
    label: 'When Customer Says…',
    desc: 'Branch condition — matches words/phrases in a typed message',
    isTrigger: false,
    isCondition: false,
  },
  button_reply_trigger: {
    color: NODE_ACCENT,
    label: 'When Button Tapped…',
    desc: 'Branch condition — matches a specific button/list tap',
    isTrigger: false,
    isCondition: false,
  },
  human_handoff: {
    color: NODE_ACCENT,
    label: 'Send to a Team Member',
    desc: 'Hand the chat to a human',
    isTrigger: false,
    isCondition: false,
  },
  canned_reply: {
    color: NODE_ACCENT,
    label: 'Send Response',
    desc: 'A fixed reply, no AI cost',
    isTrigger: false,
    isCondition: false,
  },
  rag_toggle: {
    color: NODE_ACCENT,
    label: 'Turn Smart Answers On/Off',
    desc: 'Override AI for this chat only',
    isTrigger: false,
    isCondition: false,
  },
  flag: {
    color: NODE_ACCENT,
    label: 'Flag for Review',
    desc: 'Mark this chat, bot keeps replying',
    isTrigger: false,
    isCondition: false,
  },
  ai_answer: {
    color: NODE_ACCENT,
    label: 'AI Answer',
    desc: 'Let a chosen AI agent answer from its knowledge base',
    isTrigger: false,
    isCondition: false,
  },
  send_media: {
    color: NODE_ACCENT,
    label: 'Send Media',
    desc: 'Send a document, image, video or audio from your Media library',
    isTrigger: false,
    isCondition: false,
  },
  send_list_message: {
    color: NODE_ACCENT,
    label: 'Send List Message',
    desc: 'A bottom-drawer menu of up to 10 options the customer picks from',
    isTrigger: false,
    isCondition: false,
  },
  send_button_message: {
    color: NODE_ACCENT,
    label: 'Send Button Message',
    desc: 'Body text with up to 3 tappable reply buttons',
    isTrigger: false,
    isCondition: false,
  },
  flow_end: {
    color: NODE_ACCENT,
    label: 'End',
    desc: 'Stop here — the bot stays silent and the AI does not answer after this',
    isTrigger: false,
    isCondition: false,
  },
} as const;

type NodeTypeName = keyof typeof NODE_META;

/**
 * Palette grouping. Nodes are grouped by *when they run*, never hidden —
 * matching how HubSpot greys out actions that don't apply to the workflow's
 * object type, and Twilio Studio keeps one widget library across voice and
 * messaging triggers. Hiding nodes makes the builder feel like it's missing
 * features; disabling them with a reason teaches the model instead.
 *
 * `availability` marks which trigger kind a group actually executes under:
 *  - 'any'      — works on every workflow
 *  - 'inbound'  — only on 'message.received' (synchronous conversation flow)
 *  - 'outbound' — only on lead/campaign/schedule triggers (async Bull engine)
 * A node outside the current workflow's kind is shown dimmed and undraggable,
 * because no executor exists for it in that runtime — it would fail at run time,
 * not just look odd.
 */
type PaletteAvailability = 'any' | 'inbound' | 'outbound';

interface PaletteGroup {
  title: string;
  availability: PaletteAvailability;
  items: Array<{ type: NodeTypeName; Icon: React.ComponentType<any> }>;
}

const PALETTE_GROUPS: PaletteGroup[] = [
  {
    title: 'Flow',
    availability: 'any',
    items: [
      { type: 'trigger', Icon: Bolt },
      { type: 'condition', Icon: CallSplit },
      // Valid on either runtime: a customer-message flow can kick off a
      // background CRM automation (e.g. tag the lead + start a nurture
      // campaign) just as a lead/campaign flow can chain into a sub-workflow.
      { type: 'trigger_workflow', Icon: AccountTree },
    ],
  },
  {
    // Works on either runtime — a customer-message flow and a lead/campaign flow
    // can both call out to an API and use the response.
    title: 'Integrations',
    availability: 'any',
    items: [{ type: 'http_request', Icon: Http }],
  },
  {
    // The flow's ONE Trigger (above, in Core) decides whether this flow runs
    // at all. This decides which BRANCH a given message enters through — a
    // flow can have several, wired to different downstream paths.
    //
    // 'button_reply_trigger' ("When Button Tapped…") was hidden from 2026-08-23
    // because nothing in the system ever sent an interactive message, so there
    // was no button/list-item ID for it to match. That producer now exists: the
    // "Send List Message" / "Send Button Message" nodes (below) send saved
    // Interactive Messages whose rows/buttons carry stable, minted ids, and the
    // inbound half feeds `buttonId ?? listItemId` into the evaluator — matched
    // here against this node's `buttonId`, deterministically ahead of any
    // keyword branch. So it is offered again, wired to the saved-message library
    // via a picker (no hand-typed ids). NOTE: this routes taps on messages WE
    // send only — Meta Ads (CTWA) quick-reply pills and template QUICK_REPLY
    // buttons still arrive as plain label text with no id, so those remain the
    // job of "When Customer Says…".
    title: 'Branching',
    availability: 'inbound',
    items: [
      { type: 'keyword_trigger', Icon: ChatBubbleOutline },
      { type: 'button_reply_trigger', Icon: TouchApp },
    ],
  },
  {
    title: 'Messages & actions',
    availability: 'inbound',
    items: [
      { type: 'canned_reply', Icon: Reply },
      { type: 'collect_input', Icon: QuestionAnswer },
      { type: 'send_media', Icon: AttachFile },
      { type: 'send_list_message', Icon: FormatListBulleted },
      { type: 'send_button_message', Icon: SmartButton },
      { type: 'rag_toggle', Icon: AutoAwesome },
      { type: 'ai_answer', Icon: SmartToy },
      { type: 'grant_verification', Icon: VerifiedUser },
      { type: 'flag', Icon: FlagIcon },
      { type: 'human_handoff', Icon: Forum },
      { type: 'flow_end', Icon: StopCircleOutlined },
    ],
  },
  {
    title: 'Campaigns & CRM',
    availability: 'outbound',
    items: [
      { type: 'delay', Icon: AccessTime },
      { type: 'send_campaign', Icon: Campaign },
      { type: 'update_lead', Icon: Person },
      { type: 'assign_agent', Icon: SupportAgent },
      { type: 'webhook', Icon: Http },
    ],
  },
];

/**
 * Icons for node types that are NO LONGER OFFERED in the palette but can still
 * appear on a canvas loaded from a saved workflow.
 *
 * Kept separate from PALETTE_GROUPS so hiding a node from authoring never costs
 * an existing node its icon — a retired node would otherwise render with the
 * generic fallback bolt and look broken. Currently empty: `button_reply_trigger`
 * graduated back into the "Branching" palette group once a producer existed to
 * mint matching ids. Re-add any future retiree here.
 */
const RETIRED_NODE_ICONS: Array<{ type: NodeTypeName; Icon: React.ComponentType<any> }> = [];

/** Flat lookup — node components need an icon by type, independent of grouping. */
const PALETTE_ITEMS: Array<{ type: NodeTypeName; Icon: React.ComponentType<any> }> = [
  ...PALETTE_GROUPS.flatMap((g) => g.items),
  ...RETIRED_NODE_ICONS,
];

/** Pastel icon-tile category + the small uppercase label above each node's title.
 *  Hue is for the ICON TILE only; cards stay neutral. */
type NodeCat = 'trigger' | 'logic' | 'message' | 'api' | 'action';
const NODE_CATEGORY: Partial<Record<NodeTypeName, { cat: NodeCat; label: string }>> = {
  trigger: { cat: 'trigger', label: 'TRIGGER' },
  keyword_trigger: { cat: 'logic', label: 'BRANCH' },
  button_reply_trigger: { cat: 'logic', label: 'BRANCH' },
  condition: { cat: 'logic', label: 'CONDITION' },
  delay: { cat: 'logic', label: 'WAIT' },
  canned_reply: { cat: 'message', label: 'MESSAGE' },
  collect_input: { cat: 'message', label: 'MESSAGE' },
  send_media: { cat: 'message', label: 'MESSAGE' },
  send_list_message: { cat: 'message', label: 'MESSAGE' },
  send_button_message: { cat: 'message', label: 'MESSAGE' },
  send_campaign: { cat: 'message', label: 'CAMPAIGN' },
  http_request: { cat: 'api', label: 'API REQUEST' },
  webhook: { cat: 'api', label: 'WEBHOOK' },
  trigger_workflow: { cat: 'action', label: 'WORKFLOW' },
  human_handoff: { cat: 'action', label: 'HANDOFF' },
  flag: { cat: 'action', label: 'ACTION' },
  rag_toggle: { cat: 'action', label: 'AI' },
  ai_answer: { cat: 'action', label: 'AI' },
  grant_verification: { cat: 'action', label: 'VERIFY' },
  flow_end: { cat: 'action', label: 'END' },
  update_lead: { cat: 'action', label: 'CRM' },
  assign_agent: { cat: 'action', label: 'CRM' },
};
function categoryOf(type: string): { cat: NodeCat; label: string } {
  return NODE_CATEGORY[type as NodeTypeName] ?? { cat: 'action', label: 'ACTION' };
}

/** Has the author given this step the minimum it needs to do something? Drives the
 *  check mark on the card and the "Step configured" footer. */
function isNodeConfigured(type: string, d: any): boolean {
  const has = (v: unknown) => typeof v === 'string' && v.trim() !== '';
  switch (type) {
    case 'keyword_trigger':
      return Array.isArray(d?.keywords) && d.keywords.length > 0;
    case 'button_reply_trigger':
      return has(d?.buttonId);
    case 'delay':
      return !!d?.amount;
    case 'send_campaign':
      return has(d?.campaignId);
    case 'update_lead':
      return (Array.isArray(d?.updates) && d.updates.length > 0) || !!d?.updateFields;
    case 'assign_agent':
    case 'ai_answer':
      return has(d?.agentId);
    case 'webhook':
      return has(d?.webhookUrl);
    case 'http_request':
      return has(d?.connectionId);
    case 'trigger_workflow':
      return has(d?.targetWorkflowId) || has(d?.workflowId);
    case 'canned_reply':
      return d?.mode === 'template'
        ? has(d?.templateName)
        : d?.mode === 'interactive'
          ? has(d?.interactiveMessageId) && (d?.interactiveBodyMode !== 'dynamic' || has(d?.interactiveBody))
          : has(d?.replyText);
    case 'collect_input': {
      // A plain-text question needs a prompt; an interactive one supplies the
      // question via the saved record (plus a body when that record is dynamic).
      const interactiveOk =
        has(d?.interactiveMessageId) && (d?.interactiveBodyMode !== 'dynamic' || has(d?.interactiveBody));
      return (has(d?.prompt) || interactiveOk) && !!(d?.target?.name || d?.target?.key);
    }
    case 'send_media':
      return has(d?.mediaAssetId);
    case 'send_list_message':
    case 'send_button_message':
      return has(d?.interactiveMessageId);
    case 'condition':
      // Business-hours is self-configured; match mode needs a variable to test.
      return d?.mode === 'match' ? has(d?.variable) : true;
    default:
      return true;
  }
}

function isGroupEnabled(availability: PaletteAvailability, isConversationFlow: boolean): boolean {
  if (availability === 'any') return true;
  return availability === (isConversationFlow ? 'inbound' : 'outbound');
}

/** Node type -> the trigger kind its palette group executes under. */
const NODE_AVAILABILITY = new Map<string, PaletteAvailability>(
  PALETTE_GROUPS.flatMap((g) => g.items.map((i) => [i.type as string, g.availability] as [string, PaletteAvailability]))
);

/** update_lead / assign_agent both write directly to the Lead collection via
 *  `ctx.lead._id` (update-lead.executor.ts, assign-agent.executor.ts) — unusable on any
 *  trigger whose leadSnapshot isn't backed by a real Lead document. Mirrors backend
 *  workflow-validation.util.ts's LEAD_IDENTITY_ONLY_NODES / NON_LEAD_ASYNC_TRIGGER_TYPES
 *  exactly, so the palette can never offer what the save-time validator would reject —
 *  kept in sync by hand (no import path between the two projects) since this is a small,
 *  stable pair of lists. */
const LEAD_IDENTITY_ONLY_NODE_TYPES = new Set<NodeTypeName>(['update_lead', 'assign_agent']);

/**
 * Node types gated on the `httpAutomationEnabled` plan feature — dimmed +
 * undraggable in the palette when the plan doesn't include it, matching the API
 * Connections screen's plan-lock. Backend re-checks at run time regardless.
 *
 * NOTE: 'collect_input' ("Get User Input") is intentionally NOT here. It has
 * standalone value — ask the customer something and save it to a contact field,
 * with no API call at all — and the backend gates it only on `workflowsEnabled`
 * (isAutomationActive), never on httpAutomationEnabled. Gating it here would make
 * the UI claim a plan the runtime doesn't actually require. 'grant_verification'
 * IS gated because it only has meaning for an http_request that requires step-up.
 */
const HTTP_AUTOMATION_NODE_TYPES = new Set<NodeTypeName>(['http_request', 'grant_verification']);
const NON_LEAD_ASYNC_TRIGGER_TYPES = new Set<string>(['appointment.created', 'appointment.status_changed']);

// Only the trigger events that are practically useful and actually fire are
// offered in the picker. Removed from the UI (but NOT from the backend
// dispatcher — existing workflows on them keep running):
//   • campaign.sent / campaign.failed / campaign.replied — subscribed but
//     never published anywhere, so they can never fire as a trigger.
//   • lead.updated / lead.status_changed — they do fire, but a workflow that
//     runs on every field edit is rarely what a tenant actually wants; left
//     out to keep the picker to the events that make practical sense.
// A saved workflow already on a hidden trigger still loads and edits fine —
// TRIGGER_LABEL below falls back to the raw value for anything not listed here.
const TRIGGER_TYPES = [
  { value: 'lead.created', label: 'Incoming Lead' },
  { value: 'lead.priority_changed', label: 'Lead Priority Changed' },
  { value: 'appointment.created', label: 'Incoming Appointment' },
  { value: 'manual', label: 'Manual / API' },
  { value: 'message.received', label: 'Any Incoming WhatsApp Message' },
];

/** 0 = Sunday, matching Date#getDay and the backend's business-hours util. */
const WEEKDAYS = [
  { value: 0, label: 'Sun' },
  { value: 1, label: 'Mon' },
  { value: 2, label: 'Tue' },
  { value: 3, label: 'Wed' },
  { value: 4, label: 'Thu' },
  { value: 5, label: 'Fri' },
  { value: 6, label: 'Sat' },
];

/** The browser's own zone is the right default — an author configuring hours
 *  almost always means their own working hours. Stored explicitly either way,
 *  never left to the server's local timezone. Canonicalised and snapped to the
 *  curated list below, so the default always corresponds to a real dropdown row. */
const guessTimezone = (): string => {
  try {
    const raw = Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (!raw) return 'UTC';
    const canonical = canonicalTimezone(raw);
    return TIMEZONES.some((t) => t.value === canonical) ? canonical : 'UTC';
  } catch {
    return 'UTC';
  }
};

/**
 * A short, curated business-timezone list rather than the ~450 zones
 * Intl.supportedValuesOf returns. Nobody sets opening hours in
 * America/Indiana/Tell_City, and a 450-row dropdown made picking the right
 * one a chore — these are the zones a support desk actually operates in,
 * labelled by the abbreviation people say out loud ("IST", "PT") with the
 * IANA id kept visible so the choice stays unambiguous.
 */
const TIMEZONES: Array<{ value: string; label: string }> = [
  { value: 'Asia/Kolkata', label: 'IST — India (Asia/Kolkata)' },
  { value: 'UTC', label: 'UTC — Coordinated Universal Time' },
  { value: 'Asia/Dubai', label: 'GST — Gulf (Asia/Dubai)' },
  { value: 'Asia/Karachi', label: 'PKT — Pakistan (Asia/Karachi)' },
  { value: 'Asia/Dhaka', label: 'BST — Bangladesh (Asia/Dhaka)' },
  { value: 'Asia/Singapore', label: 'SGT — Singapore (Asia/Singapore)' },
  { value: 'Asia/Tokyo', label: 'JST — Japan (Asia/Tokyo)' },
  { value: 'Europe/London', label: 'GMT/BST — UK (Europe/London)' },
  { value: 'Europe/Berlin', label: 'CET — Central Europe (Europe/Berlin)' },
  { value: 'America/New_York', label: 'ET — US Eastern (America/New_York)' },
  { value: 'America/Chicago', label: 'CT — US Central (America/Chicago)' },
  { value: 'America/Denver', label: 'MT — US Mountain (America/Denver)' },
  { value: 'America/Los_Angeles', label: 'PT — US Pacific (America/Los_Angeles)' },
  { value: 'Australia/Sydney', label: 'AET — Sydney (Australia/Sydney)' },
];

/**
 * Deprecated IANA aliases → the canonical id used in TIMEZONES above.
 *
 * These are all still perfectly valid at run time (Intl resolves
 * 'Asia/Calcutta' exactly like 'Asia/Kolkata'), so this is NOT a correctness
 * fix — it exists so a node saved with an alias still matches a dropdown row
 * instead of rendering as an empty Select and looking unconfigured.
 */
const TIMEZONE_ALIASES: Record<string, string> = {
  'Asia/Calcutta': 'Asia/Kolkata',
  'Asia/Rangoon': 'Asia/Yangon',
  'Asia/Saigon': 'Asia/Ho_Chi_Minh',
  'Europe/Kiev': 'Europe/Kyiv',
  'US/Eastern': 'America/New_York',
  'US/Central': 'America/Chicago',
  'US/Mountain': 'America/Denver',
  'US/Pacific': 'America/Los_Angeles',
  'Europe/Belfast': 'Europe/London',
  GB: 'Europe/London',
  'Etc/UTC': 'UTC',
  'Etc/GMT': 'UTC',
};

const canonicalTimezone = (tz: string): string => TIMEZONE_ALIASES[tz] ?? tz;

/**
 * The values a conversation-flow Condition node is BORN with.
 *
 * These exist because the config panel below renders each field as
 * `data.x ?? <fallback>` — which shows the author a fully populated form
 * ("We are OPEN right now", their own zone, 09:00–17:00, every day selected)
 * while `data` itself stays `{}` unless they happen to touch each dropdown.
 * A node saved in that state reaches the backend with no timezone, so
 * business-hours.util's isWithinBusinessHours returns null, the executor
 * reports failure, and the evaluator fails open to the normal AI pipeline —
 * i.e. the branch the author configured silently never runs.
 *
 * Seeding real values at creation (and normalising at save, for nodes created
 * before this existed) keeps "what the panel shows" and "what gets persisted"
 * the same thing, which is the only version of this that can't drift again.
 */
/** Operators for a conversation-flow "match a value" condition + their labels.
 *  String ops are case-insensitive + trimmed (see condition.executor.ts); gt/lt
 *  are numeric; exists/not_exists need no value. `in` ("is any of") takes a
 *  comma-separated list so one branch can catch true/yes/ok/okay at once. */
const MATCH_OPERATORS: { value: string; label: string; needsValue: boolean }[] = [
  { value: 'eq', label: 'equals', needsValue: true },
  { value: 'neq', label: 'does not equal', needsValue: true },
  { value: 'in', label: 'is any of', needsValue: true },
  { value: 'contains', label: 'contains', needsValue: true },
  { value: 'gt', label: 'greater than', needsValue: true },
  { value: 'lt', label: 'less than', needsValue: true },
  { value: 'exists', label: 'is set', needsValue: false },
  { value: 'not_exists', label: 'is empty', needsValue: false },
];
const MATCH_OP_LABEL: Record<string, string> = Object.fromEntries(
  MATCH_OPERATORS.map((o) => [o.value, o.label])
);

const conversationConditionDefaults = () => ({
  mode: 'business_hours',
  field: 'business_hours',
  /** Fixed. The node asks one question — "is the office open right now?" —
   *  and the canvas's Yes/No handles carry the answer, so there is no operator
   *  picker any more. The backend still honours a legacy 'neq' it finds on an
   *  older node; nothing here ever writes one. */
  operator: 'eq',
  timezone: guessTimezone(),
  startTime: '09:00',
  endTime: '17:00',
  /** Every day, listed explicitly. The backend also reads `[]` as "every day",
   *  but an explicit list is what the day picker below now round-trips — see
   *  the comment there for why the old empty-means-all convention was a trap. */
  days: WEEKDAYS.map((d) => d.value),
});

/** Plain-words summary of an "Open on" selection, shown under the day picker.
 *  The picker alone never made a wrong set obvious — seven small buttons read
 *  as decoration, and "open weekends only" looked much like "open every day"
 *  at a glance. Spelling it out is what makes a mis-click self-evident. */
function describeOpenDays(days: unknown): string {
  const selected = Array.isArray(days)
    ? days.filter((d) => Number.isInteger(d) && d >= 0 && d <= 6).sort((a, b) => a - b)
    : [];
  // Both an empty list and a full week mean the same thing to the backend.
  if (selected.length === 0 || selected.length === 7) return 'Open every day.';
  const names = selected
    .map((d) => WEEKDAYS.find((w) => w.value === d)?.label)
    .filter(Boolean) as string[];
  const isWeekdaysOnly = selected.join() === '1,2,3,4,5';
  return isWeekdaysOnly
    ? 'Open Mon–Fri. Closed all day on Sat and Sun.'
    : `Open only on ${names.join(', ')}. Closed all day otherwise.`;
}

/** Nodes saved before the rename still carry the old default title — show the new
 *  one. A title the user typed themselves is never touched. */
const LEGACY_LABELS: Record<string, string> = {
  'Ask for Details': 'Get User Input',
  'Send an Instant Reply': 'Send Response',
  'Send a File': 'Send Media',
  'Call an API': 'Call API',
};
function modernLabel(label: string | undefined): string | undefined {
  return label !== undefined && LEGACY_LABELS[label] ? LEGACY_LABELS[label] : label;
}

/** Standard "i" button — the explanation shows on hover/focus instead of taking
 *  up a permanent box in the config panel. */
function InfoHint({ children, wide }: { children: React.ReactNode; wide?: boolean }) {
  return (
    <Tooltip
      arrow
      placement="left"
      title={<Box sx={{ fontSize: 12.5, lineHeight: 1.45, maxWidth: wide ? 340 : 260 }}>{children}</Box>}
    >
      <IconButton
        size="small"
        aria-label="About this step"
        sx={{ alignSelf: 'flex-end', p: 0.25, mt: '-6px !important', color: 'var(--wf-text-faint, #5f6b65)', '&:hover': { color: 'var(--wf-accent, #1d6b4a)' } }}
      >
        <InfoOutlinedIcon sx={{ fontSize: 17 }} />
      </IconButton>
    </Tooltip>
  );
}

/** Coerces any error-response shape into a string safe to render inside an <Alert>.
 *  Most backend errors are already `{ error: string }`, but not every error path in
 *  this codebase serializes the same way (an uncaught exception vs. a validation
 *  result can differ) — a non-string `error` value here previously reached
 *  setError()/<Alert>{error}</Alert> directly and crashed the whole canvas with
 *  "Objects are not valid as a React child". Handles the common `{ code, message }`
 *  shape explicitly; falls back to a generic message for anything else unexpected
 *  rather than ever handing a non-string to setError. */
function toErrorMessage(value: unknown, fallback: string): string {
  if (typeof value === 'string' && value.trim()) return value;
  if (value && typeof value === 'object' && typeof (value as any).message === 'string') {
    return (value as any).message;
  }
  return fallback;
}

/** Drops keys that carry no value, so spreading a partially-filled node over
 *  the defaults lets a real default win instead of an empty string clobbering
 *  it. `days: []` is meaningful ("every day") and deliberately kept. */
function pruneEmpty(data: Record<string, any>): Record<string, any> {
  return Object.fromEntries(
    Object.entries(data).filter(([, v]) => v !== undefined && v !== null && v !== '')
  );
}

/** True when a condition node would fail isWithinBusinessHours() at run time.
 *  Mirrors that util's own null-returning cases, not a looser "is anything set". */
function isUnconfiguredBusinessHours(data: Record<string, any> | undefined): boolean {
  if (data?.['field'] === 'routing.state') return false; // legacy, never time-based
  const tz = typeof data?.['timezone'] === 'string' ? data['timezone'].trim() : '';
  const hhmm = /^\d{1,2}:\d{2}$/;
  return (
    !tz ||
    !hhmm.test(String(data?.['startTime'] ?? '')) ||
    !hhmm.test(String(data?.['endTime'] ?? ''))
  );
}

/**
 * How long a "sticky" conversation-flow node's effect lasts.
 *
 * Shared by AI Answer's agent pin and Turn Smart Answers' override so the two
 * read identically — they sit next to each other in flows and previously had
 * silently opposite lifetimes (one message vs forever), which was a trap.
 *
 * Values are HOURS (the stored unit). 0 = the original non-sticky behaviour,
 * kept so nodes saved before these fields existed are unaffected.
 */
const STICKY_DURATION_OPTIONS: Array<{ value: number; label: string; forAgent: string }> = [
  { value: 0, label: 'This message only', forAgent: 'This message only' },
  { value: 12, label: 'The next 12 hours', forAgent: 'The next 12 hours' },
  { value: 24, label: 'The next 24 hours', forAgent: 'The next 24 hours' },
];

/** Matches a stored hours value to its picker row. The tolerance is for
 *  fractional values, which the API accepts (validation allows any 0 < h <= 24)
 *  even though this picker only offers whole-hour rows. */
export const stickyOptionValue = (stored: unknown): string => {
  const hours = typeof stored === 'number' && Number.isFinite(stored) ? stored : 0;
  const match = STICKY_DURATION_OPTIONS.find((o) => Math.abs(o.value - hours) < 1e-6);
  return String(match ? match.value : hours > 0 ? hours : 0);
};

/**
 * Rows to render for a field currently holding `stored`.
 *
 * The picker offers three durations, but the API accepts any value in
 * 0 < hours <= 24 (workflow-validation.util.ts), and one retired option — a
 * 5-minute entry that existed briefly so a pin's expiry could be watched
 * without waiting half a day — may still be saved on a node. Without this,
 * every such value renders as "This message only" while the flow actually keeps
 * pinning: the dropdown would state the opposite of the behaviour, and there'd
 * be no way to even see what a node was set to.
 *
 * So an unrecognised duration gets its own row, labelled with its real length.
 * It is never offered for a node that doesn't already hold it, and picking any
 * standard row replaces it for good — retiring the value without ever lying
 * about it in the meantime.
 */
/**
 * Agent rows to render for a picker currently holding `storedId`.
 *
 * The list comes from getAgents({ isActive: true }), so an agent that has since
 * been deactivated or deleted is absent from it — and a Select whose value
 * matches no MenuItem renders EMPTY. The node would keep its agentId and the
 * canvas would keep reading "Agent: Lead Qualifier", while the editor showed a
 * blank field: the one place an author looks to answer "who is pinned here?"
 * was the one place that couldn't tell them.
 *
 * A missing agent therefore gets its own row, named from the denormalized
 * `agentName` the picker stored when it was chosen, so it stays visible and
 * self-explanatory. Marked unavailable rather than silently listed — it is a
 * real misconfiguration: at runtime resolveAnsweringAgent's selectSpecificAgent
 * returns null for it, the flow falls through to normal routing, the dead pin
 * is cleared and PINNED_AGENT_UNAVAILABLE is raised.
 */
export const agentOptionsFor = (
  agents: Array<{ _id: string; name: string }>,
  storedId: unknown,
  storedName?: unknown
): Array<{ _id: string; name: string }> => {
  const id = typeof storedId === 'string' ? storedId.trim() : '';
  if (!id || agents.some((a) => a._id === id)) return agents;
  const name = typeof storedName === 'string' && storedName.trim() ? storedName.trim() : null;
  return [...agents, { _id: id, name: `${name ?? `…${id.slice(-8)}`} (unavailable)` }];
};

export const stickyOptionsFor = (
  stored: unknown
): Array<{ value: number; label: string; forAgent: string }> => {
  const hours = typeof stored === 'number' && Number.isFinite(stored) ? stored : 0;
  if (hours <= 0 || STICKY_DURATION_OPTIONS.some((o) => Math.abs(o.value - hours) < 1e-6)) {
    return STICKY_DURATION_OPTIONS;
  }
  const label = hours < 1 ? `${Math.round(hours * 60)} minutes` : `${Math.round(hours)} hours`;
  return [...STICKY_DURATION_OPTIONS, { value: hours, label, forAgent: label }];
};

/** Compact canvas-subtitle form of a duration — "5m" / "12h" / "" for none. */
const stickyDurationLabel = (stored: unknown): string => {
  const hours = typeof stored === 'number' && Number.isFinite(stored) ? stored : 0;
  if (hours <= 0) return '';
  return hours < 1 ? `${Math.round(hours * 60)}m` : `${Math.round(hours)}h`;
};

const RAG_OVERRIDE_VALUES = [
  { value: 'inherit', label: 'Use the account default' },
  { value: 'force_on', label: 'Always on for this chat' },
  { value: 'force_off', label: 'Always off for this chat' },
];
const RAG_OVERRIDE_LABEL: Record<string, string> = Object.fromEntries(
  RAG_OVERRIDE_VALUES.map((v) => [v.value, v.label])
);

const TRIGGER_LABEL: Record<string, string> = Object.fromEntries(
  TRIGGER_TYPES.map((t) => [t.value, t.label])
);

const LEAD_FIELDS = ['status', 'source', 'customerName', 'customerPhone', 'customerEmail'];

/** Mirrors backend LEAD_STATUSES (Lead.model.ts) — kept as a local literal rather than a
 *  shared import since the frontend has no existing bridge to backend model constants. */
const LEAD_STATUS_OPTIONS = ['new', 'contacted', 'qualified', 'unqualified', 'converted', 'lost', 'archived'];

/** Mirrors backend LeadSource (Lead.model.ts). */
const LEAD_SOURCE_OPTIONS = [
  { value: 'website', label: 'Website' },
  { value: 'whatsapp', label: 'WhatsApp' },
  { value: 'meta', label: 'Meta Ads' },
  { value: 'hubspot', label: 'HubSpot' },
  { value: 'google_sheets', label: 'Google Sheets' },
  { value: 'excel', label: 'CSV / Excel import' },
  { value: 'api', label: 'External API' },
  { value: 'manual', label: 'Manual (added by a teammate)' },
];

/** Fields the trigger-node filter editor offers, beyond the dedicated "Lead type"
 *  (isReEnquiry) toggle — matchesTriggerFilters (trigger-filter.util.ts) can already
 *  evaluate every one of these off ILeadSnapshot; 'customAttributes' is special-cased
 *  below since its value lives at a tenant-typed key, not a fixed property name. */
const TRIGGER_FILTER_FIELD_OPTIONS = [
  { value: 'source', label: 'Source' },
  { value: 'status', label: 'Status' },
  { value: 'customerCountry', label: 'Country' },
  { value: 'customerCompany', label: 'Company' },
  { value: 'tags', label: 'Tags' },
  { value: 'budget', label: 'Budget' },
  { value: 'interest', label: 'Interest' },
  { value: 'estimatedValue', label: 'Estimated value' },
  { value: 'assignedTo', label: 'Assigned to' },
  { value: 'customAttributes', label: 'Custom attribute…' },
];

/** Fields the Lead-priority trigger offers: the new band, the band it left, or the score. */
const PRIORITY_TRIGGER_FILTER_FIELD_OPTIONS = [
  { value: 'priority', label: 'New priority' },
  { value: 'previousPriority', label: 'Previous priority' },
  { value: 'score', label: 'Score' },
  ...TRIGGER_FILTER_FIELD_OPTIONS.filter((f) => ['source', 'status', 'assignedTo', 'tags'].includes(f.value)),
];

/** Mirrors backend LeadPriority bands (lead-scoring.engine.ts). */
const PRIORITY_BAND_OPTIONS = ['hot', 'high', 'medium', 'low'];

/** Mirrors backend APPOINTMENT_STATUSES (Appointment.model.ts) — separate vocabulary
 *  from LEAD_STATUS_OPTIONS, must never share a dropdown with it. */
const APPOINTMENT_STATUS_OPTIONS = ['requested', 'confirmed', 'cancelled', 'completed', 'no_show'];

/** Mirrors backend AppointmentSource (Appointment.model.ts). 'instagram' is deliberately
 *  omitted — the schema enum has it, but nothing in the codebase produces it yet (no
 *  Instagram appointment-extraction call site exists), so offering it here would be a
 *  filter an author could configure that can never match anything. */
const APPOINTMENT_SOURCE_OPTIONS = [
  { value: 'whatsapp', label: 'WhatsApp' },
  { value: 'webchat', label: 'Website chat' },
  { value: 'manual', label: 'Manual (added by a teammate)' },
  { value: 'api', label: 'External API' },
];

/** Fields the Appointment trigger's filter editor offers. No isReEnquiry-equivalent —
 *  every appointment.created is unconditionally a create (see IAppointmentSnapshot /
 *  appointment.service.ts: a phone number can carry many appointments, so there's no
 *  merge-vs-create ambiguity to filter on). No 'estimatedValue' either — appointments
 *  have no numeric field today. 'requestedAt' is deliberately NOT listed here — it has
 *  its own dedicated "Date & time" toggle below (same excludeFields pattern as
 *  lead.created's isReEnquiry), since "is_empty"/"is_not_empty" reads far more clearly
 *  as a two-state toggle than a raw operator row for this specific field. */
const APPOINTMENT_FILTER_FIELD_OPTIONS = [
  { value: 'source', label: 'Source' },
  { value: 'status', label: 'Status' },
  { value: 'appointmentType', label: 'Appointment type' },
  { value: 'customerCompany', label: 'Company' },
  { value: 'requestedText', label: "Requested time (customer's words)" },
  { value: 'tags', label: 'Tags' },
  { value: 'reference', label: 'Reference' },
  { value: 'assignedTo', label: 'Assigned to' },
  { value: 'customAttributes', label: 'Custom attribute…' },
];

const OPERATORS = [
  { value: 'eq', label: 'equals' },
  { value: 'neq', label: 'not equals' },
  { value: 'contains', label: 'contains' },
  { value: 'not_contains', label: 'not contains' },
  { value: 'gt', label: 'greater than' },
  { value: 'lt', label: 'less than' },
  { value: 'gte', label: 'greater or equal' },
  { value: 'lte', label: 'less or equal' },
  { value: 'is_empty', label: 'is empty' },
  { value: 'is_not_empty', label: 'is not empty' },
];
const DELAY_UNITS = ['seconds', 'minutes', 'hours', 'days'];
const HTTP_METHODS = ['GET', 'POST', 'PUT', 'PATCH', 'DELETE'];

// ─── Subtitle helper ─────────────────────────────────────────────────────────

function subtitle(type: string, raw: Record<string, any>): string {
  const d = raw as any;
  switch (type) {
    case 'trigger':
      return TRIGGER_LABEL[d.triggerType] ?? d.triggerType ?? 'Select trigger event';
    case 'condition':
      // Match-a-value mode: show the comparison (API-result routing).
      if (d.mode === 'match') {
        const v = String(d.variable ?? '').trim();
        if (!v) return 'Match a value…';
        if (d.operator === 'exists') return `{{${v}}} is set`;
        if (d.operator === 'not_exists') return `{{${v}}} is empty`;
        return `{{${v}}} ${MATCH_OP_LABEL[d.operator as string] ?? d.operator ?? '='} "${d.value ?? ''}"`;
      }
      // A conversation-flow condition carries time fields instead of a lead
      // field; show the hours rather than a meaningless `undefined eq ""`.
      if (d.startTime || d.endTime || d.timezone) {
        // Day restrictions are appended because a set like [Sun, Sat] is the
        // difference between a condition that answers Yes and one that never
        // does on a weekday — worth seeing without opening the panel.
        const selectedDays: number[] = Array.isArray(d.days) ? d.days : [];
        const dayNote =
          selectedDays.length === 0 || selectedDays.length === 7
            ? ''
            : ` · ${selectedDays
                .slice()
                .sort((a, b) => a - b)
                .map((n: number) => WEEKDAYS.find((w) => w.value === n)?.label)
                .filter(Boolean)
                .join('/')}`;
        return `${d.operator === 'neq' ? 'Closed' : 'Open'} ${d.startTime ?? '09:00'}–${d.endTime ?? '17:00'}${dayNote}`;
      }
      return d.field && d.operator
        ? `${d.field} ${d.operator} "${d.value ?? ''}"`
        : 'Define condition…';
    case 'delay':
      return d.amount ? `Wait ${d.amount} ${d.unit ?? 'minutes'}` : 'Set delay duration…';
    case 'send_campaign':
      return d.campaignId ? `Campaign: …${String(d.campaignId).slice(-8)}` : 'Set campaign ID…';
    case 'update_lead': {
      const updates = Array.isArray(d.updates) ? d.updates : [];
      const keys = updates.map((u: any) => u.field).filter(Boolean);
      return keys.length ? `Set: ${keys.slice(0, 3).join(', ')}` : 'Define field updates…';
    }
    case 'assign_agent':
      return d.agentId ? `Agent: …${String(d.agentId).slice(-8)}` : 'Set agent ID…';
    case 'webhook':
      return d.webhookUrl
        ? `${d.webhookMethod ?? 'POST'} ${d.webhookUrl.replace(/^https?:\/\//, '').slice(0, 28)}`
        : 'Set webhook URL…';
    case 'trigger_workflow':
      return d.targetWorkflowId
        ? `Workflow: …${String(d.targetWorkflowId).slice(-8)}`
        : 'Set workflow ID…';
    case 'http_request':
      // The request itself lives on the API; the node only shows which one it calls.
      return d.connectionName ? `API: ${d.connectionName}` : 'Choose an API…';
    case 'collect_input': {
      const name = d.target?.kind === 'flow' ? d.target?.name : d.target?.key;
      const dest = name ? `{{${name}}}` : 'set a variable…';
      if (d.interactiveMessageId) {
        return `Ask (${d.interactiveMessageName ?? 'buttons/list'}) → ${dest}`;
      }
      return d.prompt ? `Ask → ${dest}` : 'Write a question…';
    }
    case 'grant_verification':
      return 'Marks this chat as verified';
    case 'flow_end':
      return 'Stops here — bot stays silent';
    case 'keyword_trigger': {
      const kws = Array.isArray(d.keywords) ? d.keywords : [];
      return kws.length ? kws.slice(0, 3).join(', ') + (kws.length > 3 ? '…' : '') : 'Add keywords…';
    }
    case 'button_reply_trigger':
      if (!d.buttonId) return 'Pick a button or list row…';
      return d.buttonLabel ? `When tapped: ${d.buttonLabel}` : `Button/list id: ${d.buttonId}`;
    case 'human_handoff':
      return 'Hands the chat off to your team';
    case 'canned_reply':
      return d.mode === 'template'
        ? d.templateName
          ? `Template: ${d.templateName}`
          : 'Choose a template…'
        : d.mode === 'interactive'
          ? d.interactiveMessageId
            ? `${d.interactiveMessageKind === 'list' ? 'List' : 'Buttons'}: ${d.interactiveMessageName ?? 'saved message'}`
            : 'Choose a saved message…'
          : d.replyText
            ? String(d.replyText).slice(0, 40)
            : 'Write a reply…';
    case 'send_media':
      return d.mediaAssetId ? 'Sends a file' : 'Choose a file…';
    case 'send_list_message':
      return d.interactiveMessageId ? `List: ${d.interactiveMessageName ?? 'saved list'}` : 'Choose a saved list…';
    case 'send_button_message':
      return d.interactiveMessageId
        ? `Message: ${d.interactiveMessageName ?? 'saved message'}`
        : 'Choose a saved message…';
    case 'rag_toggle': {
      const label = RAG_OVERRIDE_LABEL[d.ragOverride as string] ?? 'Use the account default';
      // Same reasoning as ai_answer: a permanent override and a 12h one look
      // identical on the canvas otherwise.
      const forHow = (d.ragOverride ?? 'inherit') === 'inherit' ? '' : stickyDurationLabel(d.overrideDurationHours);
      return forHow ? `${label} · ${forHow}` : label;
    }
    case 'flag':
      return d.flagReason ? String(d.flagReason).slice(0, 40) : 'No reason set';
    case 'ai_answer': {
      // Prefer the denormalized agent name (stored on pick); fall back to the id
      // tail for nodes saved before the name was captured.
      const who = d.agentName
        ? `Agent: ${d.agentName}`
        : d.agentId
          ? `Agent: …${String(d.agentId).slice(-8)}`
          : 'Pick an AI agent…';
      // Surface the pin on the canvas: "answers once" vs "owns this chat for a
      // day" are very different behaviours to leave invisible behind a click.
      const pin = stickyDurationLabel(d.pinDurationHours);
      return d.agentId && pin ? `${who} · ${pin}` : who;
    }
    default:
      return '';
  }
}

// ─── Custom node components (must be module-level for stable nodeTypes) ───────

function WFNodeBase({ id, data, selected, type }: NodeProps & { type: string }) {
  const meta = NODE_META[type as NodeTypeName];
  const label = meta?.label ?? type;
  const Icon = PALETTE_ITEMS.find((p) => p.type === type)?.Icon ?? Bolt;
  const { cat, label: eyebrow } = categoryOf(type);
  const configured = isNodeConfigured(type, data);
  const vars: string[] = Array.isArray(data.__vars) ? data.__vars : [];
  // ReactFlow caches a node's handle set; when we add/remove the API "Failed"
  // output (branchOnError), it must be told to recompute or the new handle is
  // invisible and unconnectable.
  const updateNodeInternals = useUpdateNodeInternals();
  useEffect(() => {
    updateNodeInternals(id);
  }, [id, type, data.branchOnError, updateNodeInternals]);

  return (
    <div
      className={`wf-node wf-cat--${cat}${selected ? ' selected' : ''}${data.__errored ? ' errored' : ''}${data.__incompatible ? ' incompatible' : ''}`}
      title={data.__incompatible || undefined}
    >
      {!meta?.isTrigger && <Handle type="target" position={Position.Left} />}
      <div className="wf-node__header">
        <span className="wf-node__icon">
          <Icon style={{ fontSize: 18 }} />
        </span>
        <div className="wf-node__titles">
          <span className="wf-node__eyebrow">{eyebrow}</span>
          <span className="wf-node__label">{data.label || label}</span>
        </div>
        {configured ? (
          <span className="wf-node__status" title="Configured">
            <Check />
          </span>
        ) : (
          <span className="wf-node__status is-todo" title="Needs setup" />
        )}
      </div>
      {(data.subtitle || vars.length > 0) && (
        <div className="wf-node__body">
          {data.subtitle && <div className="wf-node__text">{data.subtitle}</div>}
          {vars.length > 0 && (
            <div className="wf-node__vars" title="Variables this step saves">
              {vars.slice(0, 4).map((v) => (
                <span key={v} className="wf-node__var">
                  <ArrowDownward />
                  {v}
                </span>
              ))}
            </div>
          )}
        </div>
      )}
      {meta?.isCondition ? (
        <>
          <Handle type="source" position={Position.Right} id="yes" style={{ top: '33%' }} />
          <Handle type="source" position={Position.Right} id="no" style={{ top: '67%' }} />
          <div className="wf-node__branches">
            <span className="wf-node__branch-label yes">Yes</span>
            <span className="wf-node__branch-label no">No</span>
          </div>
        </>
      ) : type === 'http_request' && data.branchOnError ? (
        // The engine routes a failed call to the 'error' edge (http-edge.util). The
        // success handle keeps NO id so edges drawn before the toggle still attach.
        <>
          <Handle type="source" position={Position.Right} style={{ top: '33%' }} />
          <Handle type="source" position={Position.Right} id="error" style={{ top: '67%' }} />
          <div className="wf-node__branches">
            <span className="wf-node__branch-label yes">OK</span>
            <span className="wf-node__branch-label no">Failed</span>
          </div>
        </>
      ) : type === 'flow_end' ? (
        // Terminal node: nothing runs after it, so it offers no outgoing handle.
        null
      ) : (
        <Handle type="source" position={Position.Right} />
      )}
    </div>
  );
}

// One typed wrapper per node type so ReactFlow doesn't re-mount on render
function mkNode(t: NodeTypeName) {
  const C = (props: NodeProps) => <WFNodeBase {...props} type={t} />;
  C.displayName = `WF_${t}`;
  return C;
}

const DEFAULT_FALLBACK_NODE_ID = '__default_fallback';

const nodeTypes: NodeTypes = {
  trigger: mkNode('trigger'),
  condition: mkNode('condition'),
  delay: mkNode('delay'),
  send_campaign: mkNode('send_campaign'),
  update_lead: mkNode('update_lead'),
  assign_agent: mkNode('assign_agent'),
  webhook: mkNode('webhook'),
  http_request: mkNode('http_request'),
  collect_input: mkNode('collect_input'),
  grant_verification: mkNode('grant_verification'),
  trigger_workflow: mkNode('trigger_workflow'),
  keyword_trigger: mkNode('keyword_trigger'),
  button_reply_trigger: mkNode('button_reply_trigger'),
  human_handoff: mkNode('human_handoff'),
  canned_reply: mkNode('canned_reply'),
  rag_toggle: mkNode('rag_toggle'),
  flag: mkNode('flag'),
  ai_answer: mkNode('ai_answer'),
  send_media: mkNode('send_media'),
  send_list_message: mkNode('send_list_message'),
  send_button_message: mkNode('send_button_message'),
  flow_end: mkNode('flow_end'),
};

// ─── Edge defaults ────────────────────────────────────────────────────────────

const EDGE_DEFAULTS: Partial<Edge> = {
  // Orthogonal, hairline connectors with a small arrowhead (direction matters in a flow).
  type: 'smoothstep',
  markerEnd: { type: MarkerType.ArrowClosed, color: '#30363d', width: 12, height: 12 },
  style: { stroke: '#30363d', strokeWidth: 1.5 },
  animated: false,
  // Widen the invisible hit-area so a thin 2px line is easy to right-click.
  interactionWidth: 18,
};

/** Edge/arrow colour per builder theme. Edges carry their colour as an inline style
 *  (React Flow applies `style` directly), so a theme switch recolours them in JS. */
const EDGE_COLOR = { light: '#848f8a', dark: '#586478' } as const;

/** Canvas dot-grid + minimap mask colours (React Flow takes these as props). */
const CANVAS_COLORS = {
  light: { dots: '#aab4ae', mask: 'rgba(214,220,216,0.55)', node: '#1d6b4a' },
  dark: { dots: '#2b3342', mask: 'rgba(13,17,23,0.7)', node: '#4fc08d' },
} as const;

/** Live zoom % for the zoom pill. Its own component so panning/zooming re-renders only
 *  this label, never the 4,000-line canvas component. */
function ZoomReadout() {
  const { zoom } = useViewport();
  return <span className="wf-zoom__pct">{Math.round(zoom * 100)}%</span>;
}

function mkEdge(base: Edge): Edge {
  return { ...EDGE_DEFAULTS, ...base };
}

// Chip-list editor (bulk-paste keyword/exclusion input) now lives in
// @/components/common/ChipListInput — shared with the Agent form's routing
// keywords rather than duplicated here. MAX_CHIP_LIST_LENGTH (below) must
// match MAX_KEYWORDS_PER_WORKFLOW in
// backend/src/services/conversation-flow/keyword-matcher.util.ts — that file
// silently drops keywords beyond the cap (it must never throw on the reply
// hot path), but workflow-validation.util.ts rejects an over-limit save with
// a clear error, so a save-time mismatch here would surface as a save error,
// not a silent UI/backend drift.
const MAX_CHIP_LIST_LENGTH = 300;

// Number of positional {{1}}, {{2}}... placeholders in a Meta template body —
// same small pure helper CampaignForm.tsx uses; not worth cross-file coupling
// for a 10-line regex scan.
function countTemplateBodyVariables(text: string): number {
  const found = new Set<number>();
  const re = /\{\{(\d+)\}\}/g;
  let m: RegExpExecArray | null;
  while ((m = re.exec(text || ''))) {
    found.add(parseInt(m[1] || '0', 10));
  }
  return found.size;
}

/** Does this template have a URL button whose link ends in a `{{n}}` slot?
 *  Mirrors CampaignForm.tsx's helper of the same name and the backend's own
 *  detection (whatsapp-template-components.util.ts's DYNAMIC_PLACEHOLDER) —
 *  AUTHENTICATION templates never reach this config panel (filtered out
 *  where `templates` is fetched below), so there's no auto-resolve case to
 *  consider here; a match always means the field is required. */
function hasDynamicUrlButton(template: WhatsAppTemplate | undefined): boolean {
  return !!template?.components.buttons?.some(
    (b) => b.type === 'URL' && /\{\{\s*\d+\s*\}\}/.test(b.url || '')
  );
}

/**
 * A static-value text field with a "{}" button that inserts a flow variable
 * token. Gives template body slots and interactive-message slots the same
 * variable-mapping affordance free-text replies have — the author can drop in
 * `{{orderId}}` (resolved at send time by the executor) instead of only a
 * literal. Appends the token; multiple can be combined, or the field edited by
 * hand.
 */
function StaticValueField({
  value,
  onChange,
  variableTokens,
  placeholder,
}: {
  value: string;
  onChange: (next: string) => void;
  variableTokens: string[];
  placeholder?: string;
}) {
  const [anchor, setAnchor] = useState<null | HTMLElement>(null);
  return (
    <Stack direction="row" spacing={0.5} alignItems="center" sx={{ flex: 1, minWidth: 0 }}>
      <TextField
        size="small"
        fullWidth
        placeholder={placeholder ?? 'Text or {{variable}}'}
        value={value}
        onChange={(e) => onChange(e.target.value)}
      />
      {variableTokens.length > 0 && (
        <>
          <Tooltip title="Insert a flow variable">
            <Button
              size="small"
              variant="outlined"
              onClick={(e) => setAnchor(e.currentTarget)}
              sx={{ minWidth: 34, px: 0.5, fontFamily: 'monospace', fontSize: 13, lineHeight: 1, flexShrink: 0 }}
            >
              {'{}'}
            </Button>
          </Tooltip>
          <Menu
            anchorEl={anchor}
            open={!!anchor}
            onClose={() => setAnchor(null)}
            slotProps={{ paper: { sx: { maxHeight: 300 } } }}
          >
            {variableTokens.map((t) => (
              <MenuItem
                key={t}
                dense
                onClick={() => {
                  onChange(`${value ?? ''}{{${t}}}`);
                  setAnchor(null);
                }}
                sx={{ fontFamily: 'monospace', fontSize: 12.5 }}
              >
                {`{{${t}}}`}
              </MenuItem>
            ))}
          </Menu>
        </>
      )}
    </Stack>
  );
}

/**
 * The canned-reply node's template mode: pick an APPROVED template, map each
 * {{n}} body slot to either the customer's name (resolved live per-message)
 * or a fixed static value, and preview the result. Header media (if any) is
 * shown read-only — whatever the synced template defines — there's no
 * per-send override in a bot context.
 */
function CannedReplyTemplateConfig({
  data,
  upd,
  updMany,
  templates,
  variableTokens,
  wfThemeClass,
}: {
  data: any;
  upd: (key: string, value: unknown) => void;
  updMany: (patch: Record<string, unknown>) => void;
  templates: WhatsAppTemplate[];
  variableTokens: string[];
  wfThemeClass: string;
}) {
  const selected = templates.find(
    (t) => t.name === data.templateName && t.language === (data.templateLanguage ?? 'en')
  );
  const bodyParams: Array<{ kind: 'customerName' | 'static'; value?: string }> = Array.isArray(
    data.bodyParams
  )
    ? data.bodyParams
    : [];

  // Signed S3 preview URL(s) for the selected template's header media — the
  // template's own `headerMediaUrl` field is only a fallback for templates
  // that never had a file uploaded; anything uploaded through the editor is
  // private and needs a freshly-signed URL to actually render here (mirrors
  // CampaignForm.tsx's composer preview, which has the same requirement).
  const [headerMediaPreview, setHeaderMediaPreview] = useState<{ previewUrl: string | null } | null>(null);
  const [carouselMediaPreview, setCarouselMediaPreview] = useState<Array<{ previewUrl: string | null }>>([]);
  const [mediaPreviewLoading, setMediaPreviewLoading] = useState(false);

  useEffect(() => {
    setHeaderMediaPreview(null);
    setCarouselMediaPreview([]);
    if (!selected) return;

    const cards = selected.components.carousel?.cards;
    if (cards && cards.length > 0) {
      setMediaPreviewLoading(true);
      adminApi
        .getWhatsAppTemplateCarouselMediaPreview(selected.name, selected.language, { suppressPermissionLock: true })
        .then(setCarouselMediaPreview)
        .catch(() => setCarouselMediaPreview([]))
        .finally(() => setMediaPreviewLoading(false));
      return;
    }

    const header = selected.components.header;
    const isMediaHeader = header && ['IMAGE', 'VIDEO', 'DOCUMENT'].includes(header.type);
    if (!isMediaHeader || !(header.headerMediaS3Key || header.headerMediaId)) return;

    setMediaPreviewLoading(true);
    adminApi
      .getWhatsAppTemplateHeaderMediaPreview(selected.name, selected.language, { suppressPermissionLock: true })
      .then(setHeaderMediaPreview)
      .catch(() => setHeaderMediaPreview(null))
      .finally(() => setMediaPreviewLoading(false));
  }, [selected?.name, selected?.language]);

  const selectTemplate = (t: WhatsAppTemplate) => {
    const slotCount = countTemplateBodyVariables(t.components.body?.text || '');
    // One atomic update — three separate upd() calls here would each read the
    // same stale `data` and stomp each other's change (see updMany's doc comment).
    updMany({
      templateName: t.name,
      templateLanguage: t.language,
      // Fresh param slots sized to the new template — a stale mapping from a
      // previously-selected template (different slot count) would be silently wrong.
      bodyParams: Array.from({ length: slotCount }, (_, i) => bodyParams[i] ?? { kind: 'static', value: '' }),
      // Cleared, not carried over — a value typed for a different template's
      // button link is almost never right for this one, and if this template
      // doesn't have a dynamic URL button at all it would otherwise linger unused.
      buttonUrlValue: undefined,
    });
  };

  const updateParam = (index: number, next: { kind: 'customerName' | 'static'; value?: string }) => {
    const nextParams = [...bodyParams];
    nextParams[index] = next;
    upd('bodyParams', nextParams);
  };

  const previewBody = selected
    ? (selected.components.body?.text || '').replace(/\{\{(\d+)\}\}/g, (_, idxStr) => {
        const i = parseInt(idxStr, 10) - 1;
        const param = bodyParams[i];
        if (!param) return `[${idxStr}]`;
        return param.kind === 'customerName' ? 'Himanshu' : param.value?.trim() || `[${idxStr}]`;
      })
    : '';

  return (
    <>
      <Autocomplete
        size="small"
        fullWidth
        options={templates}
        value={selected ?? null}
        onChange={(_, t) => {
          if (t) selectTemplate(t);
        }}
        getOptionLabel={(t) => `${t.name} (${t.language})`}
        isOptionEqualToValue={(a, b) => a.name === b.name && a.language === b.language}
        // A template whose media can't be sent is not a valid choice for an
        // automated send — disable it (unselectable) and label why, so a flow is
        // never wired to a dead template that would fail silently at runtime.
        getOptionDisabled={(t) => isTemplateMediaUnfit(t)}
        renderOption={(props, t) => {
          const unfit = isTemplateMediaUnfit(t);
          return (
            <li {...props} key={`${t.name}:${t.language}`}>
              <span style={{ flex: 1 }}>
                {t.name} ({t.language})
              </span>
              {unfit && (
                <span
                  style={{
                    fontSize: 10,
                    fontWeight: 600,
                    color: '#f0a84a',
                    border: '1px solid #6b4a1f',
                    borderRadius: 4,
                    padding: '1px 6px',
                    whiteSpace: 'nowrap',
                  }}
                >
                  Media missing
                </span>
              )}
            </li>
          );
        }}
        noOptionsText="No templates match"
        // Portals to document.body, OUTSIDE .wf-page — so it loses the canvas
        // theme's CSS vars. `wfThemeClass` re-applies `wf-theme-vars[--light]` on
        // the portaled paper so the tokens below resolve to the matching palette
        // (white in light, dark in dark) instead of the dark hard-coded fallbacks.
        slotProps={{
          paper: {
            className: wfThemeClass,
            sx: {
              bgcolor: 'var(--wf-surface-2, #161b25)',
              color: 'var(--wf-text, #e8ecf4)',
              border: '1px solid var(--wf-border-strong, #2e3644)',
              '& .MuiAutocomplete-noOptions': { color: 'var(--wf-text-dim, #939eb2)' },
              '& .MuiAutocomplete-option': { fontSize: 13 },
              '& .MuiAutocomplete-option.Mui-focused': { bgcolor: 'var(--wf-surface-3, #1c222e) !important' },
            },
          },
        }}
        renderInput={(params) => (
          <TextField
            {...(params as any)}
            label="Template"
            placeholder="Search templates…"
            helperText={
              templates.length === 0
                ? 'No approved templates yet — create and get one approved on the Templates page first.'
                : 'Only approved templates can be sent.'
            }
          />
        )}
      />

      {selected && bodyParams.length > 0 && (
        <Stack spacing={1.5}>
          <Typography variant="caption" sx={{ color: 'var(--wf-text-dim, #8b949e)', fontWeight: 600 }}>
            Body variables
          </Typography>
          {bodyParams.map((param, i) => (
            <Stack key={i} direction="row" spacing={1} alignItems="center">
              <Typography sx={{ fontSize: 12, color: 'var(--wf-text-dim, #8b949e)', minWidth: 28 }}>{`{{${i + 1}}}`}</Typography>
              <TextField
                select
                size="small"
                value={param.kind}
                onChange={(e) =>
                  updateParam(i, {
                    kind: e.target.value as 'customerName' | 'static',
                    ...(param.value !== undefined ? { value: param.value } : {}),
                  })
                }
                sx={{ minWidth: 150 }}
              >
                <MenuItem value="customerName">Customer's name</MenuItem>
                <MenuItem value="static">Static text</MenuItem>
              </TextField>
              {param.kind === 'static' && (
                <StaticValueField
                  value={param.value ?? ''}
                  onChange={(v) => updateParam(i, { kind: 'static', value: v })}
                  variableTokens={variableTokens}
                  placeholder="Text or {{variable}}"
                />
              )}
            </Stack>
          ))}
          <Typography sx={{ fontSize: 11.5, color: 'var(--wf-text-faint, #5a6478)' }}>
            Tip: use <code>{'{}'}</code> to drop in a flow variable like <code>{'{{orderId}}'}</code> — it's filled in when the message is sent.
          </Typography>
        </Stack>
      )}

      {selected && hasDynamicUrlButton(selected) && (
        <Box>
          <Typography sx={{ fontSize: 12, color: 'var(--wf-text-dim, #8b949e)', mb: 0.5 }}>
            {`"${selected.components.buttons!.find((b) => b.type === 'URL' && /\{\{\s*\d+\s*\}\}/.test(b.url || ''))!.text}" button link`}
          </Typography>
          <StaticValueField
            value={data.buttonUrlValue ?? ''}
            onChange={(v) => upd('buttonUrlValue', v)}
            variableTokens={variableTokens}
            placeholder="e.g. inv-98214 or {{invoiceId}}"
          />
          <Typography sx={{ fontSize: 11.5, color: 'var(--wf-text-faint, #5a6478)', mt: 0.5 }}>
            Required — the value for this button's per-recipient URL slot. A flow variable is filled in at send time.
          </Typography>
        </Box>
      )}

      {selected && (
        <WhatsAppPreviewBubble
          mediaType={selected.components.header?.type !== 'TEXT' ? selected.components.header?.type : undefined}
          mediaUrl={headerMediaPreview?.previewUrl || selected.components.header?.headerMediaUrl || null}
          mediaLoading={mediaPreviewLoading && !selected.components.carousel?.cards?.length}
          bodyText={previewBody}
          footerText={selected.components.footer?.text}
          buttons={selected.components.buttons}
          emptyHint="Select a template to preview it"
          carousel={
            selected.components.carousel?.cards?.length
              ? selected.components.carousel.cards.map((card, i) => ({
                  mediaUrl: carouselMediaPreview[i]?.previewUrl || card.header?.headerMediaUrl || null,
                  mediaType: card.header?.type as 'IMAGE' | 'VIDEO' | undefined,
                  mediaLoading: mediaPreviewLoading,
                  bodyText: card.body?.text || '',
                  buttons: card.buttons,
                }))
              : undefined
          }
        />
      )}
    </>
  );
}

/**
 * `send_list_message` / `send_button_message` node config: pick one saved
 * record from the tenant's interactive-message library and map its `{{n}}`
 * slots (if any) — the same `customerName`/`static` affordance the
 * canned-reply node's template mode already gives an author, via the shared
 * `CannedReplyTemplateParam` shape. The record's rows/buttons and their ids
 * are never edited here; that only happens once, in the library's own
 * editor (WhatsApp → Interactive Messages), so every node referencing a
 * record stays wired to the same stable ids.
 */
/**
 * Every tappable (id, title) a saved interactive message can send back — list
 * rows or reply buttons. The `id` is exactly what WhatsApp returns as
 * `interactive.list_reply.id` / `interactive.button_reply.id` and what a
 * `button_reply_trigger` branch matches on, so this is the single source the
 * trigger picker offers. Options with no minted id (none, in practice — the
 * server mints on save) are skipped so a branch can never point at a blank id.
 */
function interactiveMessageTaps(
  m: InteractiveMessage
): Array<{ id: string; title: string; section?: string }> {
  if (m.kind === 'list') {
    return (m.sections ?? []).flatMap((s) =>
      (s.rows ?? [])
        .filter((r) => !!r.id)
        .map((r) => ({
          id: r.id as string,
          title: r.title,
          ...(s.title ? { section: s.title } : {}),
        }))
    );
  }
  return (m.buttons ?? [])
    .filter((b) => !!b.id)
    .map((b) => ({ id: b.id as string, title: b.title }));
}

/**
 * "When Button Tapped…" (button_reply_trigger) config. Wires the branch to a
 * saved Interactive Message's row/button by its minted id — never a hand-typed
 * id — so an author can't typo an id that silently never matches. Two cascading
 * pickers (message → option) plus a refresh, mirroring the Call-API node's
 * refresh affordance. The evaluator keys off `buttonId` alone; the message/label
 * fields are denormalized for display only.
 */
function ButtonReplyTriggerConfig({
  data,
  updMany,
  interactiveMessages,
  onRefresh,
  refreshing,
}: {
  data: any;
  updMany: (patch: Record<string, unknown>) => void;
  interactiveMessages: InteractiveMessage[];
  onRefresh: () => void;
  refreshing: boolean;
}) {
  // Only messages that actually carry a tappable option can produce an id to
  // match on. 'dynamic'-body messages stay eligible — their body is AI-written
  // but their rows/buttons still have stable ids a scripted branch can match.
  const options = interactiveMessages.filter((m) => interactiveMessageTaps(m).length > 0);
  const selectedMessage = options.find((m) => m._id === data.interactiveMessageId);
  const taps = selectedMessage ? interactiveMessageTaps(selectedMessage) : [];

  // A branch wired to an id no current option carries — its message was edited
  // or deleted after wiring, or the id was hand-typed before this picker existed.
  // Surfaced so the author knows the branch is dead until re-pointed.
  const orphanId =
    data.buttonId && !taps.some((t) => t.id === data.buttonId) ? String(data.buttonId) : '';

  const pickMessage = (m: InteractiveMessage | undefined) => {
    // Preserve the wired id only if the newly-picked message still contains it;
    // otherwise clear it so a stale id from another message never lingers.
    const kept = m && data.buttonId && interactiveMessageTaps(m).find((t) => t.id === data.buttonId);
    updMany({
      interactiveMessageId: m?._id,
      interactiveMessageName: m?.name,
      buttonId: kept ? data.buttonId : '',
      buttonLabel: kept ? kept.title : undefined,
    });
  };

  const pickTap = (id: string) => {
    const t = taps.find((x) => x.id === id);
    updMany({ buttonId: id, buttonLabel: t?.title });
  };

  return (
    <>
      <Stack direction="row" spacing={1} alignItems="flex-start">
        <TextField
          select
          label="Interactive message"
          size="small"
          fullWidth
          value={selectedMessage?._id ?? ''}
          onChange={(e) => pickMessage(options.find((m) => m._id === e.target.value))}
          helperText={
            options.length === 0
              ? 'No saved lists or button messages with options yet — build one under WhatsApp → Interactive Messages, then refresh.'
              : 'The saved message whose tap starts this branch.'
          }
        >
          {options.map((m) => (
            <MenuItem key={m._id} value={m._id}>
              {m.name} {m.kind === 'list' ? '(List)' : '(Buttons)'}
            </MenuItem>
          ))}
        </TextField>
        <Tooltip title="Refresh messages">
          <span>
            <IconButton size="small" onClick={onRefresh} disabled={refreshing} sx={{ mt: 0.5 }}>
              {refreshing ? <CircularProgress size={16} /> : <Refresh fontSize="small" />}
            </IconButton>
          </span>
        </Tooltip>
      </Stack>

      {selectedMessage && (
        <TextField
          select
          label={selectedMessage.kind === 'list' ? 'Which row' : 'Which button'}
          size="small"
          fullWidth
          value={taps.some((t) => t.id === data.buttonId) ? data.buttonId : ''}
          onChange={(e) => pickTap(e.target.value)}
          helperText="The branch runs when the customer taps exactly this option."
        >
          {taps.map((t) => (
            <MenuItem key={t.id} value={t.id}>
              {t.section ? `${t.section} — ${t.title}` : t.title}
            </MenuItem>
          ))}
        </TextField>
      )}

      {orphanId && (
        <Alert severity="warning" sx={{ fontSize: 13 }}>
          This branch is wired to <strong>{orphanId}</strong>, which isn’t in{' '}
          {selectedMessage ? 'the selected message' : 'any saved message'} anymore — so it won’t
          run. Pick the option again to re-point it.
        </Alert>
      )}

      <InfoHint>
        Checked before any “When Customer Says…” keyword — a button tap is exact and always wins over
        a text match. Routes taps on lists/buttons you send; ad and template-button taps arrive as
        plain text, so match those with “When Customer Says…”.
      </InfoHint>
    </>
  );
}

function SendInteractiveMessageConfig({
  kind,
  data,
  upd,
  updMany,
  interactiveMessages,
  mediaAssets,
}: {
  kind: 'list' | 'button';
  data: any;
  upd: (key: string, value: unknown) => void;
  updMany: (patch: Record<string, unknown>) => void;
  interactiveMessages: InteractiveMessage[];
  mediaAssets: MediaAsset[];
}) {
  // 'dynamic'-mode messages are AI-only — their body is written by the model
  // at send time, and a scripted workflow node has no free text to supply
  // (see SendInteractiveMessageNodeData.aiComposedBody on the backend).
  const options = interactiveMessages.filter((m) => m.kind === kind && m.bodyMode !== 'dynamic');
  const selected = options.find((m) => m._id === data.interactiveMessageId);
  const variables: Array<{ kind: 'customerName' | 'static'; value?: string }> = Array.isArray(
    data.variables
  )
    ? data.variables
    : [];

  const selectMessage = (m: InteractiveMessage) => {
    // One atomic update — see updMany's doc comment on why three separate
    // upd() calls here would each stomp the previous one's change.
    updMany({
      interactiveMessageId: m._id,
      // Denormalized purely for the canvas subtitle (see subtitle()'s
      // send_list_message/send_button_message cases) — same reasoning as
      // ai_answer's agentName: the node never has to re-fetch the library
      // just to show what it's pointed at. Not read anywhere on the send path.
      interactiveMessageName: m.name,
      // Fresh slots sized to the newly-picked record — a stale mapping from a
      // previously-selected message (different slot count) would silently
      // send wrong values otherwise.
      variables: Array.from({ length: m.variableCount }, (_, i) => variables[i] ?? { kind: 'static', value: '' }),
      headerMediaAssetId: undefined,
    });
  };

  const updateVariable = (i: number, next: { kind: 'customerName' | 'static'; value?: string }) => {
    const nextVars = [...variables];
    nextVars[i] = next;
    upd('variables', nextVars);
  };

  return (
    <>
      <TextField
        select
        label={kind === 'list' ? 'Saved list' : 'Saved message'}
        size="small"
        fullWidth
        value={data.interactiveMessageId ?? ''}
        onChange={(e) => {
          const picked = options.find((m) => m._id === e.target.value);
          if (picked) selectMessage(picked);
        }}
        helperText={
          options.length === 0
            ? `No saved ${kind === 'list' ? 'lists' : 'button messages'} yet — build one under WhatsApp → Interactive Messages.`
            : undefined
        }
      >
        {options.map((m) => (
          <MenuItem key={m._id} value={m._id}>
            {m.name}
          </MenuItem>
        ))}
      </TextField>

      {selected && variables.length > 0 && (
        <Stack spacing={1.5}>
          <Typography variant="caption" color="text.secondary">
            Values for this message's {'{{n}}'} slots
          </Typography>
          {variables.map((param, i) => (
            <Stack key={i} direction="row" spacing={1} alignItems="center">
              <Typography sx={{ fontSize: 12, color: 'text.secondary', minWidth: 28 }}>{`{{${i + 1}}}`}</Typography>
              <TextField
                select
                size="small"
                value={param.kind}
                onChange={(e) =>
                  updateVariable(i, {
                    kind: e.target.value as 'customerName' | 'static',
                    ...(param.value !== undefined ? { value: param.value } : {}),
                  })
                }
                sx={{ minWidth: 150 }}
              >
                <MenuItem value="customerName">Customer's name</MenuItem>
                <MenuItem value="static">Static text</MenuItem>
              </TextField>
              {param.kind === 'static' && (
                <TextField
                  size="small"
                  fullWidth
                  placeholder="Text to send"
                  value={param.value ?? ''}
                  onChange={(e) => updateVariable(i, { kind: 'static', value: e.target.value })}
                />
              )}
            </Stack>
          ))}
        </Stack>
      )}

      {kind === 'button' && selected?.header && (
        <TextField
          select
          label="Header file (optional override)"
          size="small"
          fullWidth
          value={data.headerMediaAssetId ?? ''}
          onChange={(e) => upd('headerMediaAssetId', e.target.value || undefined)}
          helperText="Same buttons, a different file for this node — leave blank to use the saved message's own file."
        >
          <MenuItem value="">Use the saved file</MenuItem>
          {mediaAssets
            .filter((m) => m.type === selected.header!.type)
            .map((m) => (
              <MenuItem key={m._id} value={m._id}>
                {m.name}
              </MenuItem>
            ))}
        </TextField>
      )}

      {selected && (
        <InfoHint>
          {kind === 'list'
            ? 'Sends this saved list menu.'
            : 'Sends this saved button message.'}{' '}
          Works only inside the 24-hour window after the customer's last message.
        </InfoHint>
      )}
    </>
  );
}

/**
 * Kind-agnostic saved-interactive-message picker — lists BOTH button and list
 * records and stamps `interactiveMessageKind` from the chosen one. Shared by the
 * "Send Response → Interactive" tab (canned_reply, mode:'interactive') and the
 * "Get User Input → Interactive" tab (collect_input). Unlike
 * SendInteractiveMessageConfig it is not locked to one kind and omits the button
 * media-header override, since both callers only need: which message + its
 * {{n}} slot values.
 */
function UnifiedInteractivePicker({
  data,
  upd,
  updMany,
  interactiveMessages,
  variableTokens,
}: {
  data: any;
  upd: (key: string, value: unknown) => void;
  updMany: (patch: Record<string, unknown>) => void;
  interactiveMessages: InteractiveMessage[];
  variableTokens: string[];
}) {
  // Show EVERY saved message, both variable- and dynamic-body. A dynamic record
  // (AI-composed body) is still usable from a workflow node — the author just
  // types the body themselves (stored as `interactiveBody`, sent as the message
  // body). Excluding them left tenants whose whole library is AI-dynamic with an
  // empty picker.
  const options = interactiveMessages;
  const selected = options.find((m) => m._id === data.interactiveMessageId);
  const isDynamic = selected?.bodyMode === 'dynamic';
  const variables: Array<{ kind: 'customerName' | 'static'; value?: string }> = Array.isArray(data.variables)
    ? data.variables
    : [];

  const selectMessage = (m: InteractiveMessage) => {
    updMany({
      interactiveMessageId: m._id,
      interactiveMessageKind: m.kind, // lets the executor skip a library read
      interactiveBodyMode: m.bodyMode, // drives the body field + validation
      interactiveMessageName: m.name, // subtitle only
      // Dynamic records need an author-written body; variable records don't.
      interactiveBody: m.bodyMode === 'dynamic' ? (data.interactiveBody ?? '') : undefined,
      variables: Array.from({ length: m.variableCount }, (_, i) => variables[i] ?? { kind: 'static', value: '' }),
    });
  };
  const updateVariable = (i: number, next: { kind: 'customerName' | 'static'; value?: string }) => {
    const nextVars = [...variables];
    nextVars[i] = next;
    upd('variables', nextVars);
  };

  return (
    <>
      <TextField
        select
        label="WhatsApp interactive message"
        size="small"
        fullWidth
        value={data.interactiveMessageId ?? ''}
        onChange={(e) => {
          const picked = options.find((m) => m._id === e.target.value);
          if (picked) selectMessage(picked);
        }}
        helperText={
          options.length === 0
            ? 'No saved button/list messages yet — build one under WhatsApp → Interactive Messages.'
            : selected
              ? `${selected.kind === 'list' ? 'List menu' : 'Buttons'}${isDynamic ? ' · AI-dynamic — add the body below' : selected.variableCount ? ` · ${selected.variableCount} variable slot(s)` : ''}`
              : 'Pick a saved button or list message.'
        }
      >
        {options.map((m) => (
          <MenuItem key={m._id} value={m._id}>
            {m.name} · {m.kind === 'list' ? 'List' : 'Buttons'}
            {m.bodyMode === 'dynamic' ? ' · AI' : ''}
          </MenuItem>
        ))}
      </TextField>

      {selected && isDynamic && (
        <TextField
          label="Message text"
          size="small"
          fullWidth
          multiline
          rows={3}
          value={data.interactiveBody ?? ''}
          onChange={(e) => upd('interactiveBody', e.target.value)}
          placeholder="e.g. Hi {{system.customerName}}, which option would you like?"
          helperText="This message has an AI-composed body, so type what to send here. You can use {{variables}}. The buttons/rows come from the saved message."
        />
      )}

      {selected && variables.length > 0 && (
        <Stack spacing={1.5}>
          <Typography variant="caption" color="text.secondary">
            Values for this message's {'{{n}}'} slots
          </Typography>
          {variables.map((param, i) => (
            <Stack key={i} direction="row" spacing={1} alignItems="center">
              <Typography sx={{ fontSize: 12, color: 'text.secondary', minWidth: 28 }}>{`{{${i + 1}}}`}</Typography>
              <TextField
                select
                size="small"
                value={param.kind}
                onChange={(e) =>
                  updateVariable(i, {
                    kind: e.target.value as 'customerName' | 'static',
                    ...(param.value !== undefined ? { value: param.value } : {}),
                  })
                }
                sx={{ minWidth: 150 }}
              >
                <MenuItem value="customerName">Customer's name</MenuItem>
                <MenuItem value="static">Static text</MenuItem>
              </TextField>
              {param.kind === 'static' && (
                <StaticValueField
                  value={param.value ?? ''}
                  onChange={(v) => updateVariable(i, { kind: 'static', value: v })}
                  variableTokens={variableTokens}
                  placeholder="Text or {{variable}}"
                />
              )}
            </Stack>
          ))}
        </Stack>
      )}
    </>
  );
}

// ─── Trigger ternary-filter toggle ─────────────────────────────────────────────
//
// Shared by every "one filter row, three friendly states" control — lead.created's
// "Lead type" (isReEnquiry) and appointment.created's "Date & time" (requestedAt
// confirmed/not) both boil down to the same shape: a single data.filters row that's
// either absent (neutral/"all"), or set one specific way for each of two opposite
// states. A raw operator+value row reads far worse for these than a 3-way toggle, so
// this is the friendly face on top — same underlying array TriggerFilterRowEditor and
// matchesTriggerFilters both read, never a parallel mechanism.
interface TernaryToggleOption {
  /** Internal state key ('all' | 'new' | 'repeat', etc.) — 'all' by convention is the
   *  neutral state that clears the row entirely. */
  value: string;
  label: string;
  description: string;
}

function TriggerTernaryFilterToggle({
  data,
  upd,
  field,
  title,
  options,
  toOptionValue,
  toFilterRow,
}: {
  data: any;
  upd: (key: string, value: unknown) => void;
  /** The data.filters field name this toggle owns (e.g. 'isReEnquiry', 'requestedAt'). */
  field: string;
  title: string;
  options: TernaryToggleOption[];
  /** Reads the current filter row for `field` (undefined if none) into one of `options`. */
  toOptionValue: (row: { field: string; operator: string; value: unknown } | undefined) => string;
  /** Writes a chosen option value back into a filter row, or null to clear it (the
   *  neutral/"all" choice). */
  toFilterRow: (optionValue: string) => { field: string; operator: string; value: unknown } | null;
}) {
  const filters = Array.isArray(data.filters) ? data.filters : [];
  const ownRow = filters.find((f: any) => f?.field === field);
  const current = toOptionValue(ownRow);
  const setValue = (next: string | null) => {
    if (!next) return;
    const otherFilters = filters.filter((f: any) => f?.field !== field);
    const row = toFilterRow(next);
    upd('filters', row ? [...otherFilters, row] : otherFilters);
  };
  const selected = options.find((o) => o.value === current);
  return (
    <Box>
      <Typography sx={{ fontSize: 11.5, color: 'var(--wf-text-dim, #939eb2)', mb: 0.5 }}>{title}</Typography>
      <ToggleButtonGroup exclusive size="small" value={current} onChange={(_, next) => setValue(next)} fullWidth>
        {options.map((o) => (
          <ToggleButton key={o.value} value={o.value} sx={{ textTransform: 'none', fontSize: 12.5 }}>
            {o.label}
          </ToggleButton>
        ))}
      </ToggleButtonGroup>
      <Typography sx={{ fontSize: 10.5, color: 'var(--wf-text-faint, #5a6478)', mt: 0.5 }}>{selected?.description}</Typography>
    </Box>
  );
}

// ─── Trigger filter-row editor ─────────────────────────────────────────────────
//
// Shared by every async CRM trigger's filter panel (lead.created, appointment.created —
// parameterized rather than duplicated per trigger type). Field/operator/value rows in
// data.filters, exactly the shape matchesTriggerFilters (trigger-filter.util.ts) already
// reads regardless of trigger type. `excludeFields` lets a sibling control (e.g.
// lead.created's "Lead type" isReEnquiry toggle) own specific filter rows without this
// editor showing or clobbering them — both read/write the same data.filters array, so each
// must preserve what it doesn't own.
interface FilterFieldOption {
  value: string;
  label: string;
}

function TriggerFilterRowEditor({
  data,
  upd,
  fieldOptions,
  statusOptions,
  sourceOptions,
  assignedToOptions,
  excludeFields,
}: {
  data: any;
  upd: (key: string, value: unknown) => void;
  fieldOptions: FilterFieldOption[];
  statusOptions: string[];
  sourceOptions: FilterFieldOption[];
  assignedToOptions: Array<{ _id: string; name: string }>;
  excludeFields?: string[];
}) {
  const filters = Array.isArray(data.filters) ? data.filters : [];
  const excluded = excludeFields ?? [];
  const preserved = filters.filter((f: any) => excluded.includes(f?.field));
  const genericFilters: Array<{ field: string; operator: string; value: unknown }> = filters.filter(
    (f: any) => !excluded.includes(f?.field)
  );
  const setGenericFilters = (next: typeof genericFilters) => {
    upd('filters', [...preserved, ...next]);
  };
  const updateRow = (
    idx: number,
    patch: Partial<{ field: string; operator: string; value: unknown }>
  ) => setGenericFilters(genericFilters.map((f, i) => (i === idx ? { ...f, ...patch } : f)));
  const removeRow = (idx: number) => setGenericFilters(genericFilters.filter((_, i) => i !== idx));
  const addRow = () =>
    setGenericFilters([...genericFilters, { field: fieldOptions[0]?.value ?? 'status', operator: 'eq', value: '' }]);

  return (
    <Box>
      <Typography sx={{ fontSize: 11.5, color: 'var(--wf-text-dim, #939eb2)', mb: 0.5 }}>
        Filters {genericFilters.length > 1 ? '— all must match' : ''}
      </Typography>
      {genericFilters.map((row, idx) => {
        const isCustomAttr = typeof row.field === 'string' && row.field.startsWith('customAttributes.');
        const fieldSelectValue = isCustomAttr ? 'customAttributes' : row.field;
        const customKey = isCustomAttr ? row.field.slice('customAttributes.'.length) : '';
        return (
          <Box key={idx} sx={{ border: '1px solid var(--wf-border, #222834)', borderRadius: '8px', p: 1, mb: 1 }}>
            <Stack direction="row" spacing={1} alignItems="center">
              <TextField
                select
                size="small"
                label="Field"
                value={fieldSelectValue || ''}
                onChange={(e) => {
                  const next = e.target.value;
                  updateRow(idx, {
                    field: next === 'customAttributes' ? 'customAttributes.' : next,
                    value: '',
                  });
                }}
                sx={{ flex: 1 }}
              >
                {fieldOptions.map((f) => (
                  <MenuItem key={f.value} value={f.value}>
                    {f.label}
                  </MenuItem>
                ))}
              </TextField>
              <IconButton size="small" onClick={() => removeRow(idx)}>
                <Delete fontSize="inherit" sx={{ color: '#ef4444' }} />
              </IconButton>
            </Stack>
            {isCustomAttr && (
              <TextField
                size="small"
                fullWidth
                label="Attribute key"
                placeholder="e.g. budget_range"
                value={customKey}
                onChange={(e) => updateRow(idx, { field: `customAttributes.${e.target.value}` })}
                sx={{ mt: 1 }}
              />
            )}
            <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
              <TextField
                select
                size="small"
                label="Operator"
                value={row.operator ?? 'eq'}
                onChange={(e) => updateRow(idx, { operator: e.target.value })}
                sx={{ flex: 1 }}
              >
                {OPERATORS.map((o) => (
                  <MenuItem key={o.value} value={o.value}>
                    {o.label}
                  </MenuItem>
                ))}
              </TextField>
              {fieldSelectValue === 'priority' || fieldSelectValue === 'previousPriority' ? (
                <TextField
                  select
                  size="small"
                  label="Value"
                  value={row.value ?? ''}
                  onChange={(e) => updateRow(idx, { value: e.target.value })}
                  sx={{ flex: 1 }}
                >
                  {PRIORITY_BAND_OPTIONS.map((p) => (
                    <MenuItem key={p} value={p}>
                      {p}
                    </MenuItem>
                  ))}
                </TextField>
              ) : fieldSelectValue === 'status' ? (
                <TextField
                  select
                  size="small"
                  label="Value"
                  value={row.value ?? ''}
                  onChange={(e) => updateRow(idx, { value: e.target.value })}
                  sx={{ flex: 1 }}
                >
                  {statusOptions.map((s) => (
                    <MenuItem key={s} value={s}>
                      {s}
                    </MenuItem>
                  ))}
                </TextField>
              ) : fieldSelectValue === 'source' ? (
                <TextField
                  select
                  size="small"
                  label="Value"
                  value={row.value ?? ''}
                  onChange={(e) => updateRow(idx, { value: e.target.value })}
                  sx={{ flex: 1 }}
                >
                  {sourceOptions.map((s) => (
                    <MenuItem key={s.value} value={s.value}>
                      {s.label}
                    </MenuItem>
                  ))}
                </TextField>
              ) : fieldSelectValue === 'assignedTo' ? (
                <TextField
                  select
                  size="small"
                  label="Value"
                  value={row.value ?? ''}
                  onChange={(e) => updateRow(idx, { value: e.target.value })}
                  sx={{ flex: 1 }}
                  helperText={assignedToOptions.length === 0 ? 'No team members found' : undefined}
                >
                  {assignedToOptions.map((a) => (
                    <MenuItem key={a._id} value={a._id}>
                      {a.name}
                    </MenuItem>
                  ))}
                </TextField>
              ) : fieldSelectValue === 'estimatedValue' || fieldSelectValue === 'score' ? (
                <TextField
                  type="number"
                  size="small"
                  label="Value"
                  value={row.value ?? ''}
                  onChange={(e) =>
                    updateRow(idx, { value: e.target.value === '' ? '' : Number(e.target.value) })
                  }
                  sx={{ flex: 1 }}
                />
              ) : (
                <TextField
                  size="small"
                  label="Value"
                  value={row.value ?? ''}
                  onChange={(e) => updateRow(idx, { value: e.target.value })}
                  sx={{ flex: 1 }}
                />
              )}
            </Stack>
          </Box>
        );
      })}
      <Button size="small" startIcon={<Add />} onClick={addRow} sx={{ color: 'var(--wf-text-dim, #8b949e)', alignSelf: 'flex-start' }}>
        Add filter
      </Button>
    </Box>
  );
}

// ─── Config panel ─────────────────────────────────────────────────────────────

/** A saved API Connection as the "Call API" node picker needs it. */
interface ApiConnectionOption {
  id: string;
  name: string;
  baseUrl: string;
  status: 'active' | 'disabled';
  /** The saved request + mappings — shown read-only on the node. */
  request?: ApiRequestDef;
}

interface ConfigPanelProps {
  node: Node;
  onUpdate: (data: Record<string, any>) => void;
  onDelete: () => void;
  onClose: () => void;
  campaigns: Array<{ _id: string; name: string }>;
  agents: Array<{ _id: string; name: string }>;
  /** AI agents (bot personas) — distinct from `agents` above, which despite
   *  its name is the team-member list "Assign Agent" writes to. Only used by
   *  the Trigger panel's agent-picker for message.received workflows. */
  botAgents: Array<{ _id: string; name: string }>;
  /** For the human_handoff node's own assignment-target override. */
  roles: Array<{ _id: string; name: string }>;
  /** APPROVED-only templates, for the canned-reply node's template mode. */
  templates: WhatsAppTemplate[];
  /** Sendable-media library, for the send_media node's file picker. */
  mediaAssets: MediaAsset[];
  /** Saved interactive-message library, for the Send List / Send Button
   *  Message node pickers and the button-reply trigger picker. */
  interactiveMessages: InteractiveMessage[];
  /** Re-fetch the interactive-message library (after saving one in another tab),
   *  with its in-flight flag — same pattern as the API-connection refresh. */
  onRefreshInteractiveMessages: () => void;
  interactiveMessagesLoading: boolean;
  workflows: Array<{ _id: string; name: string; triggerType?: string }>;
  /** Saved API Connections, for the "Call API" node's connection picker. */
  apiConnections: ApiConnectionOption[];
  apiConnectionsLoading: boolean;
  /** Re-fetch the connection list (after creating one in another tab). */
  onRefreshApiConnections: () => void;
  /** Workspace flow-variable registry, for the "Get User Input" name picker. */
  flowVariables: FieldDefinition[];
  flowVariablesLoading: boolean;
  /** Re-fetch the registry (after creating a variable in another tab). */
  onRefreshFlowVariables: () => void;
  /** Variables a reply can use, gathered from this canvas ("Get User Input" answers
   *  and "Call API" mappings) — feeds the reply editor's Insert-variable chips. */
  availableVariables: Array<{ token: string; hint: string }>;
  /** Of availableVariables, the tokens produced by steps that run BEFORE the
   *  selected node (walked backward along the edges). The reply editor surfaces
   *  these first so the relevant few aren't lost among every canvas variable. */
  priorityTokens: Set<string>;
  /** True when this workflow's Trigger Event is "When a Customer Messages" —
   *  switches Condition's field list from CRM lead fields to routing state,
   *  and requires an agent to be chosen on the Trigger node. */
  isConversationFlow: boolean;
  /** 'new' or absent for an unsaved workflow — excluded from the Trigger
   *  Workflow target list so a flow can't be offered as its own bridge target. */
  currentWorkflowId?: string;
  /** Tenant-wide escalation config, surfaced read-only on the Trigger panel so
   *  the author sees the SAME auto-escalation keywords the webhook uses rather
   *  than maintaining a second, invisible list. Null until loaded / on failure. */
  routingSettings: RoutingSettingsResponse | null;
  /** Count of this flow's own keyword_trigger + button_reply_trigger nodes,
   *  for the Trigger panel's routing-precedence ladder (Part C). */
  ownTriggerCount: number;
  /** Canvas light/dark theme — so portaled popups (e.g. the template picker's
   *  dropdown, which renders outside .wf-page and thus loses its CSS vars) can
   *  opt into the matching `wf-theme-vars[--light]` tokens. */
  wfTheme: 'light' | 'dark';
}

function ConfigPanel({
  node,
  onUpdate,
  onDelete,
  onClose,
  campaigns,
  agents,
  botAgents,
  roles,
  templates,
  mediaAssets,
  interactiveMessages,
  onRefreshInteractiveMessages,
  interactiveMessagesLoading,
  workflows,
  apiConnections,
  apiConnectionsLoading,
  onRefreshApiConnections,
  flowVariables,
  flowVariablesLoading,
  onRefreshFlowVariables,
  availableVariables,
  priorityTokens,
  isConversationFlow,
  currentWorkflowId,
  routingSettings,
  ownTriggerCount,
  wfTheme,
}: ConfigPanelProps) {
  const type = (node.type ?? 'trigger') as NodeTypeName;
  const data = node.data as any;
  const meta = NODE_META[type];

  // The reply editor, so "Insert a variable" drops the token at the caret (not at
  // the end). Keyed per node via the ConfigPanel remount, so it never leaks.
  const replyRef = useRef<HTMLTextAreaElement | null>(null);

  // human_handoff's assignment-target toggle, local: picking "A specific role"
  // must reveal the role picker immediately, before anything is saved onto the
  // node (an empty roleIds is meaningless and isn't written) — driving the
  // toggle off `data.assignment` alone would otherwise snap it straight back
  // to "Any team member" the instant it's clicked. `node.id` keys this
  // panel's remount (see the ConfigPanel call site), so this naturally resets
  // per node rather than leaking a draft between different nodes.
  const [assignmentKindDraft, setAssignmentKindDraft] = useState<'anyone' | 'role'>(
    (data.assignment as RoutingTarget | undefined)?.kind === 'role' ? 'role' : 'anyone'
  );

  // Send Response variable insert-list: split into variables produced UPSTREAM of
  // this node (shown first) and the rest (collapsed), merging in the saved registry
  // so a reusable variable is insertable everywhere. See #2.
  const [showOtherVars, setShowOtherVars] = useState(false);
  const varGroups = useMemo(() => {
    const seen = new Set(availableVariables.map((v) => v.token));
    const merged = [
      ...availableVariables,
      ...flowVariables
        .filter((f) => f.key && !seen.has(f.key))
        .map((f) => ({ token: f.key, hint: 'Saved variable' })),
    ];
    return {
      inPath: merged.filter((v) => priorityTokens.has(v.token)),
      others: merged.filter((v) => !priorityTokens.has(v.token)),
    };
  }, [availableVariables, flowVariables, priorityTokens]);

  // Flat token list for the "{}" inserter on template / interactive slot fields —
  // in-path variables first, so the most relevant ones top the menu.
  const variableTokens = useMemo(
    () => [...varGroups.inPath, ...varGroups.others].map((v) => v.token),
    [varGroups]
  );
  // Class for portaled popups so they pick up the canvas theme's CSS vars.
  const wfThemeClass = `wf-theme-vars${wfTheme === 'light' ? ' wf-theme-vars--light' : ''}`;

  // #1 parity with the API editor: register a new reusable variable into the shared
  // flow-variable registry (so it autocompletes here, in Get User Input, and in APIs).
  const [newVarName, setNewVarName] = useState('');
  const [savingVar, setSavingVar] = useState(false);
  const newVarValid = /^[A-Za-z_][A-Za-z0-9_]{0,59}$/.test(newVarName.trim());
  const saveNewVar = useCallback(async () => {
    const name = newVarName.trim();
    if (!newVarValid || savingVar) return;
    setSavingVar(true);
    try {
      await adminApi.createFlowVariable({ key: name });
    } catch (e: any) {
      // 409 just means it already exists — treat as success and refresh.
      if (e?.response?.status !== 409) {
        setSavingVar(false);
        return;
      }
    }
    await onRefreshFlowVariables();
    setNewVarName('');
    setSavingVar(false);
  }, [newVarName, newVarValid, savingVar, onRefreshFlowVariables]);

  const upd = useCallback(
    (key: string, value: unknown) => {
      const next: any = { ...data, [key]: value };
      next['subtitle'] = subtitle(type, next);
      onUpdate(next);
    },
    [data, type, onUpdate]
  );

  // Apply several field changes as ONE update — `upd` reads `data` from this
  // render's closure, so calling it multiple times in a row (e.g. selecting a
  // template sets templateName + templateLanguage + bodyParams together) has
  // each call overwrite the previous one's change: node.data only actually
  // changes once React re-renders with the new prop, so all those calls see
  // the same stale `data` and only the LAST one's field survives. This merges
  // every change into a single object and updates once.
  const updMany = useCallback(
    (patch: Record<string, unknown>) => {
      const next: any = { ...data, ...patch };
      next['subtitle'] = subtitle(type, next);
      onUpdate(next);
    },
    [data, type, onUpdate]
  );

  // Insert a {{token}} at the reply editor's caret and restore the caret after it.
  const insertReplyVar = (token: string) => {
    const { value, caret } = insertAtCaret(replyRef.current, data.replyText ?? '', token);
    upd('replyText', value);
    requestAnimationFrame(() => {
      const el = replyRef.current;
      if (!el) return;
      el.focus();
      try {
        el.setSelectionRange(caret, caret);
      } catch {
        /* ignore */
      }
    });
  };

  // ── KV helper for update_lead — use local state so empty rows are visible ─
  const [kvPairs, setKvPairsLocal] = useState<Array<{ k: string; v: string }>>(() => {
    // Support both canonical { updates: [{field,value}] } and legacy { updateFields: {k:v} }
    if (Array.isArray(data['updates'])) {
      return (data['updates'] as Array<{ field: string; value: unknown }>).map((u) => ({
        k: u.field,
        v: String(u.value ?? ''),
      }));
    }
    return Object.entries(data['updateFields'] ?? {}).map(([k, v]) => ({ k, v: String(v) }));
  });

  const setKvPairs = (pairs: Array<{ k: string; v: string }>) => {
    setKvPairsLocal(pairs);
    // Always save in canonical array format { updates: [{field, value}] }
    const updates = pairs.filter((p) => p.k).map((p) => ({ field: p.k, value: p.v }));
    upd('updates', updates);
  };

  return (
    <>
      <div className="wf-config__header">
        <span className={`wf-config__tile wf-cat--${categoryOf(type).cat}`}>
          {(() => {
            const HeaderIcon = PALETTE_ITEMS.find((p) => p.type === type)?.Icon ?? Bolt;
            return <HeaderIcon style={{ fontSize: 20 }} />;
          })()}
        </span>
        <div className="wf-config__heading">
          <div className="wf-config__title">{data.label || meta.label}</div>
          <div className="wf-config__sub">Configure this step</div>
        </div>
        <IconButton size="small" onClick={onClose} aria-label="Close" sx={{ color: 'var(--wf-text-dim, #8b949e)' }}>
          <Close fontSize="small" />
        </IconButton>
      </div>

      <div className="wf-config__body">
        <Stack spacing={2}>
          {/* ── Label ──────────────────────────────────────────────────── */}
          <TextField
            label="Label"
            size="small"
            fullWidth
            value={data.label ?? ''}
            onChange={(e) => upd('label', e.target.value)}
          />

          <Divider />

          {/* ── Trigger ────────────────────────────────────────────────── */}
          {type === 'trigger' && (
            <>
              {(() => {
                // A workflow saved on a trigger no longer in TRIGGER_TYPES
                // (e.g. lead.updated) must still show its current value rather
                // than render blank — append a labelled item for it so the
                // Select has a match, without polluting the list for new flows.
                const currentTrigger = (data.triggerType as string) ?? 'lead.created';
                const options = TRIGGER_TYPES.some((t) => t.value === currentTrigger)
                  ? TRIGGER_TYPES
                  : [
                      ...TRIGGER_TYPES,
                      { value: currentTrigger, label: TRIGGER_LABEL[currentTrigger] ?? currentTrigger },
                    ];
                return (
                  <TextField
                    select
                    label="Trigger Event"
                    size="small"
                    fullWidth
                    value={currentTrigger}
                    onChange={(e) => upd('triggerType', e.target.value)}
                    helperText="The event that starts this workflow"
                  >
                    {options.map((t) => (
                      <MenuItem key={t.value} value={t.value}>
                        {t.label}
                      </MenuItem>
                    ))}
                  </TextField>
                );
              })()}
              {data.triggerType === 'lead.created' && (
                <TriggerTernaryFilterToggle
                  data={data}
                  upd={upd}
                  field="isReEnquiry"
                  title="Lead type"
                  options={[
                    {
                      value: 'all',
                      label: 'All leads',
                      description: 'Fires for both brand-new leads and repeat submissions from an existing lead.',
                    },
                    {
                      value: 'new',
                      label: 'New only',
                      description:
                        'Skips leads that already existed under this phone/email — only fires the very first time a customer is seen.',
                    },
                    {
                      value: 'repeat',
                      label: 'Repeat enquiry only',
                      description: 'Only fires when a customer who already exists submits again (e.g. re-fills a form).',
                    },
                  ]}
                  toOptionValue={(row) =>
                    row?.operator === 'eq' && row?.value === true
                      ? 'repeat'
                      : row?.operator === 'neq' && row?.value === true
                      ? 'new'
                      : 'all'
                  }
                  toFilterRow={(v) =>
                    v === 'new'
                      ? { field: 'isReEnquiry', operator: 'neq', value: true }
                      : v === 'repeat'
                      ? { field: 'isReEnquiry', operator: 'eq', value: true }
                      : null
                  }
                />
              )}
              {data.triggerType === 'lead.created' && (
                <TriggerFilterRowEditor
                  data={data}
                  upd={upd}
                  fieldOptions={TRIGGER_FILTER_FIELD_OPTIONS}
                  statusOptions={LEAD_STATUS_OPTIONS}
                  sourceOptions={LEAD_SOURCE_OPTIONS}
                  assignedToOptions={agents}
                  excludeFields={['isReEnquiry']}
                />
              )}
              {data.triggerType === 'lead.priority_changed' && (
                <TriggerFilterRowEditor
                  data={data}
                  upd={upd}
                  fieldOptions={PRIORITY_TRIGGER_FILTER_FIELD_OPTIONS}
                  statusOptions={LEAD_STATUS_OPTIONS}
                  sourceOptions={LEAD_SOURCE_OPTIONS}
                  assignedToOptions={agents}
                />
              )}
              {data.triggerType === 'appointment.created' && (
                <TriggerTernaryFilterToggle
                  data={data}
                  upd={upd}
                  field="requestedAt"
                  title="Date & time"
                  options={[
                    {
                      value: 'all',
                      label: 'All',
                      description: 'Fires regardless of whether a specific date/time was captured yet.',
                    },
                    {
                      value: 'confirmed',
                      label: 'Confirmed only',
                      description:
                        'Only fires once a specific date/time was resolved — skips a booking that only has the customer\'s raw phrase ("jaldi", "tomorrow morning") with nothing parsed yet.',
                    },
                    {
                      value: 'unconfirmed',
                      label: 'Not yet confirmed',
                      description:
                        'Only fires while no specific date/time has been resolved — useful for nudging someone to pin down a real slot.',
                    },
                  ]}
                  toOptionValue={(row) =>
                    row?.operator === 'is_not_empty' ? 'confirmed' : row?.operator === 'is_empty' ? 'unconfirmed' : 'all'
                  }
                  toFilterRow={(v) =>
                    v === 'confirmed'
                      ? { field: 'requestedAt', operator: 'is_not_empty', value: undefined }
                      : v === 'unconfirmed'
                      ? { field: 'requestedAt', operator: 'is_empty', value: undefined }
                      : null
                  }
                />
              )}
              {data.triggerType === 'appointment.created' && (
                <TriggerFilterRowEditor
                  data={data}
                  upd={upd}
                  fieldOptions={APPOINTMENT_FILTER_FIELD_OPTIONS}
                  statusOptions={APPOINTMENT_STATUS_OPTIONS}
                  sourceOptions={APPOINTMENT_SOURCE_OPTIONS}
                  assignedToOptions={agents}
                  excludeFields={['requestedAt']}
                />
              )}
              {data.triggerType === 'message.received' && (
                <TextField
                  select
                  label="Preferred agent (optional)"
                  size="small"
                  fullWidth
                  value={data.agentId ?? ''}
                  onChange={(e) => upd('agentId', e.target.value)}
                  helperText={
                    botAgents.length === 0
                      ? 'No agents found — create one first'
                      : 'Optional. Leave blank to let the system pick the best agent per message (same as the default). Pin one only to force a specific persona.'
                  }
                >
                  <MenuItem value="">
                    <em>Auto · best agent per message</em>
                  </MenuItem>
                  {agentOptionsFor(botAgents, data.agentId, data.agentName).map((a) => (
                    <MenuItem key={a._id} value={a._id}>
                      {a.name}
                    </MenuItem>
                  ))}
                </TextField>
              )}
              {data.triggerType === 'message.received' && routingSettings && (
                <InfoHint wide>
                  <Typography sx={{ fontSize: 10.5, color: 'rgba(255,255,255,0.62)', fontWeight: 700, letterSpacing: '0.06em', mb: 1 }}>
                    MESSAGE ROUTING ORDER
                  </Typography>

                  {/* Step 1 — tenant-global, checked before ANY flow, including this one. */}
                  <Stack direction="row" spacing={1} sx={{ mb: 0.75 }}>
                    <Typography sx={{ fontSize: 11, color: 'rgba(255,255,255,0.62)', fontWeight: 700, minWidth: 14 }}>1</Typography>
                    <Typography sx={{ fontSize: 11.5, color: 'rgba(255,255,255,0.9)', lineHeight: 1.5 }}>
                      {routingSettings.escalation.enabled ? (
                        <>
                          Your global break-keywords —{' '}
                          {(() => {
                            const kw = routingSettings.escalation.keywords ?? [];
                            const builtin =
                              routingSettings.escalation.mode === 'replace'
                                ? []
                                : routingSettings.builtinExamples ?? [];
                            const all = [...kw, ...builtin];
                            const shown = all.slice(0, 5);
                            return shown.length ? (
                              <>
                                <em style={{ color: '#c4bcff', fontStyle: 'normal' }}>{shown.join(', ')}</em>
                                {all.length > shown.length ? `, +${all.length - shown.length} more` : ''}
                              </>
                            ) : (
                              'your configured phrases'
                            );
                          })()}
                          {' '}(Settings → Human Handoff). Always checked first, in every flow.
                        </>
                      ) : (
                        'Global break-keywords are currently turned off in Settings → Human Handoff.'
                      )}
                    </Typography>
                  </Stack>

                  {/* Step 2 — THIS flow's own triggers. */}
                  <Stack direction="row" spacing={1} sx={{ mb: 0.75 }}>
                    <Typography sx={{ fontSize: 11, color: 'rgba(255,255,255,0.62)', fontWeight: 700, minWidth: 14 }}>2</Typography>
                    <Typography sx={{ fontSize: 11.5, color: 'rgba(255,255,255,0.9)', lineHeight: 1.5 }}>
                      {ownTriggerCount > 0 ? (
                        <>
                          This flow's own {ownTriggerCount} trigger{ownTriggerCount === 1 ? '' : 's'} below — a
                          button tap always wins over a keyword match.
                        </>
                      ) : (
                        'This flow has no keyword or button triggers yet — add one below.'
                      )}
                    </Typography>
                  </Stack>

                  {/* Step 3 — the always-on floor. */}
                  <Stack direction="row" spacing={1}>
                    <Typography sx={{ fontSize: 11, color: 'rgba(255,255,255,0.62)', fontWeight: 700, minWidth: 14 }}>3</Typography>
                    <Typography sx={{ fontSize: 11.5, color: 'rgba(255,255,255,0.9)', lineHeight: 1.5 }}>
                      Default AI — picks the best agent per message, and escalates on repeated low
                      confidence. Runs when nothing above matched.
                    </Typography>
                  </Stack>
                </InfoHint>
              )}
            </>
          )}

          {/* ── Condition ──────────────────────────────────────────────── */}
          {type === 'condition' && isConversationFlow && (
            <>
              <TextField
                select
                label="Condition type"
                size="small"
                fullWidth
                value={data.mode === 'match' ? 'match' : 'business_hours'}
                onChange={(e) => {
                  if (e.target.value === 'match') upd('mode', 'match');
                  else {
                    upd('mode', 'business_hours');
                    upd('field', 'business_hours');
                  }
                }}
                helperText="Branch on business hours, or on a variable an earlier step set (e.g. an API result)."
                sx={{ mb: 0.5 }}
              >
                <MenuItem value="business_hours">Business hours</MenuItem>
                <MenuItem value="match">Match a value</MenuItem>
              </TextField>
              {(data.mode ?? 'business_hours') === 'match' ? (
                <>
                  <InfoHint>
                    <strong>Yes</strong> when the variable matches, <strong>No</strong> when it
                    doesn’t. Use something an earlier step set — e.g. <code>{'{{_httpStatus}}'}</code>{' '}
                    (200, 404…) or a mapped field like <code>{'{{gender}}'}</code>. Text matching
                    ignores case and spacing, so <code>true</code> also matches <code>True</code>/<code>YES </code>;
                    use <strong>is any of</strong> to catch several at once.
                  </InfoHint>
                  <TextField
                    label="Variable"
                    size="small"
                    fullWidth
                    value={data.variable ?? ''}
                    onChange={(e) => upd('variable', e.target.value.replace(/^\{\{|\}\}$/g, '').trim())}
                    placeholder="_httpStatus"
                    helperText="The name inside {{…}}, without the braces."
                  />
                  <Stack direction="row" spacing={1}>
                    <TextField
                      select
                      label="Is"
                      size="small"
                      value={data.operator ?? 'eq'}
                      onChange={(e) => upd('operator', e.target.value)}
                      sx={{ minWidth: 150 }}
                    >
                      {MATCH_OPERATORS.map((o) => (
                        <MenuItem key={o.value} value={o.value}>
                          {o.label}
                        </MenuItem>
                      ))}
                    </TextField>
                    {(MATCH_OPERATORS.find((o) => o.value === (data.operator ?? 'eq'))?.needsValue ?? true) && (
                      <TextField
                        label={data.operator === 'in' ? 'Any of (comma-separated)' : 'Value'}
                        size="small"
                        fullWidth
                        value={data.value ?? ''}
                        onChange={(e) => upd('value', e.target.value)}
                        placeholder={data.operator === 'in' ? 'true, yes, y, ok, okay, sure' : '200'}
                        helperText={data.operator === 'in' ? 'Matches if the value equals any one of these.' : undefined}
                      />
                    )}
                  </Stack>
                </>
              ) : (
                <>
              <InfoHint>
                <strong>Office open?</strong> — <strong>Yes</strong> when the current
                time is inside the hours below, <strong>No</strong> when it isn't.
              </InfoHint>
              <TextField
                select
                label="Timezone"
                size="small"
                fullWidth
                value={canonicalTimezone(data.timezone ?? guessTimezone())}
                onChange={(e) => upd('timezone', e.target.value)}
                SelectProps={{ MenuProps: { PaperProps: { style: { maxHeight: 320 } } } }}
                helperText="Your hours are read in this zone, never the server's"
              >
                {TIMEZONES.map((tz) => (
                  <MenuItem key={tz.value} value={tz.value}>
                    {tz.label}
                  </MenuItem>
                ))}
              </TextField>
              <Stack direction="row" spacing={1}>
                <TextField
                  label="Opens"
                  size="small"
                  type="time"
                  value={data.startTime ?? '09:00'}
                  onChange={(e) => upd('startTime', e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  sx={{ flex: 1 }}
                />
                <TextField
                  label="Closes"
                  size="small"
                  type="time"
                  value={data.endTime ?? '17:00'}
                  onChange={(e) => upd('endTime', e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  sx={{ flex: 1 }}
                />
              </Stack>
              {/*
                Open-on day picker.

                The previous version stored "every day" as `[]` and had the
                first click materialise the full week before toggling. That
                inverted what a click appeared to do: with all seven lit,
                clicking "Sun" to mean "open Sundays" actually REMOVED Sunday.
                Ending up on a set like [Sun, Sat] — open weekends only — was
                easy to do by accident and impossible to spot afterwards,
                because the panel gives no summary of what's selected. A
                weekday message then fails dayAllowed(), the condition answers
                No, and it reads as a broken/ignored condition.

                Now: days are always an explicit list, a click toggles exactly
                the day clicked, and the current selection is spelled out in
                words underneath so a wrong set is visible at a glance.
              */}
              <Box>
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                  sx={{ mb: 0.75 }}
                >
                  <Typography variant="caption" sx={{ color: 'var(--wf-text-dim, #8b949e)' }}>
                    Open on
                  </Typography>
                  <Stack direction="row" spacing={0.5}>
                    <Button
                      size="small"
                      onClick={() => upd('days', WEEKDAYS.map((w) => w.value))}
                      sx={{ minWidth: 0, px: 0.75, fontSize: 11, textTransform: 'none' }}
                    >
                      Every day
                    </Button>
                    <Button
                      size="small"
                      onClick={() => upd('days', [1, 2, 3, 4, 5])}
                      sx={{ minWidth: 0, px: 0.75, fontSize: 11, textTransform: 'none' }}
                    >
                      Mon–Fri
                    </Button>
                  </Stack>
                </Stack>
                <Stack direction="row" flexWrap="wrap" gap={0.5}>
                  {WEEKDAYS.map((d) => {
                    // `[]` from a node saved under the old convention still
                    // means "every day" to the backend — show it that way here
                    // too, and the first toggle writes an explicit list.
                    const raw: number[] = Array.isArray(data.days) ? data.days : [];
                    const selected = raw.length === 0 ? WEEKDAYS.map((w) => w.value) : raw;
                    const on = selected.includes(d.value);
                    return (
                      <Button
                        key={d.value}
                        size="small"
                        variant={on ? 'contained' : 'outlined'}
                        // Never allow the set to empty out: the backend reads
                        // `days: []` as "every day", so deselecting the last
                        // day would silently mean the OPPOSITE of what the
                        // empty picker shows. Keeping one day pinned makes
                        // that state unreachable instead of merely warned about.
                        disabled={on && selected.length === 1}
                        onClick={() =>
                          upd(
                            'days',
                            on
                              ? selected.filter((v) => v !== d.value)
                              : [...selected, d.value].sort((a, b) => a - b)
                          )
                        }
                        sx={{ minWidth: 40, px: 1, textTransform: 'none' }}
                      >
                        {d.label}
                      </Button>
                    );
                  })}
                </Stack>
                <Typography
                  variant="caption"
                  sx={{ display: 'block', mt: 0.75, color: 'var(--wf-text-dim, #8b949e)' }}
                >
                  {describeOpenDays(data.days)}
                </Typography>
              </Box>
              <InfoHint>
                Overnight hours are fine — set Opens later than Closes (e.g. 22:00 → 06:00).
              </InfoHint>
                </>
              )}
            </>
          )}
          {type === 'condition' && !isConversationFlow && (
            <>
              <TextField
                select
                label="Field"
                size="small"
                fullWidth
                value={data.field ?? ''}
                onChange={(e) => upd('field', e.target.value)}
              >
                {LEAD_FIELDS.map((f) => (
                  <MenuItem key={f} value={f}>
                    {f}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                select
                label="Operator"
                size="small"
                fullWidth
                value={data.operator ?? 'eq'}
                onChange={(e) => upd('operator', e.target.value)}
              >
                {OPERATORS.map((o) => (
                  <MenuItem key={o.value} value={o.value}>
                    {o.label}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                label="Value"
                size="small"
                fullWidth
                value={data.value ?? ''}
                onChange={(e) => upd('value', e.target.value)}
                helperText="Use {{lead.field}} for dynamic values"
              />
            </>
          )}

          {/* ── When Customer Says (keyword_trigger) ─────────────────────── */}
          {type === 'keyword_trigger' && (
            <>
              <ChipListInput
                label="Keywords / phrases"
                placeholder="Type a phrase and press Enter, or paste a list"
                helperText="This branch fires when a customer message contains any of these"
                values={Array.isArray(data.keywords) ? data.keywords : []}
                onChange={(next) => upd('keywords', next)}
                maxLength={MAX_CHIP_LIST_LENGTH}
              />
              <ChipListInput
                label="Never fire on these (optional)"
                placeholder="e.g. your customer service team was great"
                helperText="A message matching one of these never triggers this branch, even if it also matches a keyword above"
                values={Array.isArray(data.exclusions) ? data.exclusions : []}
                onChange={(next) => upd('exclusions', next)}
                maxLength={50}
              />
            </>
          )}

          {/* ── When Button Tapped… (button_reply_trigger) ──────────────────── */}
          {type === 'button_reply_trigger' && (
            <ButtonReplyTriggerConfig
              data={data}
              updMany={updMany}
              interactiveMessages={interactiveMessages}
              onRefresh={onRefreshInteractiveMessages}
              refreshing={interactiveMessagesLoading}
            />
          )}

          {/* ── Send to a Team Member (human_handoff) ─────────────────────── */}
          {type === 'human_handoff' && (
            <>
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                Assign to
              </Typography>
              <ToggleButtonGroup
                exclusive
                size="small"
                value={assignmentKindDraft}
                onChange={(_, next) => {
                  if (!next) return;
                  setAssignmentKindDraft(next);
                  // Stored explicitly (not cleared) — this node doesn't consult
                  // Settings → Human Handoff, so "Any team member" must mean
                  // exactly that, not "whatever the tenant default happens to be
                  // right now". Picking "A specific role" only reveals the picker
                  // below; nothing is saved until a role is actually chosen (an
                  // empty roleIds is meaningless).
                  if (next === 'anyone') upd('assignment', { kind: 'anyone' });
                }}
                sx={{ alignSelf: 'flex-start' }}
              >
                <ToggleButton value="anyone" sx={{ textTransform: 'none', fontSize: 13, px: 1.5 }}>
                  Any team member
                </ToggleButton>
                <ToggleButton value="role" sx={{ textTransform: 'none', fontSize: 13, px: 1.5 }}>
                  A specific role
                </ToggleButton>
              </ToggleButtonGroup>
              {assignmentKindDraft === 'role' && (
                <Autocomplete
                  multiple
                  size="small"
                  options={roles}
                  value={roles.filter((r) =>
                    ((data.assignment as RoutingTarget | undefined)?.kind === 'role'
                      ? (data.assignment as { kind: 'role'; roleIds: string[] }).roleIds
                      : []
                    ).includes(r._id)
                  )}
                  isOptionEqualToValue={(a, b) => a._id === b._id}
                  getOptionLabel={(r) => r.name}
                  onChange={(_, value) => {
                    // Cleared back to nothing picked — falls back to "Any team
                    // member" rather than leaving an invisible, unsaved half-state.
                    if (!value.length) {
                      setAssignmentKindDraft('anyone');
                      upd('assignment', { kind: 'anyone' });
                      return;
                    }
                    upd('assignment', { kind: 'role', roleIds: value.map((r) => r._id) });
                  }}
                  renderTags={(value, getTagProps) =>
                    value.map((r, index) => (
                      <Chip {...getTagProps({ index })} key={r._id} size="small" label={r.name} />
                    ))
                  }
                  renderInput={(params) => (
                    <TextField {...(params as any)} placeholder="Choose one or more roles" />
                  )}
                />
              )}
            </>
          )}

          {/* ── Send Response (canned_reply) ──────────────────────── */}
          {type === 'canned_reply' && (
            <>
              <ToggleButtonGroup
                exclusive
                size="small"
                value={data.mode === 'template' ? 'template' : data.mode === 'interactive' ? 'interactive' : 'text'}
                onChange={(_, next) => {
                  if (!next) return;
                  // Only one message type may ever persist on this node: switching
                  // modes clears the other modes' fields instead of leaving them
                  // as stale, unused data alongside the new mode's fields.
                  updMany(
                    next === 'template'
                      ? {
                          mode: 'template',
                          replyText: undefined,
                          interactiveMessageId: undefined,
                          interactiveMessageKind: undefined,
                          interactiveMessageName: undefined,
                          variables: undefined,
                        }
                      : next === 'interactive'
                        ? {
                            mode: 'interactive',
                            replyText: undefined,
                            templateName: undefined,
                            templateLanguage: undefined,
                            bodyParams: undefined,
                            buttonUrlValue: undefined,
                          }
                        : {
                            mode: 'text',
                            templateName: undefined,
                            templateLanguage: undefined,
                            bodyParams: undefined,
                            buttonUrlValue: undefined,
                            interactiveMessageId: undefined,
                            interactiveMessageKind: undefined,
                            interactiveMessageName: undefined,
                            variables: undefined,
                          }
                  );
                }}
                fullWidth
                sx={{ width: '100%' }}
              >
                <ToggleButton value="text" sx={{ flex: 1, textTransform: 'none', fontSize: 12.5, px: 0.5, whiteSpace: 'nowrap' }}>
                  Text
                </ToggleButton>
                <ToggleButton value="template" sx={{ flex: 1, textTransform: 'none', fontSize: 12.5, px: 0.5, whiteSpace: 'nowrap' }}>
                  Template
                </ToggleButton>
                <ToggleButton value="interactive" sx={{ flex: 1, textTransform: 'none', fontSize: 12.5, px: 0.5, whiteSpace: 'nowrap' }}>
                  Buttons/List
                </ToggleButton>
              </ToggleButtonGroup>

              {data.mode === 'template' ? (
                <CannedReplyTemplateConfig data={data} upd={upd} updMany={updMany} templates={templates} variableTokens={variableTokens} wfThemeClass={wfThemeClass} />
              ) : data.mode === 'interactive' ? (
                <>
                  <UnifiedInteractivePicker
                    data={data}
                    upd={upd}
                    updMany={updMany}
                    interactiveMessages={interactiveMessages}
                    variableTokens={variableTokens}
                  />
                  <InfoHint>
                    Sends a saved WhatsApp Button or List message. Works only inside the 24-hour window
                    after the customer's last message.
                  </InfoHint>
                </>
              ) : (
                <>
                  <TextField
                    label="Reply text"
                    size="small"
                    fullWidth
                    multiline
                    rows={4}
                    value={data.replyText ?? ''}
                    onChange={(e) => upd('replyText', e.target.value)}
                    inputRef={replyRef}
                    placeholder="e.g. Hi {{system.customerName}}, you look {{gender}}!"
                    helperText={`${(data.replyText ?? '').length}/4000 — no AI call. Insert a variable at your cursor below.`}
                    inputProps={{ maxLength: 4000 }}
                  />
                  <Box>
                    {varGroups.inPath.length > 0 && (
                      <>
                        <Typography variant="caption" color="text.secondary">
                          From earlier steps on this path — tap to insert
                        </Typography>
                        <Stack direction="row" flexWrap="wrap" gap={0.5} mt={0.5}>
                          {varGroups.inPath.map((v) => (
                            <Tooltip key={v.token} title={v.hint}>
                              <Chip
                                size="small"
                                clickable
                                color="primary"
                                variant="filled"
                                label={`{{${v.token}}}`}
                                onMouseDown={(e) => e.preventDefault()}
                                onClick={() => insertReplyVar(`{{${v.token}}}`)}
                                sx={{ fontFamily: 'monospace', fontSize: 11.5 }}
                              />
                            </Tooltip>
                          ))}
                        </Stack>
                      </>
                    )}
                    {varGroups.others.length > 0 && (
                      <Box mt={varGroups.inPath.length ? 1 : 0}>
                        {varGroups.inPath.length > 0 ? (
                          <Button
                            size="small"
                            variant="text"
                            onClick={() => setShowOtherVars((s) => !s)}
                            sx={{ textTransform: 'none', px: 0.5, minWidth: 0 }}
                          >
                            {showOtherVars ? 'Hide other variables' : `Other variables (${varGroups.others.length})`}
                          </Button>
                        ) : (
                          <Typography variant="caption" color="text.secondary">
                            Insert a variable — drops in at your cursor
                          </Typography>
                        )}
                        {(showOtherVars || varGroups.inPath.length === 0) && (
                          <Stack direction="row" flexWrap="wrap" gap={0.5} mt={0.5}>
                            {varGroups.others.map((v) => (
                              <Tooltip key={v.token} title={v.hint}>
                                <Chip
                                  size="small"
                                  clickable
                                  variant="outlined"
                                  label={`{{${v.token}}}`}
                                  onMouseDown={(e) => e.preventDefault()}
                                  onClick={() => insertReplyVar(`{{${v.token}}}`)}
                                  sx={{ fontFamily: 'monospace', fontSize: 11.5 }}
                                />
                              </Tooltip>
                            ))}
                          </Stack>
                        )}
                      </Box>
                    )}
                    {/* Register a reusable variable into the shared registry (API-editor parity). */}
                    <Stack direction="row" spacing={0.5} alignItems="center" mt={1}>
                      <TextField
                        size="small"
                        variant="standard"
                        placeholder="Save a reusable variable…"
                        value={newVarName}
                        onChange={(e) => setNewVarName(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            void saveNewVar();
                          }
                        }}
                        sx={{ maxWidth: 220 }}
                        inputProps={{ style: { fontFamily: 'monospace', fontSize: 12 } }}
                      />
                      <Tooltip
                        title={
                          newVarValid
                            ? 'Save to your variable library — reusable in APIs, Get User Input and other flows'
                            : 'Letters, numbers and underscore; must start with a letter'
                        }
                      >
                        <span>
                          <IconButton size="small" disabled={!newVarValid || savingVar} onClick={() => void saveNewVar()}>
                            {savingVar ? <CircularProgress size={15} /> : <BookmarkAddOutlined fontSize="small" />}
                          </IconButton>
                        </span>
                      </Tooltip>
                    </Stack>
                  </Box>
                  <InfoHint>
                    Sends this reply. Connect another step (like "Send to a Team Member") to run
                    it next — otherwise the flow ends here.
                  </InfoHint>
                </>
              )}
            </>
          )}

          {/* ── AI Answer (ai_answer) ─────────────────────────────────────── */}
          {type === 'ai_answer' && (
            <>
              <TextField
                select
                label="Answer as agent"
                size="small"
                fullWidth
                value={data.agentId ?? ''}
                onChange={(e) => {
                  // Store the agent name alongside the id so the canvas node shows
                  // "Agent: Sales Bot" instead of an opaque id tail. Backend reads
                  // only agentId; agentName is display-only.
                  const picked = botAgents.find((a) => a._id === e.target.value);
                  updMany({ agentId: e.target.value, agentName: picked?.name ?? '' });
                }}
                helperText={
                  botAgents.length === 0
                    ? 'No agents found — create one first'
                    : 'This agent answers from its own knowledge base for this branch.'
                }
              >
                {agentOptionsFor(botAgents, data.agentId, data.agentName).map((a) => (
                  <MenuItem key={a._id} value={a._id}>
                    {a.name}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                label="Extra instructions (optional)"
                size="small"
                fullWidth
                multiline
                minRows={2}
                value={data.promptOverride ?? ''}
                onChange={(e) => upd('promptOverride', e.target.value)}
                helperText={'Appended to the agent’s own prompt for this branch, e.g. “Keep it under 2 sentences.”'}
              />
              {/*
                Without this, the pick lasted exactly ONE message. A specialist
                agent is normally reserved from auto-routing, and the automatic
                router skips reserved agents entirely — so the customer's second
                message quietly fell back to a general agent. Defaults to 24h
                (matching WhatsApp's own service window) for new nodes; a node
                saved before this existed reads as "This message only" and keeps
                behaving exactly as it did.
              */}
              <TextField
                select
                label="Keep answering as this agent for"
                size="small"
                fullWidth
                value={stickyOptionValue(data.pinDurationHours)}
                onChange={(e) => upd('pinDurationHours', Number(e.target.value))}
                helperText="Later messages in this chat keep this agent, even if it's reserved from automatic routing."
              >
                {stickyOptionsFor(data.pinDurationHours).map((o) => (
                  <MenuItem key={o.value} value={String(o.value)}>
                    {o.forAgent}
                  </MenuItem>
                ))}
              </TextField>
              <InfoHint>
                Hands the reply to this AI agent (Smart Answers / knowledge base), then the
                normal AI pipeline sends it. Wire a next step or end here — either way the agent
                answers.
                {Number(data.pinDurationHours ?? 0) > 0 && (
                  <>
                    {' '}
                    The pin clears early if the chat is handed to a team member.
                  </>
                )}
              </InfoHint>
            </>
          )}

          {/* ── Send Media (send_media) ──────────────────────────────────── */}
          {type === 'send_media' && (
            <>
              <TextField
                select
                label="File to send"
                size="small"
                fullWidth
                value={data.mediaAssetId ?? ''}
                onChange={(e) => upd('mediaAssetId', e.target.value)}
                helperText={
                  mediaAssets.length === 0
                    ? 'No media yet — upload files under Sources → Media first.'
                    : 'The file the bot sends when this step runs.'
                }
              >
                {mediaAssets.map((m) => (
                  <MenuItem key={m._id} value={m._id}>
                    {m.name} ({m.type})
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                label="Caption (optional)"
                size="small"
                fullWidth
                value={data.caption ?? ''}
                onChange={(e) => upd('caption', e.target.value)}
                placeholder="Shown under the file (ignored for audio)"
                inputProps={{ maxLength: 1024 }}
              />
              <InfoHint>
                Sends the selected file. Works only inside the 24-hour window after the
                customer's last message (a WhatsApp rule for non-template media). Manage files
                under Sources → Media.
              </InfoHint>
            </>
          )}

          {/* ── Send List Message / Send Button Message ─────────────────────── */}
          {(type === 'send_list_message' || type === 'send_button_message') && (
            <SendInteractiveMessageConfig
              kind={type === 'send_list_message' ? 'list' : 'button'}
              data={data}
              upd={upd}
              updMany={updMany}
              interactiveMessages={interactiveMessages}
              mediaAssets={mediaAssets}
            />
          )}

          {/* ── Turn Smart Answers On/Off (rag_toggle) ────────────────────── */}
          {type === 'rag_toggle' && (
            <>
              <TextField
                select
                label="For this chat"
                size="small"
                fullWidth
                value={data.ragOverride ?? 'inherit'}
                onChange={(e) => upd('ragOverride', e.target.value)}
              >
                {RAG_OVERRIDE_VALUES.map((v) => (
                  <MenuItem key={v.value} value={v.value}>
                    {v.label}
                  </MenuItem>
                ))}
              </TextField>
              {/*
                Only meaningful when actually overriding — "Use the account
                default" IS the reset, so an expiry on it would say nothing.
              */}
              {(data.ragOverride ?? 'inherit') !== 'inherit' && (
                <TextField
                  select
                  label="Keep this setting for"
                  size="small"
                  fullWidth
                  value={stickyOptionValue(data.overrideDurationHours)}
                  onChange={(e) => upd('overrideDurationHours', Number(e.target.value))}
                  helperText="After this, the chat goes back to your account default."
                >
                  <MenuItem value="0">Until something changes it</MenuItem>
                  {stickyOptionsFor(data.overrideDurationHours)
                    .filter((o) => o.value > 0)
                    .map((o) => (
                      <MenuItem key={o.value} value={String(o.value)}>
                        {o.label}
                      </MenuItem>
                    ))}
                </TextField>
              )}
              <InfoHint>
                Sets the override, then keeps going — connect this to another node
                (or leave it as a dead end to let the bot reply normally right after).
                {(data.ragOverride ?? 'inherit') !== 'inherit' &&
                  !(Number(data.overrideDurationHours) > 0) && (
                    <>
                      {' '}
                      <strong>This has no expiry</strong> — it stays in force for this chat
                      until another automation changes it.
                    </>
                  )}
              </InfoHint>
            </>
          )}

          {/* ── Flag for Review (flag) ────────────────────────────────────── */}
          {type === 'flag' && (
            <>
              <TextField
                label="Reason (optional)"
                size="small"
                fullWidth
                value={data.flagReason ?? ''}
                onChange={(e) => upd('flagReason', e.target.value)}
                placeholder="e.g. Mentioned a competitor"
                helperText="Shown on the chat in Chat Logs → Flagged. Leave blank and this automation's name is shown instead."
                inputProps={{ maxLength: 200 }}
              />
              <InfoHint>
                Marks the chat for review, then keeps going — the bot still replies
                normally unless another node here says otherwise.
              </InfoHint>
            </>
          )}

          {/* ── Delay ──────────────────────────────────────────────────── */}
          {type === 'delay' && (
            <Stack direction="row" spacing={1}>
              <TextField
                label="Duration"
                size="small"
                type="number"
                inputProps={{ min: 1 }}
                value={data.amount ?? 30}
                onChange={(e) => upd('amount', parseInt(e.target.value) || 1)}
                sx={{ flex: 1 }}
              />
              <TextField
                select
                label="Unit"
                size="small"
                value={data.unit ?? 'minutes'}
                onChange={(e) => upd('unit', e.target.value)}
                sx={{ flex: 1 }}
              >
                {DELAY_UNITS.map((u) => (
                  <MenuItem key={u} value={u}>
                    {u}
                  </MenuItem>
                ))}
              </TextField>
            </Stack>
          )}

          {/* ── Send Campaign ───────────────────────────────────────────── */}
          {type === 'send_campaign' && (
            <>
              <TextField
                select
                label="Campaign"
                size="small"
                fullWidth
                value={data.campaignId ?? ''}
                onChange={(e) => upd('campaignId', e.target.value)}
                helperText={
                  campaigns.length === 0 ? 'No campaigns found' : 'Select a campaign template'
                }
              >
                {campaigns.map((c) => (
                  <MenuItem key={c._id} value={c._id}>
                    {c.name}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                label="Message Override"
                size="small"
                fullWidth
                multiline
                rows={3}
                value={data.messageOverride ?? ''}
                onChange={(e) => upd('messageOverride', e.target.value)}
                helperText="Leave blank to use template default. Use {{lead.name}} tokens."
              />
            </>
          )}

          {/* ── Update Lead ─────────────────────────────────────────────── */}
          {type === 'update_lead' && (
            <>
              <Typography variant="subtitle2">Field Updates</Typography>
              {kvPairs.map((pair, i) => (
                <div key={i} className="wf-kv-row">
                  <TextField
                    placeholder="Field"
                    size="small"
                    value={pair.k}
                    onChange={(e) => {
                      const next: Array<{ k: string; v: string }> = kvPairs.map((p, j) =>
                        j === i ? { k: e.target.value, v: p.v } : p
                      );
                      setKvPairs(next);
                    }}
                    sx={{ flex: 1 }}
                  />
                  <TextField
                    placeholder="Value"
                    size="small"
                    value={pair.v}
                    onChange={(e) => {
                      const next: Array<{ k: string; v: string }> = kvPairs.map((p, j) =>
                        j === i ? { k: p.k, v: e.target.value } : p
                      );
                      setKvPairs(next);
                    }}
                    sx={{ flex: 1 }}
                  />
                  <IconButton
                    size="small"
                    onClick={() => setKvPairs(kvPairs.filter((_, j) => j !== i))}
                  >
                    <Delete fontSize="inherit" sx={{ color: '#ef4444' }} />
                  </IconButton>
                </div>
              ))}
              <Button
                size="small"
                startIcon={<Add />}
                onClick={() => setKvPairs([...kvPairs, { k: '', v: '' }])}
                sx={{ color: 'var(--wf-text-dim, #8b949e)', alignSelf: 'flex-start' }}
              >
                Add field
              </Button>
            </>
          )}

          {/* ── Assign Agent ────────────────────────────────────────────── */}
          {type === 'assign_agent' && (
            <TextField
              select
              label="Assign to"
              size="small"
              fullWidth
              value={data.agentId ?? ''}
              onChange={(e) => upd('agentId', e.target.value)}
              helperText={agents.length === 0 ? 'No team members found — add one under Team' : 'Assign the lead to this team member'}
            >
              {agents.map((a) => (
                <MenuItem key={a._id} value={a._id}>
                  {a.name}
                </MenuItem>
              ))}
            </TextField>
          )}

          {/* ── Webhook ─────────────────────────────────────────────────── */}
          {type === 'webhook' && (
            <>
              <Stack direction="row" spacing={1}>
                <TextField
                  select
                  label="Method"
                  size="small"
                  value={data.webhookMethod ?? 'POST'}
                  onChange={(e) => upd('webhookMethod', e.target.value)}
                  sx={{ width: 100 }}
                >
                  {HTTP_METHODS.map((m) => (
                    <MenuItem key={m} value={m}>
                      {m}
                    </MenuItem>
                  ))}
                </TextField>
                <TextField
                  label="URL"
                  size="small"
                  fullWidth
                  value={data.webhookUrl ?? ''}
                  onChange={(e) => upd('webhookUrl', e.target.value)}
                  placeholder="https://..."
                />
              </Stack>
              <TextField
                label="Headers (JSON)"
                size="small"
                fullWidth
                multiline
                rows={3}
                value={data.webhookHeaders ?? '{}'}
                onChange={(e) => upd('webhookHeaders', e.target.value)}
                helperText='e.g. {"Authorization": "Bearer {{token}}"}'
                inputProps={{ style: { fontFamily: 'monospace', fontSize: 12 } }}
              />
              <TextField
                label="Body (JSON)"
                size="small"
                fullWidth
                multiline
                rows={4}
                value={data.webhookBody ?? '{}'}
                onChange={(e) => upd('webhookBody', e.target.value)}
                helperText="Use {{lead.field}} for dynamic values"
                inputProps={{ style: { fontFamily: 'monospace', fontSize: 12 } }}
              />
              <TextField
                label="Timeout (ms)"
                size="small"
                type="number"
                value={data.timeoutMs ?? 5000}
                onChange={(e) => upd('timeoutMs', parseInt(e.target.value) || 5000)}
              />
            </>
          )}

          {/* ── Call API (http_request) ──────────────────────────────── */}
          {type === 'http_request' &&
            (() => {
              // The node only PICKS an API. Its request, auth and response mapping are
              // built and tested once on the API page, so nothing about the call is
              // edited here — every flow using it runs the same, tested definition.
              const activeConnections = apiConnections.filter((c) => c.status === 'active');
              const selectedConnection = apiConnections.find((c) => c.id === data.connectionId);
              const connectionMissing =
                !!data.connectionId && !apiConnectionsLoading && apiConnections.length > 0 && !selectedConnection;
              const req = selectedConnection?.request;
              const maps = req?.responseMappings ?? [];
              return (
                <>
                  <Stack direction="row" spacing={1} alignItems="flex-start">
                    <TextField
                      select
                      label="API"
                      size="small"
                      fullWidth
                      required
                      error={!data.connectionId || connectionMissing}
                      value={connectionMissing ? '' : (data.connectionId ?? '')}
                      onChange={(e) => {
                        const c = apiConnections.find((x) => x.id === e.target.value);
                        // One update — consecutive `upd` calls would drop the first change.
                        // Also drop request fields left over from when this node carried its
                        // own URL/headers/body: the API owns the request now, and a stale
                        // `url` is never used (and would only confuse a future reader).
                        updMany({
                          connectionId: e.target.value,
                          connectionName: c?.name,
                          url: undefined,
                          ...(c?.request
                            ? {
                                method: undefined,
                                path: undefined,
                                queryParams: undefined,
                                headers: undefined,
                                bodyMode: undefined,
                                body: undefined,
                                responseMappings: undefined,
                              }
                            : {}),
                        });
                      }}
                      helperText={
                        connectionMissing
                          ? 'This API was deleted — pick another.'
                          : selectedConnection?.status === 'disabled'
                            ? 'This API is disabled — calls are skipped until it is re-enabled.'
                            : activeConnections.length === 0
                              ? 'No APIs yet — create one, then refresh.'
                              : 'Choose which saved API this step calls.'
                      }
                    >
                      {activeConnections.map((c) => (
                        <MenuItem key={c.id} value={c.id}>
                          {c.name}
                        </MenuItem>
                      ))}
                      {selectedConnection?.status === 'disabled' && (
                        <MenuItem value={selectedConnection.id} disabled>
                          {selectedConnection.name} (disabled)
                        </MenuItem>
                      )}
                    </TextField>
                    <Tooltip title="Refresh APIs">
                      <span>
                        <IconButton size="small" onClick={onRefreshApiConnections} disabled={apiConnectionsLoading} sx={{ mt: 0.5 }}>
                          {apiConnectionsLoading ? <CircularProgress size={16} /> : <Refresh fontSize="small" />}
                        </IconButton>
                      </span>
                    </Tooltip>
                  </Stack>
                  <Stack direction="row" spacing={2}>
                    <Link
                      href="/admin/automation/api-connections/new"
                      target="_blank"
                      rel="noopener noreferrer"
                      underline="hover"
                      sx={{ fontSize: 12, display: 'inline-flex', alignItems: 'center', gap: 0.5 }}
                    >
                      Create a new API <OpenInNew sx={{ fontSize: 13 }} />
                    </Link>
                    {selectedConnection && (
                      <Link
                        href={`/admin/automation/api-connections/${selectedConnection.id}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        underline="hover"
                        sx={{ fontSize: 12, display: 'inline-flex', alignItems: 'center', gap: 0.5 }}
                      >
                        Edit this API <OpenInNew sx={{ fontSize: 13 }} />
                      </Link>
                    )}
                  </Stack>

                  {selectedConnection && (
                    <Box sx={{ p: 1.25, borderRadius: 1, border: '1px solid var(--wf-border-strong, #30363d)', bgcolor: 'var(--wf-surface-2, rgba(255,255,255,0.03))' }}>
                      <Typography variant="caption" color="text.secondary">
                        This step will call
                      </Typography>
                      <Typography sx={{ fontFamily: 'monospace', fontSize: 12, wordBreak: 'break-all' }}>
                        <strong>{req?.method ?? 'GET'}</strong>{' '}
                        {joinEndpoint(selectedConnection.baseUrl, req?.path, req?.queryParams)}
                      </Typography>
                      {maps.length > 0 ? (
                        <Box mt={1}>
                          <Typography variant="caption" color="text.secondary">
                            It gives you these variables (use them in replies):
                          </Typography>
                          <Stack direction="row" flexWrap="wrap" gap={0.5} mt={0.5}>
                            {maps.map((m, i) => (
                              <Chip
                                key={i}
                                size="small"
                                label={
                                  m.target.kind === 'flow'
                                    ? `{{${m.target.name}}}`
                                    : `${m.target.kind === 'contactField' ? 'contact' : 'lead'} · ${m.target.key}`
                                }
                              />
                            ))}
                          </Stack>
                        </Box>
                      ) : (
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                          No response values are mapped yet — add them on the API page.
                        </Typography>
                      )}
                    </Box>
                  )}

                  <FormControlLabel
                    control={<Switch checked={!!data.branchOnError} onChange={(e) => upd('branchOnError', e.target.checked)} />}
                    label="Add a failure branch (otherwise the flow continues on error)"
                  />
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                    {data.branchOnError
                      ? 'The node now has an “OK” and a “Failed” output — wire “Failed” to your error handling (a reply, a retry, a handoff). A blocked or non-2xx call takes it.'
                      : 'On error the flow continues on the normal path. You can also branch on {{_httpStatus}} (0 = blocked/network, else the HTTP code) with a Condition step.'}
                  </Typography>
                </>
              );
            })()}

          {/* ── Get User Input (collect_input) ─────────────────────────── */}
          {type === 'collect_input' &&
            (() => {
              const target = data.target ?? { kind: 'flow', name: '' };
              const kind = target.kind ?? 'flow';
              const nameVal = kind === 'flow' ? target.name ?? '' : target.key ?? '';
              const choices: string[] = Array.isArray(data.choices) ? data.choices : [];
              // Tab state: an explicit UI flag, but ALSO flip to interactive
              // whenever a message is already selected (so a saved node re-opens
              // on the right tab even without the flag).
              const inputMode: 'text' | 'interactive' =
                data.inputMode === 'interactive' || data.interactiveMessageId ? 'interactive' : 'text';
              return (
                <>
                  <ToggleButtonGroup
                    exclusive
                    size="small"
                    value={inputMode}
                    onChange={(_, next) => {
                      if (!next) return;
                      if (next === 'interactive') {
                        updMany({ inputMode: 'interactive' });
                      } else {
                        // Back to a typed question — drop every interactive field.
                        updMany({
                          inputMode: 'text',
                          interactiveMessageId: undefined,
                          interactiveMessageKind: undefined,
                          interactiveMessageName: undefined,
                          variables: undefined,
                        });
                      }
                    }}
                    fullWidth
                    sx={{ width: '100%' }}
                  >
                    <ToggleButton value="text" sx={{ flex: 1, textTransform: 'none', fontSize: 12.5, px: 0.5, whiteSpace: 'nowrap' }}>
                      Text question
                    </ToggleButton>
                    <ToggleButton value="interactive" sx={{ flex: 1, textTransform: 'none', fontSize: 12.5, px: 0.5, whiteSpace: 'nowrap' }}>
                      Buttons/List
                    </ToggleButton>
                  </ToggleButtonGroup>

                  {inputMode === 'interactive' ? (
                    <>
                      <UnifiedInteractivePicker
                        data={data}
                        upd={upd}
                        updMany={updMany}
                        interactiveMessages={interactiveMessages}
                        variableTokens={variableTokens}
                      />
                      <InfoHint>
                        The customer can tap a button/row <strong>or type a reply</strong> — both are
                        captured. The chosen label is saved to your variable; the stable id to{' '}
                        <code>{`{{${nameVal || 'name'}_id}}`}</code>.
                      </InfoHint>
                    </>
                  ) : (
                    <>
                      <TextField
                        label="Question to ask"
                        size="small"
                        fullWidth
                        multiline
                        rows={2}
                        value={data.prompt ?? ''}
                        onChange={(e) => upd('prompt', e.target.value)}
                        placeholder="What's your order number?"
                        helperText="You can use {{input.x}} from earlier answers."
                      />
                      <TextField
                        select
                        label="Answer type"
                        size="small"
                        fullWidth
                        value={data.responseType ?? 'text'}
                        onChange={(e) => upd('responseType', e.target.value)}
                      >
                        {['text', 'email', 'phone', 'number', 'date', 'choice', 'media'].map((t) => (
                          <MenuItem key={t} value={t}>
                            {t === 'media' ? 'photo / file' : t}
                          </MenuItem>
                        ))}
                      </TextField>
                      {data.responseType === 'choice' && (
                        <TextField
                          label="Choices (comma-separated)"
                          size="small"
                          fullWidth
                          value={choices.join(', ')}
                          onChange={(e) =>
                            upd('choices', e.target.value.split(',').map((s) => s.trim()).filter(Boolean))
                          }
                          placeholder="Yes, No"
                        />
                      )}
                      {data.responseType === 'media' && (
                        <>
                          <TextField
                            select
                            label="Accepted file type"
                            size="small"
                            fullWidth
                            value={
                              Array.isArray(data.acceptedMediaTypes) && data.acceptedMediaTypes.length
                                ? data.acceptedMediaTypes[0]
                                : 'any'
                            }
                            onChange={(e) =>
                              upd(
                                'acceptedMediaTypes',
                                e.target.value === 'any' ? undefined : [e.target.value]
                              )
                            }
                            helperText="Locks the answer to one kind of file — any other kind is re-asked."
                          >
                            {['any', 'image', 'video', 'audio', 'document', 'sticker'].map((t) => (
                              <MenuItem key={t} value={t} sx={{ textTransform: 'capitalize' }}>
                                {t === 'any' ? 'Any file' : t}
                              </MenuItem>
                            ))}
                          </TextField>
                          <InfoHint>
                            The flow <strong>waits</strong> for the customer to send a photo/file — just like it
                            waits for a typed answer. The stored media URL is saved to your variable
                            {nameVal ? (
                              <>
                                {' '}(<code>{`{{${nameVal}}}`}</code>), with <code>{`{{${nameVal}_mime}}`}</code>,{' '}
                                <code>{`{{${nameVal}_type}}`}</code> and <code>{`{{${nameVal}_name}}`}</code> alongside
                              </>
                            ) : (
                              <>, plus companion <code>_mime</code> / <code>_type</code> / <code>_name</code> variables</>
                            )}
                            . There's a short delay while we fetch the file before the flow continues.
                          </InfoHint>
                        </>
                      )}
                    </>
                  )}
                  <Divider textAlign="left">
                    <Typography variant="caption" color="text.secondary">
                      Save the answer to
                    </Typography>
                  </Divider>
                  <TextField
                    select
                    label="Save as"
                    size="small"
                    fullWidth
                    value={kind}
                    onChange={(e) => {
                      const nk = e.target.value;
                      upd('target', nk === 'flow' ? { kind: 'flow', name: nameVal } : { kind: nk, key: nameVal });
                    }}
                  >
                    <MenuItem value="flow">Flow variable</MenuItem>
                    <MenuItem value="contactField">Contact field</MenuItem>
                  </TextField>
                  {kind === 'flow' ? (
                    <Box>
                      <Typography sx={{ fontSize: 12, color: 'var(--wf-text-dim, #939eb2)', mb: 0.5 }}>
                        Variable name
                      </Typography>
                      <FlowVariablePicker
                        value={nameVal}
                        onChange={(name) => upd('target', { kind: 'flow', name })}
                        options={flowVariables.map((f) => ({ key: f.key, label: f.label, description: f.description, type: f.type }))}
                        loading={flowVariablesLoading}
                        onRefresh={onRefreshFlowVariables}
                        placeholder="e.g. order_number"
                      />
                      <Typography sx={{ fontSize: 11.5, color: 'var(--wf-text-faint, #5a6478)', mt: 0.5 }}>
                        {nameVal
                          ? `Use it later as {{${nameVal}}} — lasts for this conversation’s flow only.`
                          : 'Pick an existing variable or type a new name (letters, numbers and _ ; start with a letter).'}
                      </Typography>
                    </Box>
                  ) : (
                    <TextField
                      label="Contact field key"
                      size="small"
                      fullWidth
                      placeholder="e.g. city"
                      value={nameVal}
                      onChange={(e) => upd('target', { kind, key: e.target.value })}
                      helperText="Saved on the contact permanently. Must match a field under Settings › Custom Fields."
                    />
                  )}
                  <TextField
                    label="If the answer is invalid (re-ask message)"
                    size="small"
                    fullWidth
                    value={data.invalidMessage ?? ''}
                    onChange={(e) => upd('invalidMessage', e.target.value)}
                    placeholder="Sorry, that doesn't look right. Please try again."
                  />
                  <Stack direction="row" spacing={1}>
                    <TextField
                      label="Max retries"
                      size="small"
                      type="number"
                      value={data.maxRetries ?? 2}
                      onChange={(e) => upd('maxRetries', parseInt(e.target.value) || 0)}
                      sx={{ width: 120 }}
                    />
                    <TextField
                      label="Wait up to (hours)"
                      size="small"
                      type="number"
                      value={data.timeoutHours ?? 24}
                      onChange={(e) => upd('timeoutHours', parseInt(e.target.value) || 24)}
                      sx={{ width: 150 }}
                    />
                  </Stack>
                </>
              );
            })()}

          {/* ── Mark Verified (grant_verification) ──────────────────────── */}
          {type === 'grant_verification' && (
            <InfoHint>
              Marks this chat as <strong>verified</strong> for the rest of the flow, which unlocks any
              “Call API” step whose connection requires verification. Place this node only
              <strong> after</strong> your own check (e.g. ask for a PIN → call your verify API →
              branch on the result).
            </InfoHint>
          )}

          {/* ── End (flow_end) ──────────────────────────────────────────── */}
          {type === 'flow_end' && (
            <InfoHint>
              Ends the conversation here. The bot sends nothing further and the normal AI answer
              (Smart Answers / knowledge base) <strong>does not run</strong> after this — use it on a
              path you want to finish deliberately silent, like a “gave up / not verified” branch.
              Any step wired after it never runs.
            </InfoHint>
          )}

          {/* ── Trigger Workflow ────────────────────────────────────────── */}
          {type === 'trigger_workflow' && (
            <>
              {(() => {
                // A customer-message flow can only ever bridge INTO a lead/
                // campaign workflow — the async engine that runs the target
                // has no executor for keyword_trigger/canned_reply/etc, so a
                // message.received target would fail every run. Excluding it
                // here is the primary guard; the backend also fails that
                // combination cleanly (a visibly failed execution, not a
                // crash) if this is ever bypassed via a raw API call.
                const targetable = workflows.filter(
                  (w) =>
                    w.triggerType !== 'message.received' &&
                    (!currentWorkflowId || currentWorkflowId === 'new' || w._id !== currentWorkflowId)
                );
                return (
                  <TextField
                    select
                    label="Target Workflow"
                    size="small"
                    fullWidth
                    value={data.targetWorkflowId ?? ''}
                    onChange={(e) => upd('targetWorkflowId', e.target.value)}
                    helperText={
                      targetable.length === 0
                        ? 'No eligible workflows found — this only lists lead/campaign workflows'
                        : 'Select a lead/campaign workflow to run in the background'
                    }
                  >
                    {targetable.map((w) => (
                      <MenuItem key={w._id} value={w._id}>
                        {w.name}
                      </MenuItem>
                    ))}
                  </TextField>
                );
              })()}
              {isConversationFlow && (
                <InfoHint>
                  Runs in the background after this point in the flow — it does not change what
                  the customer sees. The customer is matched to a lead by phone number
                  (created automatically if they're new).
                </InfoHint>
              )}
            </>
          )}
        </Stack>
      </div>

      <div className="wf-config__footer">
        <span className={`wf-config__status${isNodeConfigured(type, data) ? '' : ' is-todo'}`}>
          <span className="dot" />
          {isNodeConfigured(type, data) ? 'Step configured' : 'Needs setup'}
        </span>
        {type !== 'trigger' && (
          <Tooltip title="Remove this step">
            <IconButton size="small" onClick={onDelete} aria-label="Remove this step" sx={{ color: 'var(--wf-text-dim, #8b949e)', '&:hover': { color: '#dc2626' } }}>
              <Delete fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </div>
    </>
  );
}

// ─── Inner canvas (needs useReactFlow) ───────────────────────────────────────

function CanvasInner() {
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { screenToFlowPosition, setCenter, getNode, zoomIn, zoomOut, fitView, fitBounds, getNodes, setViewport } =
    useReactFlow();
  const rfWrapper = useRef<HTMLDivElement>(null);
  const pageRef = useRef<HTMLDivElement>(null);

  // ── Workflow meta ───────────────────────────────────────────────────────
  const [wfName, setWfName] = useState('Untitled Workflow');
  const [wfDesc, setWfDesc] = useState('');
  const [isActive, setIsActive] = useState(false);
  const [wfSettings, setWfSettings] = useState({
    maxRetries: 3,
    retryOnFailure: true,
    deduplicationWindow: 3600,
    maxConcurrentExecutions: 100,
  });

  // ── ReactFlow state ─────────────────────────────────────────────────────
  const [nodes, setNodes, onNodesChangeRaw] = useNodesState<Record<string, any>>([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);

  // Drop any change targeting the synthetic fallback node before it reaches the
  // real node list — it isn't in `nodes`, so applyNodeChanges would ignore it
  // anyway, but filtering here keeps that invariant explicit and future-proof.
  const onNodesChange = useCallback(
    (changes: Parameters<typeof onNodesChangeRaw>[0]) =>
      onNodesChangeRaw(
        changes.filter((c: any) => c.id !== DEFAULT_FALLBACK_NODE_ID)
      ),
    [onNodesChangeRaw]
  );

  // ── UI state ────────────────────────────────────────────────────────────
  const [selectedNodeId, setSelectedNodeId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Open in full screen by default — the builder needs the width, and the
  // half-width shell view stays available via the toolbar toggle (Esc exits).
  const [isFullscreen, setIsFullscreen] = useState(true);

  /** Node ids the current save error is about — the canvas outlines them so a
   *  banner still tells the author exactly which node to fix. Cleared on any edit. */
  const [errorNodeIds, setErrorNodeIds] = useState<string[]>([]);

  /** Right-click context menu for deleting a node or a connection. Positioned in
   *  screen coords relative to the canvas wrapper. Null = closed. */
  const [ctxMenu, setCtxMenu] = useState<
    { x: number; y: number; kind: 'node' | 'edge'; id: string; deletable: boolean } | null
  >(null);

  /** Select a node, pan the viewport to it, and mark it as the error target.
   *  Used both by client-side pre-checks and node-attributed server errors. */
  const focusNode = useCallback(
    (nodeId: string | undefined, alsoHighlight: string[] = []) => {
      const ids = [nodeId, ...alsoHighlight].filter(Boolean) as string[];
      setErrorNodeIds(ids);
      if (!nodeId) return;
      setSelectedNodeId(nodeId);
      const n = getNode(nodeId);
      if (n) {
        // width/height can be unmeasured on a node that's never been rendered
        // in view; fall back to sane defaults so the centre point is still right.
        const w = n.width ?? 220;
        const h = n.height ?? 90;
        setCenter(n.position.x + w / 2, n.position.y + h / 2, { zoom: 1, duration: 400 });
      }
    },
    [getNode, setCenter]
  );

  // ── Select lists for config panel dropdowns ──────────────────────────────
  // ── Test panel (dry run — never executes the flow) ───────────────────────
  // Builder theme: LIGHT by default, dark one click away. Remembered per browser.
  const [wfTheme, setWfTheme] = useState<'light' | 'dark'>(() => {
    try {
      return localStorage.getItem('wf-theme') === 'dark' ? 'dark' : 'light';
    } catch {
      return 'light';
    }
  });
  const toggleWfTheme = useCallback(() => {
    setWfTheme((t) => {
      const next = t === 'light' ? 'dark' : 'light';
      try {
        localStorage.setItem('wf-theme', next);
      } catch {
        /* private mode — the choice just won't persist */
      }
      return next;
    });
  }, []);
  // Collapsible node palette: a slim icon rail when collapsed. Remembered per browser.
  const [paletteCollapsed, setPaletteCollapsed] = useState<boolean>(() => {
    try {
      return localStorage.getItem('wf-palette-collapsed') === '1';
    } catch {
      return false;
    }
  });
  const togglePalette = useCallback(() => {
    setPaletteCollapsed((c) => {
      try {
        localStorage.setItem('wf-palette-collapsed', c ? '0' : '1');
      } catch {
        /* private mode — just won't persist */
      }
      return !c;
    });
  }, []);

  // Canvas lock: freezes node positions, connections and selection/deletion (pan and
  // zoom still work) — React Flow's own lock semantics, but driven by OUR state so it
  // is explicit, visible on the button, and also blocks dropping new nodes.
  const [canvasLocked, setCanvasLocked] = useState(false);

  // Real browser full screen (hides the tab strip + address bar for a bigger canvas).
  const [browserFs, setBrowserFs] = useState<boolean>(() => !!document.fullscreenElement);
  useEffect(() => {
    const onChange = () => setBrowserFs(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', onChange);
    return () => document.removeEventListener('fullscreenchange', onChange);
  }, []);
  // Node search (⌘K / Ctrl+K focuses it).
  const [paletteQuery, setPaletteQuery] = useState('');
  const paletteSearchRef = useRef<HTMLInputElement | null>(null);
  const paletteGroupsView = useMemo(() => {
    const q = paletteQuery.trim().toLowerCase();
    if (!q) return PALETTE_GROUPS;
    return PALETTE_GROUPS.map((g) => ({
      ...g,
      items: g.items.filter(({ type }) => {
        const m = NODE_META[type];
        return `${m.label} ${m.desc}`.toLowerCase().includes(q);
      }),
    })).filter((g) => g.items.length > 0);
  }, [paletteQuery]);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setPaletteCollapsed(false);
        setTimeout(() => paletteSearchRef.current?.focus(), 0);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  // Honest save status: compare the current graph to the last saved/loaded one.
  const [savedSig, setSavedSig] = useState<string | null>(null);
  const sigRef = useRef('');
  const [testOpen, setTestOpen] = useState(false);
  const [testMessage, setTestMessage] = useState('');
  const [testButtonId, setTestButtonId] = useState('');
  const [testRunning, setTestRunning] = useState(false);
  const [testResult, setTestResult] = useState<any>(null);
  const [testError, setTestError] = useState<string | null>(null);
  // Multi-turn test: the chat so far, the server-held conversation state (non-null
  // while the flow waits for an answer), the live variable bag, and the API switch.
  const [testTranscript, setTestTranscript] = useState<Array<{ role: 'customer' | 'bot' | 'system'; text: string }>>([]);
  const [testSession, setTestSession] = useState<any>(null);
  const [testVars, setTestVars] = useState<Record<string, unknown>>({});
  const [testLiveApi, setTestLiveApi] = useState(true);
  const transcriptRef = useRef<HTMLDivElement | null>(null);
  const [saveWarnings, setSaveWarnings] = useState<string[]>([]);

  const [campaignList, setCampaignList] = useState<Array<{ _id: string; name: string }>>([]);
  const [agentList, setAgentList] = useState<Array<{ _id: string; name: string }>>([]);
  const [botAgentList, setBotAgentList] = useState<Array<{ _id: string; name: string }>>([]);
  // Roles — for the human_handoff node's own assignment-target override
  // (defaults to inheriting the tenant-wide setting when unset).
  const [roleList, setRoleList] = useState<Array<{ _id: string; name: string }>>([]);
  const [workflowList, setWorkflowList] = useState<
    Array<{ _id: string; name: string; triggerType?: string }>
  >([]);
  const [routingSettings, setRoutingSettings] = useState<RoutingSettingsResponse | null>(null);
  // Saved API Connections, for the "Call API" node's connection picker.
  const [apiConnectionList, setApiConnectionList] = useState<ApiConnectionOption[]>([]);
  const [apiConnectionsLoading, setApiConnectionsLoading] = useState(false);
  // Re-fetchable so an author can create a connection in another tab and pull it
  // in without reloading (and losing) the canvas.
  const refreshApiConnections = useCallback(() => {
    setApiConnectionsLoading(true);
    apiConnectionApi
      .list()
      .then((list) =>
        setApiConnectionList(
          list.map((c) => ({
            id: c.id,
            name: c.name,
            baseUrl: c.baseUrl,
            status: c.status,
            ...(c.request ? { request: c.request } : {}),
          }))
        )
      )
      .catch(() => {})
      .finally(() => setApiConnectionsLoading(false));
  }, []);

  // Workspace flow-variable registry — the reusable {{variable}} names offered by
  // the "Get User Input" picker. Re-fetchable so a name created in another tab
  // (Settings › Custom Fields › Flow variables) can be pulled in without reloading
  // the canvas. Advisory only: an author may still type an unregistered name.
  const [flowVariables, setFlowVariables] = useState<FieldDefinition[]>([]);
  const [flowVariablesLoading, setFlowVariablesLoading] = useState(false);
  const refreshFlowVariables = useCallback(() => {
    setFlowVariablesLoading(true);
    adminApi
      .getFieldDefinitions('flow', { suppressPermissionLock: true })
      .then(setFlowVariables)
      .catch(() => {})
      .finally(() => setFlowVariablesLoading(false));
  }, []);

  // What each node PRODUCES (answers collected by "Get User Input", values mapped
  // by each "Call API"), keyed by node id. Shared by the flat insert-list and the
  // upstream-path computation so the two never drift.
  const producedByNode = useMemo(() => {
    const map = new Map<string, Array<{ token: string; hint: string }>>();
    for (const n of nodes) {
      const d = (n.data ?? {}) as any;
      const list: Array<{ token: string; hint: string }> = [];
      if (n.type === 'collect_input' && d.target) {
        if (d.target.kind === 'flow' && d.target.name) list.push({ token: d.target.name, hint: 'Answer to “Get User Input”' });
        else if (d.target.key) list.push({ token: `contact.custom.${d.target.key}`, hint: 'Answer saved to a contact field' });
      }
      if (n.type === 'http_request') {
        const api = apiConnectionList.find((c) => c.id === d.connectionId);
        for (const m of api?.request?.responseMappings ?? []) {
          if (m.target.kind === 'flow') list.push({ token: m.target.name, hint: `From ${api?.name}` });
          else if (m.target.kind === 'contactField') list.push({ token: `contact.custom.${m.target.key}`, hint: `From ${api?.name}` });
        }
      }
      if (list.length) map.set(n.id, list);
    }
    return map;
  }, [nodes, apiConnectionList]);

  // Everything a reply could reference, derived from what is actually on this canvas.
  // Flow variables live for the run; contact fields are readable in-run once mapped.
  const availableVariables = useMemo(() => {
    const out: Array<{ token: string; hint: string }> = [];
    const seen = new Set<string>();
    const add = (token: string, hint: string) => {
      if (!token || seen.has(token)) return;
      seen.add(token);
      out.push({ token, hint });
    };
    for (const n of nodes) for (const v of producedByNode.get(n.id) ?? []) add(v.token, v.hint);
    add('system.customerName', 'Customer name');
    add('system.customerPhone', 'Customer phone');
    return out;
  }, [nodes, producedByNode]);

  // Tokens produced by steps that actually run BEFORE the selected node (walk the
  // edges backward). These are the ones a Send Response here can truly rely on, so
  // the config panel surfaces them first instead of every variable on the canvas.
  const upstreamTokens = useMemo(() => {
    const set = new Set<string>();
    if (!selectedNodeId) return set;
    const preds = new Map<string, string[]>();
    for (const e of edges) {
      (preds.get(e.target) ?? preds.set(e.target, []).get(e.target)!).push(e.source);
    }
    const visited = new Set<string>();
    const stack = [...(preds.get(selectedNodeId) ?? [])];
    while (stack.length) {
      const id = stack.pop()!;
      if (visited.has(id)) continue;
      visited.add(id);
      for (const v of producedByNode.get(id) ?? []) set.add(v.token);
      for (const p of preds.get(id) ?? []) stack.push(p);
    }
    return set;
  }, [selectedNodeId, edges, producedByNode]);
  // Only APPROVED templates are selectable for the canned-reply node's
  // template mode — Meta rejects sends of anything else.
  const [approvedTemplates, setApprovedTemplates] = useState<WhatsAppTemplate[]>([]);
  // The tenant's sendable-media library, for the send_media node's file picker.
  const [mediaAssets, setMediaAssets] = useState<MediaAsset[]>([]);
  // The tenant's saved interactive-message library, for the Send List / Send
  // Button Message node pickers AND the button-reply trigger picker. Re-fetchable
  // so an author can save a message in another tab and pull it (and its minted
  // option ids) into a trigger without reloading — same pattern as the API
  // connection / flow-variable refreshes above.
  const [interactiveMessages, setInteractiveMessages] = useState<InteractiveMessage[]>([]);
  const [interactiveMessagesLoading, setInteractiveMessagesLoading] = useState(false);
  const refreshInteractiveMessages = useCallback(() => {
    setInteractiveMessagesLoading(true);
    adminApi
      .getInteractiveMessages()
      .then((list) => setInteractiveMessages(list || []))
      .catch(() => {})
      .finally(() => setInteractiveMessagesLoading(false));
  }, []);
  // Starter-template picker: prefills the canvas from a ready-made workflow so an
  // admin can learn the two engines by example (GET /automation/workflows/templates).
  const [templateOpen, setTemplateOpen] = useState(false);
  const [templates, setTemplates] = useState<any[]>([]);
  const [templatesLoading, setTemplatesLoading] = useState(false);
  const [templatesError, setTemplatesError] = useState<string | null>(null);

  const selectedNode = nodes.find((n) => n.id === selectedNodeId) ?? null;
  const triggerNodeData = nodes.find((n) => n.type === 'trigger')?.data as
    | Record<string, any>
    | undefined;
  const { hasFeature } = usePlanFeature();
  const isConversationFlow = triggerNodeData?.['triggerType'] === 'message.received';
  const currentTriggerType = triggerNodeData?.['triggerType'] as string | undefined;
  // HTTP-automation nodes are gated on the httpAutomationEnabled plan feature —
  // kept consistent with the API Connections screen's plan-lock. Backend also
  // re-checks at run time, so a saved flow never runs a node a downgrade removed.
  const httpAutomationLocked = !hasFeature('httpAutomationEnabled');

  // What ReactFlow actually renders: the real nodes (with an error outline flag
  // injected), plus — only on a conversation flow — the persistent, read-only
  // "If nothing above matches" fallback node. The fallback is synthetic: it is
  // filtered back out before save (see handleSave/buildBackendGraph) and can't
  // be selected or dragged.
  const displayNodes = useMemo<Node[]>(() => {
    const errored = new Set(errorNodeIds);
    // Variables a step saves, shown as small "↓ name" links on its card. DERIVED here
    // for display only — never written into saved node data.
    const varsFor = (n: Node): string[] => {
      const d: any = n.data ?? {};
      if (n.type === 'collect_input') {
        const t = d.target;
        return t?.kind === 'flow' && t.name ? [t.name] : t?.key ? [`contact.${t.key}`] : [];
      }
      if (n.type === 'http_request') {
        const api = apiConnectionList.find((c) => c.id === d.connectionId);
        return (api?.request?.responseMappings ?? []).map((m) =>
          m.target.kind === 'flow'
            ? m.target.name
            : `${m.target.kind === 'contactField' ? 'contact' : 'lead'}.${m.target.key}`
        );
      }
      return [];
    };
    // A step already on the canvas that the CURRENT trigger can't run (e.g. the trigger
    // was switched from a customer message to a lead event). Shown dimmed + dashed so the
    // mismatch is visible before Save, instead of surfacing only as a save-time error.
    const incompatibleReason = (n: Node): string | null => {
      if (n.type === 'trigger') return null;
      const av = NODE_AVAILABILITY.get(String(n.type));
      if (av && !isGroupEnabled(av, isConversationFlow)) {
        return isConversationFlow
          ? 'Not available for this trigger — it only runs on campaign & CRM workflows.'
          : 'Not available for this trigger — it only runs when the trigger is “When a Customer Messages”.';
      }
      if (LEAD_IDENTITY_ONLY_NODE_TYPES.has(n.type as NodeTypeName) && currentTriggerType && NON_LEAD_ASYNC_TRIGGER_TYPES.has(currentTriggerType)) {
        return 'Not available for this trigger — it writes to a Lead record, which this trigger doesn’t have.';
      }
      return null;
    };
    const withFlags: Node[] = nodes.map((n) => {
      const vars = varsFor(n);
      const isErr = errored.has(n.id);
      const inc = incompatibleReason(n);
      if (!isErr && !vars.length && !inc) return n;
      return {
        ...n,
        data: {
          ...n.data,
          ...(isErr ? { __errored: true } : {}),
          ...(vars.length ? { __vars: vars } : {}),
          ...(inc ? { __incompatible: inc } : {}),
        },
      };
    });
    return withFlags;
  }, [nodes, errorNodeIds, apiConnectionList, isConversationFlow, currentTriggerType]);

  // ── Load workflow ───────────────────────────────────────────────────────
  useEffect(() => {
    if (id === 'new') {
      const requestedTrigger = searchParams.get('trigger');
      const isMessageFlow = requestedTrigger === 'message.received';
      const requestedName = searchParams.get('name');
      if (requestedName) setWfName(requestedName.slice(0, 100));
      setNodes([
        {
          id: 'trigger-1',
          type: 'trigger',
          position: { x: 120, y: 180 },
          data: isMessageFlow
            ? {
                label: 'Trigger',
                triggerType: 'message.received',
                subtitle: 'Any Incoming WhatsApp Message',
              }
            : {
                label: 'Trigger',
                triggerType: 'lead.created',
                subtitle: 'Incoming Lead',
              },
        },
      ]);
      setEdges([]);
      setLoading(false);
      return;
    }

    adminApi
      .getWorkflowById(id!)
      .then((wf: any) => {
        setWfName(wf.name ?? '');
        setWfDesc(wf.description ?? '');
        setIsActive(wf.isActive ?? false);
        if (wf.settings) setWfSettings(wf.settings);

        const rfNodes: Node[] = (wf.nodes ?? []).map((n: any) => {
          // agentId lives on the workflow document (Workflow.agentId), not the
          // node — surfaced here on the trigger node's data so the existing
          // "edit trigger settings on the trigger node" UI pattern covers it
          // too, exactly like triggerType.
          //
          // triggerType falls back to the workflow-level trigger.type: a
          // workflow created via the API may carry it only there, and without
          // this the canvas would read the trigger as a CRM one and show the
          // wrong node palette for it.
          const data =
            n.type === 'trigger'
              ? {
                  ...n.data,
                  agentId: wf.agentId ?? undefined,
                  triggerType: n.data?.triggerType ?? wf.trigger?.type,
                }
              : { ...n.data };
          return {
            id: n.id,
            type: n.type,
            position: n.position ?? { x: 0, y: 0 },
            data: { ...data, label: modernLabel(n.label), subtitle: subtitle(n.type, data) },
          };
        });

        const rfEdges: Edge[] = (wf.edges ?? []).map((e: any) =>
          mkEdge({
            id: e.id,
            source: e.source,
            target: e.target,
            sourceHandle: e.sourceHandle ?? null,
            targetHandle: e.targetHandle ?? null,
            label: e.label ?? '',
          })
        );

        setNodes(rfNodes);
        setEdges(rfEdges);
      })
      .catch((err: any) => setError(err.message ?? 'Failed to load workflow'))
      .finally(() => setLoading(false));
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Keep edge + arrow colours in step with the theme. Returns the SAME array when
  // nothing needs changing, so this can't loop.
  useEffect(() => {
    const color = EDGE_COLOR[wfTheme];
    setEdges((eds) =>
      eds.every((e) => (e.style as any)?.stroke === color)
        ? eds
        : eds.map((e) => {
            const next: Edge = { ...e, style: { ...(e.style ?? {}), stroke: color } };
            // markerEnd is only re-coloured when it is the object form (an id-string marker has no colour).
            if (e.markerEnd && typeof e.markerEnd === 'object') next.markerEnd = { ...e.markerEnd, color };
            return next;
          })
    );
  }, [wfTheme, edges.length, setEdges]);

  // ── Fetch dropdown lists ─────────────────────────────────────────────────
  useEffect(() => {
    adminApi
      .getCampaigns(undefined, { suppressPermissionLock: true })
      .then(setCampaignList)
      .catch(() => {});
    // "Assign Agent" sets lead.assignedTo, which is a TEAM MEMBER (user) — not an
    // AI agent. List active team members so the dropdown populates and stores a
    // valid user id.
    adminApi
      .getTeamDirectory({ suppressPermissionLock: true })
      .then((list) =>
        setAgentList(
          (list || [])
            .filter((m) => m.isActive)
            .map((m) => ({ _id: m._id, name: `${m.firstName} ${m.lastName}`.trim() || m.email }))
        )
      )
      .catch(() => {});
    adminApi
      .getWorkflows(undefined, { suppressPermissionLock: true })
      .then((list) =>
        setWorkflowList(
          (list || []).map((w: any) => ({ _id: w._id, name: w.name, triggerType: w.trigger?.type }))
        )
      )
      .catch(() => {});
    // API Connections for the "Call API" node picker — best-effort (empty is fine;
    // the node also supports an inline URL).
    refreshApiConnections();
    // Flow-variable registry for the "Get User Input" picker — best-effort.
    refreshFlowVariables();
    // For the human_handoff node's own assignment-target override — same Role
    // roster the Human Handoff settings page and Roles admin page use.
    userManagementService
      .getRoles()
      .then((list) => setRoleList((list || []).map((r) => ({ _id: r._id, name: r.name }))))
      .catch(() => {});
    // Distinct from getTeamMembers() above — these are AI agents (bot
    // personas), the only thing a message.received workflow can be scoped to.
    adminApi
      .getAgents({ isActive: true }, { suppressPermissionLock: true })
      .then(({ data }) => setBotAgentList((data || []).map((a: any) => ({ _id: a._id, name: a.name }))))
      .catch(() => {});
    // Tenant-wide escalation config — surfaced read-only in the default-
    // fallback node and the info panel, so the builder shows the SAME keyword
    // list the webhook actually uses (Settings → Routing) instead of the author
    // maintaining a second, invisible one. Failure is non-fatal: the fallback
    // node just omits the keyword detail.
    adminApi
      .getRoutingSettings({ suppressPermissionLock: true })
      .then((r) => setRoutingSettings(r))
      .catch(() => {});
    // For the canned-reply node's template mode — only APPROVED templates
    // are ever sendable, so filter here rather than showing rejected/pending
    // ones an author could pick and then have fail at send time. AUTHENTICATION
    // templates have no free-text body to interpolate and aren't meant for bot
    // replies (Meta reserves them for OTP delivery), so they're excluded too.
    adminApi
      .getWhatsAppTemplates({ suppressPermissionLock: true })
      .then((list) =>
        setApprovedTemplates(
          (list || []).filter((t) => t.status === 'APPROVED' && t.category !== 'AUTHENTICATION')
        )
      )
      .catch(() => {});
    // Sendable-media library — for the send_media node's file picker.
    adminApi
      .getMediaAssets()
      .then((list) => setMediaAssets(list || []))
      .catch(() => {});
    // Saved interactive-message library — for the Send List / Send Button
    // Message node pickers and the button-reply trigger picker.
    refreshInteractiveMessages();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Connect ─────────────────────────────────────────────────────────────
  const onConnect = useCallback(
    (connection: Connection) => {
      const edge = mkEdge({
        id: `e-${connection.source}-${connection.sourceHandle ?? ''}-${connection.target}-${Date.now()}`,
        source: connection.source!,
        target: connection.target!,
        sourceHandle: connection.sourceHandle ?? null,
        targetHandle: connection.targetHandle ?? null,
        label:
          connection.sourceHandle === 'yes' ? 'Yes' : connection.sourceHandle === 'no' ? 'No' : '',
      });
      setEdges((eds) => addEdge(edge, eds));
    },
    [setEdges]
  );

  // ── Reconnect (re-wire) an existing edge ─────────────────────────────────
  // Drag an edge endpoint onto a different handle to re-route it; drop it on
  // empty canvas to remove it. Standard ReactFlow v11 reconnection pattern.
  const edgeReconnectOk = useRef(true);
  const onEdgeUpdateStart = useCallback(() => {
    edgeReconnectOk.current = false;
  }, []);
  const onEdgeUpdate = useCallback(
    (oldEdge: Edge, newConnection: Connection) => {
      edgeReconnectOk.current = true;
      setEdges((els) =>
        updateEdge(oldEdge, newConnection, els, { shouldReplaceId: false })
      );
    },
    [setEdges]
  );
  const onEdgeUpdateEnd = useCallback(
    (_: unknown, edge: Edge) => {
      if (!edgeReconnectOk.current) setEdges((eds) => eds.filter((e) => e.id !== edge.id));
      edgeReconnectOk.current = true;
    },
    [setEdges]
  );

  // ── Drag from palette ────────────────────────────────────────────────────
  const onDragOver = useCallback((e: RDragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  }, []);

  const onDrop = useCallback(
    (e: RDragEvent<HTMLDivElement>) => {
      e.preventDefault();
      const typeName = e.dataTransfer.getData('wf/nodeType') as NodeTypeName;
      if (!typeName || !NODE_META[typeName]) return;
      if (canvasLocked) return; // a locked canvas doesn't accept new nodes
      // Plan gate (defense-in-depth — the palette item is already undraggable).
      if (HTTP_AUTOMATION_NODE_TYPES.has(typeName) && httpAutomationLocked) return;

      const meta = NODE_META[typeName];
      const position = screenToFlowPosition({ x: e.clientX, y: e.clientY });
      const id = `${typeName}-${Date.now()}`;

      // A conversation-flow condition is seeded with the same values its config
      // panel would *display* as fallbacks, so an author who accepts them
      // without touching a dropdown still saves a node that runs. See
      // conversationConditionDefaults.
      // New AI Answer nodes default to a 24h pin (see the picker's comment).
      // Only NEW nodes — an existing node with no pinDurationHours keeps
      // "this message only", so behaviour never changes under a saved flow.
      const seed: Record<string, any> =
        typeName === 'condition' && isConversationFlow
          ? conversationConditionDefaults()
          : typeName === 'ai_answer'
            ? { pinDurationHours: 24 }
            : typeName === 'collect_input'
                ? { responseType: 'text', target: { kind: 'flow', name: '' }, maxRetries: 2 }
                : {};

      setNodes((nds) =>
        nds.concat({
          id,
          type: typeName,
          position,
          data: { ...seed, label: meta.label, subtitle: subtitle(typeName, seed) },
        })
      );
      setSelectedNodeId(id);
    },
    [screenToFlowPosition, setNodes, isConversationFlow, httpAutomationLocked, canvasLocked]
  );

  // ── Update node data from config panel ───────────────────────────────────
  const updateNodeData = useCallback(
    (nodeId: string, data: Record<string, any>) => {
      setNodes((nds) => nds.map((n) => (n.id === nodeId ? { ...n, data } : n)));
      // Editing a node clears its error outline — the author is fixing it.
      setErrorNodeIds((ids) => (ids.includes(nodeId) ? ids.filter((x) => x !== nodeId) : ids));
    },
    [setNodes]
  );

  // ── Delete node ──────────────────────────────────────────────────────────
  const deleteNode = useCallback(
    (nodeId: string) => {
      // The trigger node and the synthetic fallback node are never deletable —
      // a workflow must have exactly one trigger, and the fallback isn't a real
      // node. Guard here so every delete path (button, key, context menu) obeys it.
      if (nodeId === DEFAULT_FALLBACK_NODE_ID) return;
      const target = nodes.find((n) => n.id === nodeId);
      if (target?.type === 'trigger') return;
      setNodes((nds) => nds.filter((n) => n.id !== nodeId));
      setEdges((eds) => eds.filter((e) => e.source !== nodeId && e.target !== nodeId));
      setSelectedNodeId((cur) => (cur === nodeId ? null : cur));
      setErrorNodeIds((ids) => ids.filter((x) => x !== nodeId));
    },
    [nodes, setNodes, setEdges]
  );

  // ── Delete a connection ──────────────────────────────────────────────────
  const deleteEdge = useCallback(
    (edgeId: string) => setEdges((eds) => eds.filter((e) => e.id !== edgeId)),
    [setEdges]
  );

  // ── Right-click context menu (delete node / connection) ──────────────────
  const openCtxMenu = useCallback(
    (e: React.MouseEvent, kind: 'node' | 'edge', id: string, deletable: boolean) => {
      e.preventDefault();
      const rect = rfWrapper.current?.getBoundingClientRect();
      setCtxMenu({
        x: e.clientX - (rect?.left ?? 0),
        y: e.clientY - (rect?.top ?? 0),
        kind,
        id,
        deletable,
      });
    },
    []
  );

  const onNodeContextMenu = useCallback(
    (e: React.MouseEvent, node: Node) => {
      // Trigger and the synthetic fallback can't be deleted — show the menu
      // but with the delete action disabled, so a right-click still feels alive.
      const deletable = node.id !== DEFAULT_FALLBACK_NODE_ID && node.type !== 'trigger';
      openCtxMenu(e, 'node', node.id, deletable);
    },
    [openCtxMenu]
  );

  const onEdgeContextMenu = useCallback(
    (e: React.MouseEvent, edge: Edge) => openCtxMenu(e, 'edge', edge.id, true),
    [openCtxMenu]
  );

  const closeCtxMenu = useCallback(() => setCtxMenu(null), []);

  const runCtxDelete = useCallback(() => {
    if (!ctxMenu || !ctxMenu.deletable) return;
    if (ctxMenu.kind === 'node') deleteNode(ctxMenu.id);
    else deleteEdge(ctxMenu.id);
    setCtxMenu(null);
  }, [ctxMenu, deleteNode, deleteEdge]);

  // ── Test a message (server-side dry run, never executes the flow) ────────
  const buildBackendGraph = useCallback(
    () => ({
      nodes: nodes.map((n) => {
        const { label, subtitle: _s, ...rest } = n.data as any;
        return {
          id: n.id,
          type: n.type!,
          label: label || n.type,
          position: n.position,
          data: rest,
        };
      }),
      edges: edges.map((e) => ({
        id: e.id,
        source: e.source,
        target: e.target,
        sourceHandle: e.sourceHandle ?? null,
        targetHandle: e.targetHandle ?? null,
        label: (e.label as string) ?? '',
      })),
    }),
    [nodes, edges]
  );

  // The signature covers the NAME and the saved shape of the graph (positions, data,
  // edges) — not selection or display-only fields — so it flips exactly when a save
  // would change something.
  const graphSig = useMemo(
    () => JSON.stringify({ n: wfName, g: buildBackendGraph() }),
    [wfName, buildBackendGraph]
  );
  sigRef.current = graphSig;
  // Baseline: once the workflow has loaded (a short beat later, so post-load effects
  // that tidy node data don't count as edits).
  useEffect(() => {
    if (loading || id === 'new') return;
    const t = setTimeout(() => setSavedSig((prev) => prev ?? sigRef.current), 400);
    return () => clearTimeout(t);
  }, [loading, id]);
  const saveStatus =
    id === 'new'
      ? 'Not saved yet'
      : savedSig === null
        ? ''
        : saved || graphSig === savedSig
          ? 'All changes saved'
          : 'Unsaved changes';

  /** What the customer would see for one outcome entry, as a chat bubble. */
  const outcomeText = (o: any): string => {
    switch (o?.type) {
      case 'instant_reply':
        return o.replyText;
      case 'ask_question':
        return o.prompt;
      case 'template_reply':
        return `[Approved template: ${o.templateName}]`;
      case 'human_handoff':
        return '[Chat handed to your team]';
      case 'send_media':
        return '[Sends a file]';
      case 'send_list_message':
        return '[Sends a list menu]';
      case 'send_button_message':
        return '[Sends a message with reply buttons]';
      default:
        return '';
    }
  };

  const resetTest = useCallback(() => {
    setTestTranscript([]);
    setTestSession(null);
    setTestVars({});
    setTestResult(null);
    setTestError(null);
    setTestMessage('');
  }, []);

  // Keep the newest bubble in view.
  useEffect(() => {
    const el = transcriptRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [testTranscript]);

  const handleTest = useCallback(async () => {
    const text = testMessage.trim();
    if (!text) return;
    setTestRunning(true);
    setTestError(null);
    setTestTranscript((t) => [...t, { role: 'customer', text }]);
    setTestMessage('');
    try {
      // Send the current canvas as `draft` so unsaved edits are what gets
      // tested — otherwise an author edits, tests, and sees stale behaviour.
      const result = await adminApi.simulateWorkflowMessage({
        message: text,
        ...(!testSession && testButtonId.trim() ? { buttonReplyId: testButtonId.trim() } : {}),
        ...(id && id !== 'new' ? { workflowId: id } : {}),
        draft: buildBackendGraph(),
        ...(testSession ? { session: testSession } : {}),
        liveApi: testLiveApi,
      });
      setTestResult(result);
      setTestSession(result.session ?? null);
      setTestVars(result.variables ?? {});

      const outcomes: any[] = Array.isArray(result.trace?.outcomes) ? result.trace.outcomes : [];
      const bubbles: Array<{ role: 'bot' | 'system'; text: string }> = outcomes
        .map((o) => ({ role: 'bot' as const, text: outcomeText(o) }))
        .filter((b) => b.text);
      if (!bubbles.length) {
        bubbles.push({
          role: 'system',
          text: result.escalation?.wouldEscalate
            ? 'This asks for a human, so it is handed to your team before any flow runs.'
            : result.match
              ? 'The flow ends without a reply — the AI would answer normally.'
              : 'No keyword matched — the AI would answer normally.',
        });
      }
      setTestTranscript((t) => [...t, ...bubbles]);
    } catch (err: any) {
      setTestError(err?.response?.data?.error ?? err.message ?? 'Test failed');
    } finally {
      setTestRunning(false);
    }
  }, [testMessage, testButtonId, testSession, testLiveApi, id, buildBackendGraph]);

  // ── Starter templates ──────────────────────────────────────────────────────
  const openTemplatePicker = useCallback(() => {
    setTemplateOpen(true);
    if (templates.length || templatesLoading) return; // fetch once, then cache
    setTemplatesLoading(true);
    setTemplatesError(null);
    adminApi
      .getWorkflowTemplates()
      .then((list) => setTemplates(Array.isArray(list) ? list : []))
      .catch((err: any) => setTemplatesError(err?.message ?? 'Failed to load templates'))
      .finally(() => setTemplatesLoading(false));
  }, [templates.length, templatesLoading]);

  // Prefill the canvas from a template. Mirrors the workflow-load mapping above
  // (label → data, computed subtitle, mkEdge) so an applied template behaves
  // exactly like a hand-built one. Replaces whatever is on the canvas; the
  // trigger node it carries flips the engine (palette + save trigger.type)
  // automatically through isConversationFlow.
  const applyTemplate = useCallback(
    (t: any) => {
      const rfNodes: Node[] = (t.nodes ?? []).map((n: any) => {
        const data =
          n.type === 'trigger'
            ? { ...n.data, triggerType: n.data?.triggerType ?? t.trigger?.type }
            : { ...n.data };
        return {
          id: n.id,
          type: n.type,
          position: n.position ?? { x: 0, y: 0 },
          data: { ...data, label: modernLabel(n.label), subtitle: subtitle(n.type, data) },
        };
      });
      const rfEdges: Edge[] = (t.edges ?? []).map((e: any) =>
        mkEdge({
          id: e.id,
          source: e.source,
          target: e.target,
          sourceHandle: e.sourceHandle ?? null,
          targetHandle: e.targetHandle ?? null,
          label: e.label ?? '',
        } as Edge)
      );
      setNodes(rfNodes);
      setEdges(rfEdges);
      setSelectedNodeId(null);
      setErrorNodeIds([]);
      setSaveWarnings([]);
      // Prefill name/description only when blank — never clobber the author's own.
      if (!wfName.trim()) setWfName(t.name ?? '');
      if (!wfDesc.trim()) setWfDesc(t.description ?? '');
      setTemplateOpen(false);
    },
    [wfName, wfDesc]
  );

  // ── Save ─────────────────────────────────────────────────────────────────
  const handleSave = useCallback(async () => {
    if (!wfName.trim()) {
      setError('Workflow name is required');
      return;
    }
    setSaving(true);
    setError(null);
    setErrorNodeIds([]);
    try {
      const triggerNode = nodes.find((n) => n.type === 'trigger');
      const triggerType = (triggerNode?.data as any)?.['triggerType'] ?? 'manual';
      // Optional now — a message.received flow is tenant-scoped; the agent is a
      // hint used only when a tenant wants to pin one persona.
      const triggerAgentId = (triggerNode?.data as any)?.['agentId'] || undefined;

      const backendNodes = nodes.map((n) => {
        const { label, subtitle: _s, ...rest } = n.data as any;
        // Repair pass for condition nodes created before they were seeded at
        // drop time: persist the values the panel has been *showing* all along
        // rather than the empty object behind them, so the node stops silently
        // failing open at run time. Only ever fills what's missing — an
        // author's own hours are never overwritten.
        let data = rest;
        if (n.type === 'condition' && isConversationFlow && rest?.['mode'] !== 'match' && rest?.['field'] !== 'routing.state') {
          if (isUnconfiguredBusinessHours(rest)) {
            data = { ...conversationConditionDefaults(), ...pruneEmpty(rest) };
          }
          // Canonicalise the zone so a node saved as 'Asia/Calcutta' keeps
          // matching a dropdown row. Purely cosmetic at run time — Intl
          // resolves both ids identically.
          if (typeof data['timezone'] === 'string') {
            data = { ...data, timezone: canonicalTimezone(data['timezone']) };
          }
        }
        return {
          id: n.id,
          type: n.type!,
          label: label || n.type,
          position: n.position,
          data,
        };
      });
      const backendEdges = edges.map((e) => ({
        id: e.id,
        source: e.source,
        target: e.target,
        sourceHandle: e.sourceHandle ?? null,
        targetHandle: e.targetHandle ?? null,
        label: (e.label as string) ?? '',
      }));

      const payload = {
        name: wfName,
        description: wfDesc,
        trigger: { type: triggerType, config: {} },
        agentId: triggerType === 'message.received' ? triggerAgentId : undefined,
        nodes: backendNodes,
        edges: backendEdges,
        settings: wfSettings,
      };

      let result: any;
      if (id === 'new') {
        result = await adminApi.createWorkflow(payload);
        navigate(`/admin/automation/workflows/${result._id}`, { replace: true });
      } else {
        result = await adminApi.updateWorkflow(id!, payload);
      }

      // Advisory only — the save already succeeded. Surfaced so an author
      // learns about an unreachable node or a missing route to a human
      // without being blocked from saving a work-in-progress.
      setSaveWarnings(Array.isArray(result?.warnings) ? result.warnings : []);
      setSaved(true);
      setSavedSig(sigRef.current);
      setTimeout(() => setSaved(false), 2500);
    } catch (err: any) {
      if (!showPlanLockIfTagged(err)) {
        // The backend returns { error, errorNodeId?, errorNodeIds? } on a
        // validation 400 — prefer that specific message over axios's generic
        // "Request failed with status code 400", and focus the offending node.
        const body = err?.response?.data;
        setError(toErrorMessage(body?.error, toErrorMessage(err.message, 'Save failed')));
        if (body?.errorNodeId || body?.errorNodeIds) {
          focusNode(body.errorNodeId, body.errorNodeIds ?? []);
        }
      }
    } finally {
      setSaving(false);
    }
  }, [wfName, wfDesc, wfSettings, nodes, edges, id, navigate, focusNode, isConversationFlow]);

  // ── Toggle active ─────────────────────────────────────────────────────────
  const toggleActive = useCallback(async () => {
    if (id === 'new') return;
    try {
      if (isActive) {
        await adminApi.deactivateWorkflow(id!);
      } else {
        await adminApi.activateWorkflow(id!);
      }
      setIsActive((v) => !v);
    } catch (err: any) {
      if (!showPlanLockIfTagged(err)) {
        setError(err.message ?? 'Toggle failed');
      }
    }
  }, [id, isActive]);

  // ── Fullscreen ────────────────────────────────────────────────────────────
  // Two layers: a CSS overlay (fixed, inset:0, `isFullscreen`) that covers the app
  // shell, plus the browser's real full screen on top of it. The overlay alone is what
  // the page always used; it sits above the app shell but below MUI popovers so every
  // dropdown keeps working.
  // Toggles the browser's REAL full screen on the whole document. Full-screening the
  // document (not the canvas element) is what keeps MUI popovers/tooltips — which
  // portal to <body> — visible, so the native API is safe here. If the browser blocks
  // or lacks it, fall back to flipping the overlay so the button never does nothing.
  const toggleFullscreen = useCallback(async () => {
    try {
      if (document.fullscreenElement) {
        await document.exitFullscreen();
      } else if (document.documentElement.requestFullscreen) {
        await document.documentElement.requestFullscreen();
        setIsFullscreen(true); // keep the overlay layout so the canvas fills the screen
      } else {
        setIsFullscreen((v) => !v);
      }
    } catch {
      setIsFullscreen((v) => !v);
    }
  }, []);

  // "Fit view": React Flow's own fitView silently does nothing until EVERY node has
  // been measured, which is the likeliest reason the stock button looked dead. Try it,
  // and fall back to fitting explicit bounds computed from node positions.
  const fitAll = useCallback(() => {
    const opts = { padding: 0.2, duration: 300 };
    if (fitView(opts)) return;
    const all = getNodes();
    if (!all.length) {
      setViewport({ x: 0, y: 0, zoom: 1 }, { duration: 300 });
      return;
    }
    const rect = boundsOfNodes(all);
    if (rect) fitBounds(rect, opts);
  }, [fitView, fitBounds, getNodes, setViewport]);

  useEffect(() => {
    if (!isFullscreen) return;
    const onKey = (e: KeyboardEvent) => {
      // Escape closes the context menu first (handled in its own effect); only
      // exit full screen when no menu is open, so one Escape does one thing.
      if (e.key === 'Escape' && !ctxMenu) setIsFullscreen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isFullscreen, ctxMenu]);

  // Escape closes the right-click menu.
  useEffect(() => {
    if (!ctxMenu) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setCtxMenu(null);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [ctxMenu]);

  // ─── Render ──────────────────────────────────────────────────────────────

  if (loading) {
    // Full-screen loader that matches where the builder is about to open —
    // same fixed overlay + theme as .wf-page--fullscreen, so the transition
    // from loader to canvas is seamless rather than a flash of a different page.
    return (
      <div className={`wf-page wf-page--fullscreen wf-loader${wfTheme === 'light' ? ' wf-page--light' : ''}`}>
        <CircularProgress size={30} sx={{ color: 'var(--wf-accent)' }} />
        <div className="wf-loader__label">Loading workflow…</div>
      </div>
    );
  }

  return (
    <div
      className={`wf-page${isFullscreen ? ' wf-page--fullscreen' : ''}${wfTheme === 'light' ? ' wf-page--light' : ''}`}
      ref={pageRef}
    >
      {/* ── Top bar ──────────────────────────────────────────────────────── */}
      <div className="wf-topbar">
        <Tooltip title="Back to workflows list">
          <IconButton
            className="wf-topbar__back"
            size="small"
            onClick={() => navigate('/admin/automation/workflows')}
          >
            <ArrowBack fontSize="small" />
          </IconButton>
        </Tooltip>

        <span className="wf-topbar__tile">
          <AccountTree fontSize="small" />
        </span>

        <div className="wf-topbar__title">
          <div className="wf-topbar__titlerow">
            <div className="wf-topbar__name">
              <input
                value={wfName}
                onChange={(e) => setWfName(e.target.value)}
                placeholder="Workflow name…"
                spellCheck={false}
                aria-label="Workflow name"
              />
            </div>
            <span className={`wf-pill${id !== 'new' && isActive ? ' is-active' : ''}`}>
              {id !== 'new' && isActive ? 'Active' : 'Draft'}
            </span>
          </div>
          <div className="wf-topbar__sub">
            <span>{isConversationFlow ? 'Incoming' : 'Outgoing'}</span>
            {saveStatus && (
              <>
                <span className="wf-topbar__sep">·</span>
                <span className={`wf-topbar__status-text${saveStatus === 'Unsaved changes' ? ' is-dirty' : ''}`}>
                  {saveStatus === 'All changes saved' && <CheckCircle sx={{ fontSize: 13 }} />}
                  {saveStatus}
                </span>
              </>
            )}
          </div>
        </div>

        <div className="wf-topbar__spacer" />

        {id !== 'new' && (
          <FormControlLabel
            control={
              <Switch
                checked={isActive}
                onChange={toggleActive}
                size="small"
                sx={{
                  '& .MuiSwitch-switchBase.Mui-checked': { color: 'var(--wf-accent)' },
                  '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                    backgroundColor: 'var(--wf-accent)',
                  },
                }}
              />
            }
            label={
              <Typography sx={{ fontSize: 12.5, color: isActive ? 'var(--wf-accent)' : 'var(--wf-text-dim)' }}>
                {isActive ? 'Active' : 'Inactive'}
              </Typography>
            }
            sx={{ mr: 0.5 }}
          />
        )}

        <div className="wf-topbar__divider" />

        <Tooltip title="Prefill the canvas from a ready-made example workflow">
          <Button
            size="small"
            className="wf-topbar__btn"
            startIcon={<LibraryBooks fontSize="small" />}
            onClick={openTemplatePicker}
          >
            Templates
          </Button>
        </Tooltip>

        <Tooltip title={wfTheme === 'light' ? 'Switch to dark canvas' : 'Switch to light canvas'}>
          <IconButton size="small" className="wf-topbar__icon" onClick={toggleWfTheme} aria-label="Toggle light/dark canvas">
            {wfTheme === 'light' ? <DarkMode fontSize="small" /> : <LightMode fontSize="small" />}
          </IconButton>
        </Tooltip>

        <Tooltip title={browserFs ? 'Exit full screen' : 'Full screen'}>
          <IconButton className="wf-topbar__icon" size="small" onClick={toggleFullscreen}>
            {browserFs ? <FullscreenExit fontSize="small" /> : <Fullscreen fontSize="small" />}
          </IconButton>
        </Tooltip>

        {isConversationFlow && (
          <Tooltip title="Try a customer message against this flow">
            <Button
              size="small"
              className={`wf-topbar__test${testOpen ? ' is-open' : ''}`}
              startIcon={<PlayArrow />}
              onClick={() => setTestOpen((v) => !v)}
              aria-pressed={testOpen}
            >
              Test flow
            </Button>
          </Tooltip>
        )}

        <Button
          variant="contained"
          size="small"
          className="wf-topbar__save"
          startIcon={saving ? <CircularProgress size={14} color="inherit" /> : <Check />}
          onClick={handleSave}
          disabled={saving}
        >
          {saving ? 'Saving…' : 'Save workflow'}
        </Button>
      </div>

      {/* Errors / warnings live UNDER the top bar so they never squeeze the title or the Save button. */}
      {(error || saveWarnings.length > 0) && (
        <div className={`wf-banner ${error ? 'wf-banner--error' : 'wf-banner--warn'}`} role={error ? 'alert' : 'status'}>
          <span className="wf-banner__icon">{error ? <ErrorOutlineIcon fontSize="small" /> : <WarningAmberIcon fontSize="small" />}</span>
          <div className="wf-banner__text">
            {error ? (
              error
            ) : (
              <>
                {saveWarnings.slice(0, 3).map((w, i) => (
                  <div key={i}>{w}</div>
                ))}
                {saveWarnings.length > 3 && <div>+{saveWarnings.length - 3} more</div>}
              </>
            )}
          </div>
          <IconButton size="small" className="wf-banner__close" aria-label="Dismiss" onClick={() => (error ? setError(null) : setSaveWarnings([]))}>
            <Close fontSize="small" />
          </IconButton>
        </div>
      )}

      {/* ── Body ─────────────────────────────────────────────────────────── */}
      <div className="wf-body">
        {/* ── Palette ────────────────────────────────────────────────────── */}
        <div className={`wf-palette${paletteCollapsed ? ' wf-palette--collapsed' : ''}`}>
          <div className="wf-palette__header">
            <span className="wf-palette__header-label">Build your flow</span>
            <Tooltip title={paletteCollapsed ? 'Expand nodes panel' : 'Collapse nodes panel'} placement="right">
              <IconButton
                size="small"
                className="wf-palette__toggle"
                onClick={togglePalette}
                aria-label={paletteCollapsed ? 'Expand nodes panel' : 'Collapse nodes panel'}
              >
                {paletteCollapsed ? <TbLayoutSidebarLeftExpand size={20} /> : <TbLayoutSidebarLeftCollapse size={20} />}
              </IconButton>
            </Tooltip>
          </div>
          {!paletteCollapsed && (
            <div className="wf-palette__search">
              <SearchIcon className="wf-palette__search-icon" />
              <input
                ref={paletteSearchRef}
                value={paletteQuery}
                onChange={(e) => setPaletteQuery(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Escape') setPaletteQuery('');
                }}
                placeholder="Search nodes…"
                aria-label="Search nodes"
                spellCheck={false}
              />
              <kbd className="wf-palette__kbd">
                {typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform) ? '⌘K' : 'Ctrl K'}
              </kbd>
            </div>
          )}
          <div className="wf-palette__list">
            {!paletteCollapsed && paletteQuery && paletteGroupsView.length === 0 && (
              <div className="wf-palette__empty">No nodes match “{paletteQuery}”</div>
            )}
            {paletteGroupsView.map((group) => {
              const groupEnabled = isGroupEnabled(group.availability, isConversationFlow);
              const groupReason = isConversationFlow
                ? 'Only available on campaign & CRM workflows — this one starts from a customer message.'
                : 'Only available when the trigger is "When a Customer Messages".';
              return (
                <div className="wf-palette__group" key={group.title}>
                  {!paletteCollapsed && (
                    <div className="wf-palette__group-title">
                      <span>{group.title.toUpperCase()}</span>
                      {!groupEnabled && <span className="wf-palette__group-tag">N/A</span>}
                    </div>
                  )}
                  {group.items.map(({ type, Icon }) => {
                    const meta = NODE_META[type];
                    const leadOnlyBlocked =
                      groupEnabled &&
                      LEAD_IDENTITY_ONLY_NODE_TYPES.has(type) &&
                      !!currentTriggerType &&
                      NON_LEAD_ASYNC_TRIGGER_TYPES.has(currentTriggerType);
                    const planLocked = HTTP_AUTOMATION_NODE_TYPES.has(type) && httpAutomationLocked;
                    const enabled = groupEnabled && !leadOnlyBlocked && !planLocked;
                    const reason = planLocked
                      ? "HTTP API Automation isn’t included in your current plan — upgrade to use this."
                      : leadOnlyBlocked
                        ? `"${meta.label}" writes to the Lead record — this flow's trigger doesn't have one (it starts from an Appointment).`
                        : groupReason;
                    const item = (
                      <div
                        key={type}
                        className={`wf-palette__item wf-cat--${categoryOf(type).cat}${enabled ? '' : ' is-disabled'}${paletteCollapsed ? ' wf-palette__item--rail' : ''}`}
                        draggable={enabled}
                        onDragStart={(e: RDragEvent<HTMLDivElement>) => {
                          if (!enabled) {
                            e.preventDefault();
                            return;
                          }
                          e.dataTransfer.setData('wf/nodeType', type);
                          e.dataTransfer.effectAllowed = 'move';
                        }}
                      >
                        <div className="wf-palette__dot">
                          <Icon style={{ fontSize: 18 }} />
                        </div>
                        {!paletteCollapsed && (
                          <div className="wf-palette__info">
                            <span className="wf-palette__label">{meta.label}</span>
                            <span className="wf-palette__desc">{meta.desc}</span>
                          </div>
                        )}
                      </div>
                    );
                    // Collapsed rail: no text, so EVERY item gets a tooltip (name + what it does).
                    return enabled && !paletteCollapsed ? (
                      item
                    ) : (
                      <Tooltip
                        key={type}
                        title={enabled ? `${meta.label} — ${meta.desc}` : reason}
                        placement="right"
                        arrow
                      >
                        <span style={{ display: 'block' }}>{item}</span>
                      </Tooltip>
                    );
                  })}
                </div>
              );
            })}
          </div>
        </div>

        {/* ── Canvas ─────────────────────────────────────────────────────── */}
        <div className="wf-canvas" ref={rfWrapper} onDrop={onDrop} onDragOver={onDragOver}>
          <div className="wf-canvas__crumb">
            <span className="wf-canvas__count">
              {nodes.length} {nodes.length === 1 ? 'step' : 'steps'}
            </span>
          </div>
          {testOpen && isConversationFlow && (
            <div className="wf-test">
              <div className="wf-test__head">
                <span>Test a customer conversation</span>
                <Stack direction="row" alignItems="center" spacing={0.5}>
                  <Tooltip title="Start over">
                    <span>
                      <IconButton
                        size="small"
                        onClick={resetTest}
                        disabled={!testTranscript.length && !testSession}
                        sx={{ color: 'var(--wf-text-dim, #939eb2)' }}
                      >
                        <Refresh fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                  <IconButton size="small" onClick={() => setTestOpen(false)} sx={{ color: 'var(--wf-text-dim, #939eb2)' }}>
                    <Close fontSize="small" />
                  </IconButton>
                </Stack>
              </div>
              <div className="wf-test__body">
                {/* ── Conversation so far ───────────────────────────────── */}
                <Box
                  ref={transcriptRef}
                  sx={{ maxHeight: 240, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 0.75, mb: 1.25 }}
                >
                  {testTranscript.map((m, i) => (
                    <Box
                      key={i}
                      sx={{
                        alignSelf: m.role === 'customer' ? 'flex-end' : m.role === 'bot' ? 'flex-start' : 'center',
                        maxWidth: m.role === 'system' ? '100%' : '85%',
                        px: 1.25,
                        py: 0.75,
                        borderRadius: '10px',
                        fontSize: 12.5,
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-word',
                        background: m.role === 'customer' ? 'var(--wf-accent-dim, #27407a)' : m.role === 'bot' ? 'var(--wf-surface-2, #161b25)' : 'transparent',
                        border: m.role === 'bot' ? '1px solid var(--wf-border, #222834)' : 'none',
                        color: m.role === 'system' ? 'var(--wf-text-dim, #939eb2)' : 'var(--wf-text, #e8ecf4)',
                        fontStyle: m.role === 'system' ? 'italic' : 'normal',
                      }}
                    >
                      {m.text}
                    </Box>
                  ))}
                </Box>

                {/* ── Message / answer box ──────────────────────────────── */}
                <Stack direction="row" spacing={1}>
                  <TextField
                    size="small"
                    fullWidth
                    autoFocus
                    placeholder={testSession ? 'Type the customer’s answer…' : 'e.g. I need a refund'}
                    value={testMessage}
                    onChange={(e) => setTestMessage(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') handleTest();
                    }}
                    inputProps={{ maxLength: 4000 }}
                  />
                  <Button
                    variant="contained"
                    size="small"
                    onClick={handleTest}
                    disabled={testRunning || !testMessage.trim()}
                    sx={{ textTransform: 'none', minWidth: 72 }}
                  >
                    {testRunning ? <CircularProgress size={15} sx={{ color: '#fff' }} /> : testSession ? 'Send' : 'Run'}
                  </Button>
                </Stack>

                {!testSession && (
                  <TextField
                    size="small"
                    fullWidth
                    placeholder="Optional: simulate a button/list tap instead — e.g. confirm_yes"
                    value={testButtonId}
                    onChange={(e) => setTestButtonId(e.target.value)}
                    sx={{ mt: 1 }}
                    InputProps={{ sx: { fontSize: 13 } }}
                  />
                )}

                <FormControlLabel
                  sx={{ mt: 0.5, mr: 0, '& .MuiFormControlLabel-label': { fontSize: 12, color: 'var(--wf-text, #c5cdd9)' } }}
                  control={<Switch size="small" checked={testLiveApi} onChange={(e) => setTestLiveApi(e.target.checked)} />}
                  label={
                    <Tooltip
                      arrow
                      title={
                        testLiveApi
                          ? 'Makes real requests — a POST/PUT/DELETE API can change data on the other system. Nothing is sent to a customer.'
                          : 'API steps are skipped. Nothing is sent to a customer.'
                      }
                    >
                      <span>Call real APIs</span>
                    </Tooltip>
                  }
                />

                {testError && (
                  <Alert severity="error" sx={{ mt: 1, fontSize: 12 }}>
                    {testError}
                  </Alert>
                )}

                {testResult && (
                  <Box sx={{ mt: 1.25 }}>
                    <Alert
                      severity={
                        testResult.escalation?.wouldEscalate ? 'warning' : testResult.match ? 'success' : 'info'
                      }
                      sx={{ fontSize: 12.5 }}
                    >
                      {testResult.summary}
                    </Alert>

                    {Object.keys(testVars).length > 0 && (
                      <Box sx={{ mt: 1.25 }}>
                        <Typography sx={{ fontSize: 10.5, color: 'var(--wf-text-faint, #5a6478)', mb: 0.5 }}>VARIABLES SO FAR</Typography>
                        <Stack direction="row" flexWrap="wrap" gap={0.5}>
                          {Object.entries(testVars).map(([k, v]) => (
                            <Chip
                              key={k}
                              size="small"
                              label={`${k} = ${typeof v === 'string' ? v : JSON.stringify(v)}`.slice(0, 48)}
                              sx={{ fontSize: 11, color: 'var(--wf-text, #e8ecf4)', background: 'var(--wf-surface-2, #161b25)', border: '1px solid var(--wf-border, #222834)' }}
                            />
                          ))}
                        </Stack>
                      </Box>
                    )}

                    {Array.isArray(testResult.trace?.steps) && testResult.trace.steps.length > 0 && (
                      <Box sx={{ mt: 1.25 }}>
                        <Typography sx={{ fontSize: 10.5, color: 'var(--wf-text-faint, #5a6478)', mb: 0.5 }}>WHAT HAPPENED THIS TURN</Typography>
                        {testResult.trace.steps.map((s: any, i: number) => (
                          <Box key={`${s.nodeId}-${i}`} sx={{ display: 'flex', gap: 1, mb: 0.75 }}>
                            <Box
                              sx={{
                                width: 18,
                                height: 18,
                                borderRadius: '50%',
                                background: 'var(--wf-accent, #4c8dff)',
                                color: 'var(--wf-on-accent, #0a0d13)',
                                fontSize: 10,
                                fontWeight: 700,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                flexShrink: 0,
                                mt: '1px',
                              }}
                            >
                              {i + 1}
                            </Box>
                            <Box sx={{ minWidth: 0 }}>
                              <Typography sx={{ fontSize: 12, fontWeight: 600, color: 'var(--wf-text, #e8ecf4)' }}>{s.label}</Typography>
                              <Typography sx={{ fontSize: 11.5, color: 'var(--wf-text-dim, #939eb2)' }}>{s.detail}</Typography>
                            </Box>
                          </Box>
                        ))}
                        {testResult.trace?.note && (
                          <Typography sx={{ fontSize: 11.5, color: '#d29922', mt: 0.5 }}>{testResult.trace.note}</Typography>
                        )}
                      </Box>
                    )}
                  </Box>
                )}
              </div>
            </div>
          )}

          <ReactFlow
            nodes={displayNodes}
            edges={edges}
            onNodesChange={onNodesChange}
            onEdgesChange={onEdgesChange}
            onConnect={onConnect}
            onNodeClick={(_, node) => {
              // The synthetic fallback node has no config; ignore clicks on it.
              if (node.id !== DEFAULT_FALLBACK_NODE_ID) setSelectedNodeId(node.id);
            }}
            onPaneClick={() => {
              setSelectedNodeId(null);
              closeCtxMenu();
            }}
            {...(canvasLocked
              ? {}
              : { onNodeContextMenu, onEdgeContextMenu, onEdgeUpdate, onEdgeUpdateStart, onEdgeUpdateEnd })}
            edgesUpdatable={!canvasLocked}
            edgeUpdaterRadius={14}
            onMoveStart={closeCtxMenu}
            nodeTypes={nodeTypes}
            fitView
            fitViewOptions={{ padding: 0.2 }}
            nodesDraggable={!canvasLocked}
            nodesConnectable={!canvasLocked}
            elementsSelectable={!canvasLocked}
            deleteKeyCode={canvasLocked ? null : 'Delete'}
            multiSelectionKeyCode="Shift"
            snapToGrid
            snapGrid={[16, 16]}
            defaultEdgeOptions={EDGE_DEFAULTS}
          >
            <Background variant={BackgroundVariant.Dots} gap={22} size={wfTheme === 'light' ? 1.7 : 1.4} color={CANVAS_COLORS[wfTheme].dots} />
            <Panel position="bottom-left" className="wf-zoom">
              <Tooltip title="Zoom out" placement="top">
                <button type="button" className="wf-zoom__btn" aria-label="Zoom out" onClick={() => zoomOut({ duration: 200 })}>
                  <ZoomOut fontSize="small" />
                </button>
              </Tooltip>
              <ZoomReadout />
              <Tooltip title="Zoom in" placement="top">
                <button type="button" className="wf-zoom__btn" aria-label="Zoom in" onClick={() => zoomIn({ duration: 200 })}>
                  <ZoomIn fontSize="small" />
                </button>
              </Tooltip>
              <span className="wf-zoom__sep" />
              <Tooltip title="Fit all nodes in view" placement="top">
                <button type="button" className="wf-zoom__btn" aria-label="Fit all nodes in view" onClick={fitAll}>
                  <FitScreen fontSize="small" />
                </button>
              </Tooltip>
              <Tooltip
                title={canvasLocked ? 'Canvas locked — click to unlock' : 'Lock canvas (stops moving, connecting and deleting nodes)'}
                placement="top"
              >
                <button
                  type="button"
                  className={`wf-zoom__btn${canvasLocked ? ' is-active' : ''}`}
                  aria-label="Lock or unlock the canvas"
                  aria-pressed={canvasLocked}
                  onClick={() => setCanvasLocked((l) => !l)}
                >
                  {canvasLocked ? <LockIcon fontSize="small" /> : <LockOpen fontSize="small" />}
                </button>
              </Tooltip>
            </Panel>
            <MiniMap
              nodeStrokeColor={() => CANVAS_COLORS[wfTheme].node}
              nodeColor={() => `${CANVAS_COLORS[wfTheme].node}33`}
              maskColor={CANVAS_COLORS[wfTheme].mask}
            />
          </ReactFlow>

          {/* Right-click context menu — delete a node or a connection. */}
          {ctxMenu && (
            <>
              {/* Invisible full-canvas backdrop closes the menu on any outside click. */}
              <div className="wf-ctx-backdrop" onClick={closeCtxMenu} onContextMenu={(e) => { e.preventDefault(); closeCtxMenu(); }} />
              <div className="wf-ctx-menu" style={{ left: ctxMenu.x, top: ctxMenu.y }}>
                <div className="wf-ctx-menu__title">
                  {ctxMenu.kind === 'node' ? 'Node' : 'Connection'}
                </div>
                <button
                  className="wf-ctx-menu__item danger"
                  disabled={!ctxMenu.deletable}
                  onClick={runCtxDelete}
                >
                  <Delete style={{ fontSize: 15 }} />
                  {ctxMenu.kind === 'node' ? 'Delete node' : 'Delete connection'}
                </button>
                {ctxMenu.kind === 'node' && !ctxMenu.deletable && (
                  <div className="wf-ctx-menu__hint">The Trigger can't be deleted.</div>
                )}
              </div>
            </>
          )}
        </div>

        {/* ── Config panel ───────────────────────────────────────────────── */}
        <div className={`wf-config${selectedNode ? '' : ' hidden'}`}>
          {selectedNode ? (
            <ConfigPanel
              key={selectedNode.id}
              node={selectedNode}
              onUpdate={(data) => updateNodeData(selectedNode.id, data)}
              onDelete={() => deleteNode(selectedNode.id)}
              onClose={() => setSelectedNodeId(null)}
              campaigns={campaignList}
              agents={agentList}
              botAgents={botAgentList}
              roles={roleList}
              templates={approvedTemplates}
              mediaAssets={mediaAssets}
              interactiveMessages={interactiveMessages}
              onRefreshInteractiveMessages={refreshInteractiveMessages}
              interactiveMessagesLoading={interactiveMessagesLoading}
              workflows={workflowList}
              apiConnections={apiConnectionList}
              apiConnectionsLoading={apiConnectionsLoading}
              onRefreshApiConnections={refreshApiConnections}
              flowVariables={flowVariables}
              flowVariablesLoading={flowVariablesLoading}
              onRefreshFlowVariables={refreshFlowVariables}
              availableVariables={availableVariables}
              priorityTokens={upstreamTokens}
              isConversationFlow={isConversationFlow}
              routingSettings={routingSettings}
              ownTriggerCount={
                nodes.filter((n) => n.type === 'keyword_trigger' || n.type === 'button_reply_trigger').length
              }
              wfTheme={wfTheme}
              {...(id ? { currentWorkflowId: id } : {})}
            />
          ) : (
            <div className="wf-config__empty">
              <div className="wf-config__empty-icon">◈</div>
              <Typography sx={{ fontSize: 13, color: 'var(--wf-text-faint, #484f58)', fontWeight: 600 }}>
                No node selected
              </Typography>
              <Typography sx={{ fontSize: 12, color: 'var(--wf-text-faint, #484f58)', mt: 0.5 }}>
                Click a node to configure it
              </Typography>
            </div>
          )}
        </div>
      </div>

      <div className="wf-statusbar">
        <div className="wf-statusbar__left">
          <span className="wf-statusbar__dot" />
          <span>
            {isConversationFlow
              ? 'Runs live, as each customer message arrives'
              : 'Runs in the background, after its trigger fires'}
          </span>
        </div>
        <div>
          {wfName || 'Untitled workflow'} · {nodes.length} {nodes.length === 1 ? 'step' : 'steps'} · {edges.length}{' '}
          {edges.length === 1 ? 'connection' : 'connections'}
        </div>
        <div className="wf-statusbar__right">
          <span className={`wf-statusbar__dot${id !== 'new' && isActive ? '' : ' is-off'}`} />
          <span>{id !== 'new' && isActive ? 'Active' : 'Not live'}</span>
        </div>
      </div>

      {/* ── Starter-template picker ──────────────────────────────────────── */}
      <Dialog
        open={templateOpen}
        onClose={() => setTemplateOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          // Portaled outside .wf-page, so opt into the same tokens explicitly.
          className: `wf-theme-vars${wfTheme === 'light' ? ' wf-theme-vars--light' : ''}`,
          sx: { background: 'var(--wf-dialog-bg)', border: '1px solid var(--wf-border-strong)', color: 'var(--wf-text)' },
        }}
      >
        <DialogTitle sx={{ fontSize: 16, fontWeight: 700, pb: 0.5 }}>
          Start from a template
          <Typography sx={{ fontSize: 12.5, color: 'var(--wf-text-dim, #8b949e)', mt: 0.5, fontWeight: 400 }}>
            Prefill the canvas with a ready-made example. Applying one replaces the current canvas.
          </Typography>
        </DialogTitle>
        <DialogContent dividers sx={{ borderColor: 'var(--wf-border-strong)' }}>
          {templatesLoading ? (
            <Stack alignItems="center" sx={{ py: 4 }}>
              <CircularProgress size={24} sx={{ color: 'var(--wf-accent)' }} />
            </Stack>
          ) : templatesError ? (
            <Alert severity="error" sx={{ fontSize: 12.5 }}>
              {templatesError}
            </Alert>
          ) : templates.length === 0 ? (
            <Typography sx={{ fontSize: 13, color: 'var(--wf-text-dim, #8b949e)', py: 2, textAlign: 'center' }}>
              No templates available.
            </Typography>
          ) : (
            <Stack spacing={2}>
              {(() => {
                // Group by vertical (absent → 'general'); general first, then the
                // rest A–Z, so a tenant sees their industry's starters together.
                const groups: Record<string, any[]> = {};
                for (const t of templates) {
                  const v = (t.vertical as string) || 'general';
                  (groups[v] ||= []).push(t);
                }
                const order = Object.keys(groups).sort((a, b) =>
                  a === 'general' ? -1 : b === 'general' ? 1 : a.localeCompare(b)
                );
                const cap = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);
                return order.map((v) => (
                  <Box key={v}>
                    <Typography
                      sx={{
                        fontSize: 10.5,
                        fontWeight: 700,
                        letterSpacing: '0.08em',
                        color: 'var(--wf-text-faint, #5a6478)',
                        mb: 1,
                      }}
                    >
                      {cap(v).toUpperCase()}
                    </Typography>
                    <Stack spacing={1.25}>
                      {(groups[v] ?? []).map((t) => {
                        const isCF = t.engine === 'conversation-flow';
                        return (
                          <Box
                            key={t.id}
                            onClick={() => applyTemplate(t)}
                            sx={{
                              p: 1.5,
                              borderRadius: 1.5,
                              border: '1px solid var(--wf-border-strong, #30363d)',
                              cursor: 'pointer',
                              transition: 'border-color .15s, background .15s',
                              '&:hover': {
                                borderColor: 'var(--wf-accent)',
                                background: 'rgba(76,141,255,0.06)',
                              },
                            }}
                          >
                            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
                              <Typography sx={{ fontSize: 14, fontWeight: 600, flex: 1 }}>{t.name}</Typography>
                              <Chip
                                size="small"
                                label={isCF ? 'Incoming' : 'Outgoing'}
                                sx={{
                                  height: 20,
                                  fontSize: 11,
                                  fontWeight: 600,
                                  color: 'var(--wf-accent)',
                                  background: 'var(--wf-accent-dim)',
                                }}
                              />
                            </Stack>
                            <Typography sx={{ fontSize: 12.5, color: 'var(--wf-text-dim, #8b949e)', lineHeight: 1.5 }}>
                              {t.description}
                            </Typography>
                          </Box>
                        );
                      })}
                    </Stack>
                  </Box>
                ));
              })()}
            </Stack>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 1.5 }}>
          <Button
            onClick={() => setTemplateOpen(false)}
            sx={{ color: 'var(--wf-text-dim, #939eb2)', textTransform: 'none' }}
          >
            Cancel
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}

// ─── Public export (wrapped in ReactFlowProvider) ────────────────────────────

export default function WorkflowCanvas() {
  return (
    <ReactFlowProvider>
      <CanvasInner />
    </ReactFlowProvider>
  );
}
